<template>
  <section id="createOrder">
    <div class="container">
      <!-- Frequently Ordered now lives in the global floating Top Parts
           widget, so this is a single full-width column (the old right
           sidebar was removed). -->
      <div class="row create-order-row">
        <div class="col-12 mb-4 mb-lg-0">
          <b-form id="orderForm" class="row create-order-form" @submit.prevent="createOrder">
            <!-- ===== HEADER =====
                 Title + brief value prop. Tells users what they
                 can do here without making them parse the form
                 first. -->
            <div class="col-12">
              <h1 class="create-order-title">Create New Order</h1>
              <p class="create-order-subtitle">
                Order parts for your existing equipment, or
                <a href="mailto:parts@jcsteele.com?subject=New%20Equipment%20Parts%20Request"
                   class="new-equipment-link">
                  request help for new equipment</a>.
              </p>
            </div>

            <!-- ===== STEP 1: ORDER CONTEXT =====
                 Company + PO Number. The two pieces of info that
                 every order needs regardless of which path the
                 user takes (frequently ordered, machine browse,
                 or new equipment). -->
            <!-- ===== STEP 1: ORDER CONTEXT =====
                 Company is now the only required up-front
                 input. PO Number is captured per-part via the
                 picker modal when the user clicks ADD —
                 matching how Frequently Ordered works. This
                 means a single cart can have multiple POs
                 across different parts without forcing the
                 user to commit to a single PO at the start. -->
            <!-- ===== COMPLETED STEPS NAV =====
                 Steps the user has finished (or started) collapse into
                 clickable numbered circles here; the active step stays
                 expanded below. Clicking a circle re-expands that step. -->
            <div class="col-12 completed-steps-row" v-if="navCircles.length">
              <span class="completed-steps-label">Completed Steps:</span>
              <button v-for="n in navCircles" :key="'csnav-' + n"
                      type="button"
                      class="completed-step-circle"
                      :class="isStepDone(n) ? 'is-done' : 'is-pending'"
                      :title="circleTitle(n)"
                      @click="goToStep(n)">{{ n }}</button>
            </div>

            <div class="col-12 form-step" v-show="currentStep === 1">
              <div class="form-step-header">
                <span class="form-step-num" :class="{ 'is-complete': !!companyId }">1</span>
                <h3 class="form-step-title">Choose Company</h3>
              </div>

              <div class="row form-step-body">
                <div class="col-12">
                  <label for="CompanySelect">Company</label>
                  <div
                    :class="[
  'ui fluid search selection dropdown',
  { disabled: true }
]"
                    id="CompanySelect"
                  >
                    <input
                      type="hidden"
                      name="company"
                      v-model="companyId"
                      id="company"
                    />
                    <div class="default text">Select your company</div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <div
                        class="item"
                        v-for="company in companies"
                        :key="company.value"
                        v-bind:data-value="company.value"
                      >
                        {{ company.text }}
                      </div>
                    </div>
                  </div>

                  <div class="form-helper-text" v-if="disabled">
                    Locked while a cart is active.
                    <a href="/cart">Review</a> or
                    <a href="#" @click.prevent="showDeleteModal()">clear it</a>
                    to switch.
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== STEP 2: FIND YOUR MACHINE =====
                 Just the machine select + optional machine
                 image. The wear-parts toggle moved up into
                 Step 3's header so it lives next to the part
                 list it filters. -->
            <div class="col-12 form-step" v-show="currentStep === 2">
              <div class="form-step-header">
                <span class="form-step-num" :class="{ 'is-complete': !!machineId }">2</span>
                <h3 class="form-step-title">Find Your Machine</h3>
              </div>

              <div class="row form-step-body">
                <div class="col-12">
                  <label for="MachineSelect">Machine</label>
                  <div
                    class="ui fluid search selection dropdown"
                    :class="[{ error: machineError }]"
                    id="MachineSelect"
                  >
                    <input
                      type="hidden"
                      name="machine"
                      v-model="machineId"
                      id="Machine"
                      @change="onMachineChange"
                      required
                    />
                    <div class="default text">Select your machine</div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <div
                        class="item"
                        v-for="machine in machines"
                        :key="machine.value"
                        v-bind:data-value="machine.value"
                        required
                      >
                        {{ machine.text }}
                      </div>
                      <!-- ===== "Don't see your machine?" footer =====
                           Sits below the data items as a non-selectable
                           help block. Both buttons (call + email) live
                           here so they're discoverable in context — the
                           moment a user can't find their machine, the
                           help is one click away.

                           data-no-select on the wrapper isn't a Semantic
                           UI native attribute, but combined with
                           click.stop on each interactive child it stops
                           the dropdown from absorbing the click as a
                           value-pick. -->
                      <div class="dropdown-footer-help" data-no-select="true"
                           @click.stop>
                        <div class="dropdown-footer-divider"></div>
                        <div class="dropdown-footer-content">
                          <div class="dropdown-footer-title">
                            <span class="dropdown-footer-icon">?</span>
                            <strong>Don't see your machine?</strong>
                          </div>
                          <p class="dropdown-footer-blurb">
                            Ordering for new or recently-purchased equipment that
                            isn't on your account yet? Our parts team can help.
                          </p>
                          <div class="dropdown-footer-actions">
                            <button type="button"
                                    class="btn btn-sm dropdown-footer-action"
                                    @click.stop.prevent="openPartsInquiryModal('machine')">
                              Click Here to Email Parts Team -OR- call us at 1-800-278-3353
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 mt-3"
                     v-if="machineId && (machineImage || machineImageLoading)">
                  <!-- Machine image — only renders if the
                       machine actually has an imagePath on
                       file. While the lookup is in flight,
                       show a spinner instead of jumping the
                       layout. -->
                  <div class="machine-image-wrap"
                       :class="{ 'is-zoom-locked': inPlaceZoomLocked.machine }"
                       :style="{
                         '--lock-top': lockedStickyTop + 'px',
                         '--lock-left': lockedStickyLeft + 'px',
                         '--lock-width': lockedStickyWidth + 'px'
                       }"
                       @mousedown="onLockedMouseDown('machine', $event)">
                    <!-- Locked-state close button. v-if so it
                         only renders (and only steals layout)
                         while locked. @mousedown.stop blocks the
                         wrap's drag handler so clicking × never
                         accidentally starts a drag. -->
                    <button v-if="inPlaceZoomLocked.machine"
                            class="locked-close-btn"
                            type="button"
                            title="Close locked image"
                            @click="onCloseLock('machine')"
                            @mousedown.stop>×</button>
                    <div v-if="machineImageLoading" class="machine-image-loader">
                      <b-spinner small variant="secondary"></b-spinner>
                      <span>Loading image…</span>
                    </div>
                    <img
                      v-else-if="machineImage"
                      :src="machineImage"
                      class="machine-preview-img"
                      :style="{ transformOrigin: inPlaceZoomOrigin.machine }"
                      @mousemove="inPlaceZoomMove('machine', $event)"
                      @click="onImageClick('machine', machineImage)"
                      :title="inPlaceZoomLocked.machine
                        ? 'Drag to pan inside · Double-click + drag to move panel · Click × to close'
                        : 'Hover to zoom · Single-click to lock zoom in place · Double-click to open in popup'"
                      draggable="false"
                      alt="Selected machine"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== STEP 3: FIND YOUR PART =====
                 Renders only when a machine is selected.
                 Replaces the old navigation to
                 /orders/shopwearparts.

                 Header carries the wear-parts toggle on the
                 right — moved up from Step 2 so the filter
                 lives next to the list it actually filters.

                 Two-step inline UX:
                   1. Pick an assembly from the dropdown
                      (populated from
                      getSelectItemsByCompanyMachine).
                   2. Sub-assemblies + wear parts accordion
                      loads for that assembly via
                      getAssembliesWithWearParts.

                 ADD on each part feeds addToOrderClick — the
                 same gate Frequently Ordered uses — so the
                 PO picker modal, staging tray, debounced
                 cart sync, and header counter emit all work
                 without new plumbing. PO is captured per-add
                 so users can mix multiple POs in one cart. -->
            <div class="col-12 form-step"
                 v-if="showInlineWearParts"
                 v-show="currentStep === 3">
              <div class="form-step-header form-step-header-with-action">
                <div class="form-step-header-main">
                  <span class="form-step-num"
                        :class="{ 'is-complete': !!selectedWearAssemblyId }">3</span>
                  <h3 class="form-step-title">Find Your Part</h3>
                </div>
                <b-form-checkbox
                  id="configuredParts"
                  v-model="configuredWear"
                  name="configuredParts"
                  :value="true"
                  :unchecked-value="false"
                  class="wear-parts-toggle wear-parts-toggle-header"
                >
                  <span class="wear-parts-label">Show wear parts only</span>
                  <span class="wear-parts-helper">
                    Uncheck to browse every part
                  </span>
                </b-form-checkbox>
              </div>

              <div class="form-step-body inline-wear-stepbody">
                <!-- Use v-show rather than v-if here. v-if
                     would toggle this <p> in/out of the DOM
                     when the user flips the wear-parts
                     checkbox — and because the surrounding
                     siblings (incl. two divs that share the
                     class .inline-wear-picker) aren't keyed,
                     Vue's diff can patch the assembly
                     dropdown's DOM node into a different
                     position, blowing away Semantic UI's
                     event bindings on it. v-show keeps the
                     node in place and just hides it via CSS,
                     so the diff sees no structural change. -->
                <!-- Assembly picker — driven by
                     wearAssemblyOptions. Built as a Semantic
                     UI dropdown (rather than a native
                     <select>) so the "Don't see your
                     assembly?" help footer can live INSIDE
                     the menu — matching the Machine
                     dropdown's pattern exactly. The previous
                     native-<select> implementation couldn't
                     host arbitrary HTML inside <option>
                     elements, so the help block had to live
                     below it; now it sits flush at the
                     bottom of the open menu like users
                     expect.

                     Explicit `key` here disambiguates this
                     picker from the sub-assembly picker
                     below — both share the class
                     .inline-wear-picker, and unkeyed Vue
                     diffs can confuse them. -->
                <div class="inline-wear-picker"
                     key="assembly-picker">
                  <label for="WearAssemblySelect"
                         class="inline-wear-picker-label">
                    Choose an Assembly
                  </label>
                  <div class="ui fluid search selection dropdown inline-wear-assembly-dropdown"
                       :class="{ disabled: wearAssembliesLoading }"
                       id="WearAssemblySelect">
                    <input type="hidden"
                           name="wearAssembly"
                           id="WearAssembly"
                           v-model="selectedWearAssemblyId"
                           @change="onWearAssemblyChange" />
                    <div class="default text">
                      {{ wearAssembliesLoading ? 'Loading assemblies…' : '— Select an assembly —' }}
                    </div>
                    <i class="dropdown icon"></i>
                    <div class="menu">
                      <div
                        class="item"
                        v-for="opt in wearAssemblyOptions"
                        :key="opt.value"
                        v-bind:data-value="opt.value">
                        {{ opt.text }}
                      </div>
                      <!-- ===== "Don't see your assembly?" footer =====
                           Mirrors the Machine dropdown's
                           footer. Sits below the data items
                           as a non-selectable help block.
                           data-no-select + click.stop on the
                           interactive children stops Semantic
                           UI from absorbing the click as a
                           value-pick. Only renders once the
                           assembly list has finished loading
                           AND has at least one item — the
                           empty state below the picker handles
                           the "no assemblies on this machine"
                           case separately. -->
                      <div class="dropdown-footer-help" data-no-select="true"
                           v-if="!wearAssembliesLoading && wearAssemblyOptions.length > 0"
                           @click.stop>
                        <div class="dropdown-footer-divider"></div>
                        <div class="dropdown-footer-content">
                          <div class="dropdown-footer-title">
                            <span class="dropdown-footer-icon">?</span>
                            <strong>Don't see your assembly?</strong>
                          </div>
                          <p class="dropdown-footer-blurb">
                            Looking for an assembly that isn't on this machine
                            yet? Our parts team can help.
                          </p>
                          <div class="dropdown-footer-actions">
                            <button type="button"
                                    class="btn btn-sm dropdown-footer-action"
                                    @click.stop.prevent="openPartsInquiryModal('assembly')">
                              Click Here to Email Parts Team -OR- call us at 1-800-278-3353
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Assembly image — same pattern as Step 2:
                     spinner while loading, omitted entirely
                     when the assembly has no image on file. -->
                <div class="machine-image-wrap"
                     :class="{ 'is-zoom-locked': inPlaceZoomLocked.assembly }"
                     :style="{
                       '--lock-top': lockedStickyTop + 'px',
                       '--lock-left': lockedStickyLeft + 'px',
                       '--lock-width': lockedStickyWidth + 'px'
                     }"
                     @mousedown="onLockedMouseDown('assembly', $event)"
                     v-if="selectedWearAssemblyId && (assemblyImage || assemblyImageLoading)">
                  <!-- Locked-state close button. See machine
                       wrap above for full rationale. -->
                  <button v-if="inPlaceZoomLocked.assembly"
                          class="locked-close-btn"
                          type="button"
                          title="Close locked image"
                          @click="onCloseLock('assembly')"
                          @mousedown.stop>×</button>
                  <div v-if="assemblyImageLoading" class="machine-image-loader">
                    <b-spinner small variant="secondary"></b-spinner>
                    <span>Loading image…</span>
                  </div>
                  <img
                    v-else-if="assemblyImage"
                    :src="assemblyImage"
                    class="machine-preview-img"
                    :style="{ transformOrigin: inPlaceZoomOrigin.assembly }"
                    @mousemove="inPlaceZoomMove('assembly', $event)"
                    @click="onImageClick('assembly', assemblyImage)"
                    :title="inPlaceZoomLocked.assembly
                      ? 'Drag to pan inside · Double-click + drag to move panel · Click × to close'
                      : 'Hover to zoom · Single-click to lock zoom in place · Double-click to open in popup'"
                    draggable="false"
                    alt="Selected assembly"
                  />
                </div>

                <!-- Sub-Assembly picker — filters the accordion
                     below to a single sub-assembly. Options
                     come from wearAssemblies (each entry is
                     itself a sub-assembly). Picking one
                     auto-expands its card so the user goes
                     straight to the parts. -->
                <div class="inline-wear-picker"
                     key="subassembly-picker"
                     v-if="selectedWearAssemblyId && !wearPartsLoading && wearAssemblies.length > 0">
                  <label for="inline-wear-subassembly"
                         class="inline-wear-picker-label">
                    Jump to Sub-Assembly
                  </label>
                  <select id="inline-wear-subassembly"
                          class="inline-wear-picker-select"
                          v-model="selectedSubAssemblyId">
                    <option :value="null">— All sub-assemblies —</option>
                    <option v-for="sa in wearAssemblies"
                            :key="'sa-opt-' + sa.assemblyId"
                            :value="sa.assemblyId">
                      {{ sa.assemblyName }}
                    </option>
                  </select>
                </div>

                <!-- States: empty picker, parts loading,
                     parts empty for this assembly, or the
                     accordion. -->
                <div class="inline-wear-empty"
                     v-if="!wearAssembliesLoading && wearAssemblyOptions.length === 0">
                  No assemblies found for this machine.
                </div>

                <div class="inline-wear-loader"
                     v-else-if="wearPartsLoading">
                  <b-spinner small variant="secondary"></b-spinner>
                  <span>Loading parts…</span>
                </div>

                <div class="inline-wear-empty"
                     v-else-if="showWearPartsList && wearAssemblies.length === 0">
                  {{ configuredWear ? 'No wear parts in this assembly.' : 'No parts in this assembly.' }}
                </div>

                <div class="inline-wear-list"
                     v-else-if="showWearPartsList">
                  <template v-for="(asm, idx) in displayedSubAssemblies">
                  <div class="inline-wear-card"
                       :key="'inline-wear-' + asm.assemblyId">
                    <button type="button"
                            class="inline-wear-card-header"
                            @click="toggleInlineWearAssembly(asm.assemblyId)">
                      <span class="inline-wear-chevron"
                            :class="{ 'is-expanded': isInlineWearAssemblyExpanded(asm.assemblyId) }"
                            aria-hidden="true">
                        {{ isInlineWearAssemblyExpanded(asm.assemblyId) ? '−' : '+' }}
                      </span>
                      <span class="inline-wear-card-name">
                        {{ asm.assemblyName }}
                      </span>
                      <span class="inline-wear-card-count">
                        {{ (asm.parts || []).length }}
                        {{ (asm.parts || []).length === 1 ? 'part' : 'parts' }}
                      </span>
                    </button>
                    <ul class="inline-wear-parts"
                        v-show="isInlineWearAssemblyExpanded(asm.assemblyId)">
                      <li class="inline-wear-part"
                          v-for="part in asm.parts"
                          :key="'inline-part-' + (part.partId || part.id)">
                        <div class="inline-wear-part-main">
                          <div class="inline-wear-part-code-row">
                            <span class="inline-wear-part-code">
                              {{ part.code }}
                            </span>
                            <span class="inline-wear-discount-chip"
                                  v-if="isWearEligible(part)">
                              2% Discount
                            </span>
                          </div>
                          <div class="inline-wear-part-desc">
                            {{ part.description }}
                          </div>
                          <div class="inline-wear-part-price">
                            {{ part.price | currency }}
                            <span class="inline-wear-part-qpm"
                                  v-if="part.quantityPerMachine">
                              · QTY/MACH: {{ part.quantityPerMachine }}
                            </span>
                          </div>
                        </div>
                        <a href="#"
                           class="inline-wear-part-add"
                           :class="{
                             'is-selected': isPartSelected({
                               partId: part.partId || part.id,
                               partCode: part.code
                             }),
                             disabled: addingInsightPart
                           }"
                           @click.prevent="addInlineWearPart(part, asm)"
                           :title="isPartSelected({ partId: part.partId || part.id, partCode: part.code })
                                   ? 'Edit this part — move to a different order or remove'
                                   : 'Add this part to an order'">
                          {{ isPartSelected({
                               partId: part.partId || part.id,
                               partCode: part.code
                             }) ? 'Edit' : 'Add' }}
                        </a>
                      </li>
                    </ul>
                  </div>
                  <!-- Wayfinding hint: when there's more than
                       one sub-assembly, the first is expanded
                       and the rest are collapsed below. This
                       banner sits right after the first card
                       so users can't miss that there are more
                       categories to explore. -->
                  <div class="inline-wear-more-hint"
                       v-if="idx === 0 && displayedSubAssemblies.length > 1"
                       :key="'hint-' + asm.assemblyId">
                    <span aria-hidden="true">↓</span>
                    {{ displayedSubAssemblies.length - 1 }}
                    more {{ displayedSubAssemblies.length - 1 === 1 ? 'category' : 'categories' }}
                    of parts below — click <strong>+</strong> on any header to expand
                  </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- The "Don't see your machine?" call-to-action
                 used to live here as a standalone yellow box.
                 It moved up into the machine dropdown's footer
                 so the help is presented in context — at the
                 moment the user discovers their machine isn't
                 listed, not at the bottom of the form. The
                 modal it opens is defined further down. -->

          </b-form>
        </div>
      </div>
    </div>

    <!-- ===== PO PICKER MODAL =====
         Two modes:
           - 'add'  → user is queueing a brand-new part. Shows every
                     existing Order plus "+ New PO Number".
           - 'edit' → part is already queued. Shows other Orders to
                     move to, plus "+ New PO Number" and a Remove
                     option. The current Order is filtered out so
                     the user doesn't see a no-op choice.
         Cancel dismisses without changes. -->
    <b-modal id="poPickerModal"
             v-model="showPoPickerModal"
             :title="pickerModalTitle"
             ok-only
             ok-title="Cancel"
             ok-variant="outline-dark"
             size="md"
             centered
             hide-header-close
             no-close-on-backdrop
             modal-class="po-picker-modal"
             @ok="onPoPickerCancel">
      <div class="po-picker-body">
        <!-- Edit-mode hint: tells the user where the part is now. -->
        <div class="po-picker-current"
             v-if="pickerMode === 'edit' && pendingPartCurrentOrder">
          Currently in
          <strong>Purchase Order #{{ pendingPartCurrentOrder.poNumber }}</strong>.
        </div>
        <p class="po-picker-text" v-if="pickerMode === 'add'">
          Pick an existing order to group this part with, or start a
          new one with a different PO Number.
        </p>
        <p class="po-picker-text" v-else>
          Pick a different order to move this part to, or remove it
          from the queue entirely.
        </p>

        <div class="po-picker-options">
          <!-- Other-order options (filtered to exclude current in edit mode) -->
          <button type="button"
                  class="po-picker-option"
                  v-for="g in pickableOrders"
                  :key="'pick-' + g.poNumber"
                  @click="onPoPickerChoose(g.poNumber)">
            <span class="po-picker-option-num">Purchase Order #{{ g.poNumber }}</span>
            <span class="po-picker-option-meta">
              {{ g.parts.length }}
              {{ g.parts.length === 1 ? 'part' : 'parts' }}
            </span>
          </button>

          <!-- New PO Number option — works in both modes -->
          <button type="button"
                  class="po-picker-option po-picker-option-new"
                  @click="onPoPickerNewPo()">
            <span class="po-picker-option-num">＋ New PO Number</span>
            <span class="po-picker-option-meta">
              {{ pickerMode === 'edit'
                 ? 'Move this part to a brand-new order'
                 : 'Start a new order with a different PO' }}
            </span>
          </button>

          <!-- Remove option — only in edit mode -->
          <button type="button"
                  class="po-picker-option po-picker-option-remove"
                  v-if="pickerMode === 'edit'"
                  @click="onPoPickerRemove()">
            <span class="po-picker-option-num">✕ Remove from queue</span>
            <span class="po-picker-option-meta">
              Drop this part — you can re-add it later
            </span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ===== PO REQUIRED MODAL =====
         Custom b-modal (not msgBoxOk) so we can host an inline PO
         Number input alongside the explanation. Save validates the
         field is non-empty, mirrors the value into this.poNumber
         (which is v-modeled to the form's PO field on the left, so
         the value appears there too), and resumes the queued action.

         Wording uses "order" since this is the Create New Order
         page. The mirror page on the quote side (if/when it exists)
         should swap "order" → "quote" in the explainer text. -->
    <b-modal id="poExplainerModal"
             v-model="showPoExplainerModal"
             title="PO Number Required"
             ok-title="Save and Continue"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-dark"
             size="md"
             centered
             hide-header-close
             no-close-on-backdrop
             no-close-on-esc
             modal-class="po-explainer-modal"
             @ok="onPoModalConfirm"
             @cancel="onPoModalCancel"
             @shown="onPoModalShown">
      <div class="po-explainer-body">
        <p class="po-explainer-text">
          Each PO Number you use creates a separate order. If you add
          multiple parts under the same PO Number, they're grouped
          into one order together. If you switch to a different PO
          Number for the next part, it becomes a separate order — and
          so on for each new PO Number you use.
        </p>
        <label for="poExplainerInput" class="po-modal-label">PO Number</label>
        <input id="poExplainerInput"
               type="text"
               v-model="poExplainerInput"
               placeholder="Enter PO Number"
               class="po-modal-input"
               autocomplete="off"
               @keyup.enter="onPoModalConfirm" />
      </div>
    </b-modal>

    <!-- ===== MACHINE IMAGE ZOOM MODAL =====
         Same modal + zoom controls as ShopWearParts.vue.
         Mouse wheel zooms; click+drag pans when zoomed in. -->
    <div
      v-if="showImageModal"
      class="custom-image-modal"
      @click="closeImageModal"
    >
      <div class="custom-image-modal-inner" @click.stop>
        <button
          type="button"
          class="custom-image-close"
          @click="closeImageModal"
          aria-label="Close"
        >
          ×
        </button>

        <div
          class="diagram-zoom-wrap"
          @wheel.prevent="handleWheel"
          @mousedown="startDrag"
        >
          <img
            :src="modalImage"
            class="imagepreview"
            :style="imageTransformStyle"
            @dragstart.prevent
          />
        </div>

        <div class="custom-image-controls">
          <button type="button" class="btn btn-secondary" @click="zoomOut">−</button>
          <button type="button" class="btn btn-secondary" @click="zoomIn">+</button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="resetZoom"
          >
            Reset
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="closeImageModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- ===== PARTS INQUIRY MODAL =====
         Opened from the "Don't see your machine?" footer in
         the machine dropdown. Mirrors the bug-report form
         pattern (description + optional file), plus customer
         contact fields so the parts team can reply.

         Submits to FeApiService.submitPartsInquiry which
         POSTs to /Email/parts-inquiry as multipart/form-data.
         Server hardcodes the recipient as parts@jcsteele.com;
         the customer's email goes into Reply-To so the team
         can answer directly. -->
    <b-modal id="parts-inquiry-modal"
             v-model="showPartsInquiryModal"
             title="Email the Parts Team"
             size="lg"
             centered
             :ok-title="partsInquirySending ? 'Sending…' : 'Email Inquiry or call 1-800-278-3353'"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="partsInquirySending || !partsInquiryFormValid"
             @ok="submitPartsInquiry"
             @hidden="resetPartsInquiryForm">

      <p class="parts-inquiry-intro">
        Tell us about the equipment you need parts for. We'll get back
        to you at the email you provide.
      </p>

      <div v-if="partsInquirySuccess" class="ui visible success message">
        <div class="header">Inquiry sent.</div>
        <p>Our parts team will follow up by email shortly.</p>
      </div>

      <div v-if="partsInquiryError" class="ui visible error message">
        <div class="header">Could not send your inquiry.</div>
        <p>{{ partsInquiryError }}</p>
      </div>

      <div v-if="!partsInquirySuccess" class="parts-inquiry-form">
        <div class="form-row">
          <div class="col-md-6">
            <label for="partsInquiryName">
              Your Name <span class="text-danger">*</span>
            </label>
            <input id="partsInquiryName"
                   type="text"
                   class="form-control"
                   maxlength="120"
                   v-model="partsInquiry.customerName" />
          </div>
          <div class="col-md-6">
            <label for="partsInquiryEmail">
              Reply Email <span class="text-danger">*</span>
            </label>
            <input id="partsInquiryEmail"
                   type="email"
                   class="form-control"
                   maxlength="200"
                   v-model="partsInquiry.customerEmail" />
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-md-6">
            <label for="partsInquiryCompany">Company</label>
            <input id="partsInquiryCompany"
                   type="text"
                   class="form-control"
                   maxlength="200"
                   v-model="partsInquiry.customerCompany" />
          </div>
          <div class="col-md-6">
            <label for="partsInquiryMachine">
              Machine / Equipment Description
            </label>
            <input id="partsInquiryMachine"
                   type="text"
                   class="form-control"
                   maxlength="200"
                   placeholder="e.g. 90A Extruder, serial 12345"
                   v-model="partsInquiry.machineDescription" />
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-12">
            <label for="partsInquiryDescription">
              What can we help you with?
              <span class="text-danger">*</span>
            </label>
            <textarea id="partsInquiryDescription"
                      class="form-control"
                      rows="6"
                      maxlength="2000"
                      placeholder="Tell us what parts you need or what's going on with your equipment."
                      v-model="partsInquiry.description"></textarea>
          </div>
        </div>

        <div class="form-row mt-3">
          <div class="col-12">
            <label for="partsInquiryFile">
              Attach a photo (optional)
            </label>
            <input id="partsInquiryFile"
                   type="file"
                   ref="partsInquiryFile"
                   accept=".jpg,.jpeg,.png"
                   @change="onPartsInquiryFileChange" />
            <div v-if="partsInquiryFileError" class="text-danger mt-1">
              {{ partsInquiryFileError }}
            </div>
            <small class="form-text text-muted">
              JPG or PNG, max 1 MB. Helpful for nameplates / serial tags.
            </small>
          </div>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
/* ============================================================================
 * CONTRIBUTOR NOTES -- header-search hydration feature
 * ============================================================================
 *
 * THIS FILE:  Create_-_Order.vue  (/orders/create)
 * SIBLINGS:   Index_-_Search.vue  (/search)
 *             Create_-_RFQ.vue  (/quotes/createrfq)
 *
 * These three files share a "header-search hydration" feature where a part
 * suggestion clicked in the floating header search bar (Head.vue's
 * selectOption) propagates context -- code, machineName, assembly,
 * allParts=true -- into whichever page the user lands on. Changes to one
 * file almost always need parallel changes to the other two. The shared
 * pattern is grep-able: search any of these tokens and you'll find it
 * mirrored across all three:
 *
 *   - Template:   class="header-search-result" v-if="headerSearch.active"
 *                 (banner sits at top of .insights-panel)
 *   - CSS:        .header-search-result* in scoped <style>
 *   - data():     headerSearch + pendingHeaderMachineName / AssemblyName /
 *                 SubAssemblyName + wearPartIds
 *   - computed:   wearPartIdSet
 *   - methods:    applyHeaderSearchQuery, applyPendingHeaderMachine,
 *                 applyHeaderAssemblyDeepSearch, _floatHeaderSearchPartToTop,
 *                 clearHeaderSearch, loadWearPartIds, isWearEligible
 *   - watchers:   machines, wearAssemblyOptions, $route
 *   - mounted():  applyHeaderSearchQuery() then loadWearPartIds()
 *   - Loaders:    loadInlineWearAssemblyOptions, loadInlineWearPartsForAssembly,
 *                 loadInlineAllPartsForAssembly each have hydration blocks
 *
 * --- ARRIVAL PATHS ---
 *   1. Direct: URL query params (?code=...&machineName=...&assembly=...&allParts=true)
 *   2. Cross-page nav: localStorage.headerSearchParams (set by any of the
 *      three pages when applyHeaderSearchQuery successfully reads URL params,
 *      or by Head.vue itself).
 *   First line of applyHeaderSearchQuery: prefer URL, fall back to
 *   localStorage. Source is logged ("source: URL" vs "source: localStorage").
 *
 * --- KNOWN RACES (DO NOT REGRESS) ---
 *
 *   [R1] mounted()'s 600ms setTimeout calls loadInlineWearAssemblyOptions
 *        when companyId+machineId are set. Race-GUARDED with
 *        `!self.headerSearch.active`. Why: when header-search is the
 *        arrival path, the machineId watcher (set by applyPendingHeader-
 *        Machine) has ALREADY triggered the load. A second call bumps
 *        wearLoadKey, which silently invalidates the in-flight parts
 *        response from loadInlineAllPartsForAssembly / loadInlineWearParts-
 *        ForAssembly (their stale-check is `if (key !== this.wearLoadKey)
 *        return;`). Symptom: parts list never populates, log shows
 *        "[...] inline wear: stale key, ignoring response".
 *
 *   [R2] applyHeaderAssemblyDeepSearch's stale-check captures machineId,
 *        NOT wearLoadKey. Why: wearLoadKey gets bumped by intra-page
 *        bootstrap chatter (other loaders firing). machineId changes only
 *        on actual user-driven machine switches, which is the only signal
 *        we genuinely want to abort on. Diagnostic log fires when the
 *        stale-check trips: "deep search response for parent X dropped --
 *        machineId changed from N to M".
 *
 *   [R3] applyHeaderSearchQuery has a first-line modify-flow guard
 *      (returns early when pendingModifyAssemblyId / pendingModify-
 *      SubAssemblyId / pendingModifyMachineName are set), so a
 *      Cart -> Modify Order/Quote arrival cleanly wins over a stale
 *      persisted header search. Search.vue does NOT have this guard
 *      because /search has no modify flow.
 *
 *   [R4] wearAssemblyOptions watcher re-fires applyHeaderAssemblyDeepSearch
 *        as a safety net. With [R1] and [R2] in place, this watcher won't
 *        actually re-fire (single load, single deep-search). But it's there
 *        to catch edge cases where wearAssemblyOptions populates after the
 *        first deep-search attempt aborted. If you see duplicate "deep
 *        search starting" log lines, something else is reassigning
 *        wearAssemblyOptions -- investigate before silencing.
 *
 * --- API ENDPOINTS ---
 *   apiService.getWearPartIds()
 *     -> GET /api/wear/GetWearPartIds. Flat list of wear-eligible PartIds.
 *        Used by isWearEligible(part) for the "2% Discount" chip.
 *        Source-of-truth: WearController.GetWearPartIds (filters Active,
 *        FK > 0, returns Distinct PartIds across ALL companies -- wear-
 *        eligibility is a per-PART attribute, not per-customer).
 *   apiService.getSelectItemsByCompanyMachine({ id })
 *     -> top-level wearAssemblyOptions, list of { value, text }
 *   apiService.getAssembliesWithWearParts({ assemblyId, companyMachineId })
 *     -> wear sub-assemblies (used when configuredWear=true)
 *   apiService.getAssemblyWithParts({ id })
 *     -> all-parts sub-assemblies (used when configuredWear=false)
 *
 * --- VUE 2 REACTIVITY GOTCHAS ---
 *   - Set mutations are NOT tracked. Use Array + computed object-map (see
 *     wearPartIds + wearPartIdSet for the canonical pattern in this file).
 *   - Vue.set / this.$set is needed when adding NEW keys to existing
 *     objects; plain assignment to a new key won't trigger reactivity.
 *     Used in the loader hydration blocks for expandedWearAssemblies.
 *   - vuetable-2 v2.0.0-beta.4 doesn't render __slot: fields correctly;
 *     use 1.7.5 with slot/slot-scope syntax (project-wide constraint).
 *
 * --- LOG PREFIX CONVENTION ---
 *   Search.vue           uses [Search]
 *   Create_-_Order.vue   uses [Create]
 *   Create_-_RFQ.vue     uses [Create RFQ]
 *   This file uses [Create]. Maintain the prefix when porting code
 *   between files.
 *
 * --- EDITING NOTES (Claude-specific, but applies to anyone) ---
 *
 *   When applying multi-step edits via a Python script, SAVE AFTER EACH
 *   step, not all at the end. sys.exit on a late failed step otherwise
 *   discards all in-memory mutations from earlier successful steps.
 *   (Bitten twice in this codebase -- once on the banner template, once
 *   on the discount chip. Pattern that works:
 *     def patch(path, old, new, label):
 *         with open(path, 'rb') as f: s = f.read().decode()
 *         # ...mutate...
 *         with open(path, 'wb') as f: f.write(s.encode())
 *   Slow but correct.)
 *
 *   s.count(substring) is a substring search, not a line search. A 4-space-
 *   indented `this.applyHeaderSearchQuery();` (mounted call) is a substring
 *   of an 8-space-indented `        this.applyHeaderSearchQuery();` ($route
 *   watcher call), so count returns 2 even though they're "different lines".
 *   Use multi-line anchors with surrounding context when disambiguating.
 *
 *   When porting a method from one file to another, the log prefix needs
 *   retagging. Search -> Create: s/[Search]/[Create]/g.
 *   Create -> RFQ: s/[Create]/[Create RFQ]/g (be careful, [Create RFQ]
 *   contains [Create] as a substring; do the longer replace first, OR
 *   make sure the source file has only [Create] before the swap).
 *
 * ============================================================================
 */

import FeApiService from "@/assets/js/FeApiService";
import { recordSearch } from "@/assets/js/activity";
import { downloadPdf } from "@/assets/js/helper";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "CreateOrder",
  computed: {
    // ===== STEP ACCORDION =====
    // Completion of each step: 1 = company chosen, 2 = machine chosen,
    // 3 = an assembly chosen in the part picker.
    step1Done() { return !!this.companyId; },
    step2Done() { return !!this.machineId; },
    step3Done() { return !!this.selectedWearAssemblyId; },
    // Which step's full block is expanded. Manual / auto-advance wins
    // (expandedStep); otherwise the first incomplete step.
    currentStep() {
      if (this.expandedStep) return this.expandedStep;
      if (!this.companyId) return 1;
      if (!this.machineId) return 2;
      return 3;
    },
    // Reachable steps (prerequisites met) EXCEPT the expanded one — these
    // show as clickable circles in the "Completed Steps" row.
    navCircles() {
      var reachable = [1];
      if (this.companyId) reachable.push(2);
      if (this.machineId) reachable.push(3);
      return reachable.filter((n) => n !== this.currentStep);
    },
    // Selection names for the circle hover tooltips.
    companyDisplayName() {
      var c = (this.companies || []).find((x) => String(x.value) === String(this.companyId));
      return c ? c.text : "";
    },
    machineDisplayName() {
      var m = (this.machines || []).find((x) => String(x.value) === String(this.machineId));
      return m ? m.text : "";
    },
    assemblyDisplayName() {
      var a = (this.wearAssemblyOptions || []).find((x) => String(x.value) === String(this.selectedWearAssemblyId));
      return a ? a.text : "";
    },
    // Transform style for the zoom modal image — combines pan (pointX/Y)
    // and scale into a single CSS transform. Mirrors ShopWearParts.vue.
    imageTransformStyle() {
      return {
        transform: `translate(${this.pointX}px, ${this.pointY}px) scale(${this.scale})`,
        transformOrigin: "center center",
      };
    },
    // Sub-assembly filter — when selectedSubAssemblyId is set,
    // narrow the accordion to that single sub-assembly. Null
    // selection shows the full list. Defensive: if the picker
    // value points to a sub-assembly that no longer exists in
    // wearAssemblies (e.g. machine swap raced ahead), fall
    // back to the full list.
    displayedSubAssemblies() {
      if (!this.selectedSubAssemblyId) return this.wearAssemblies;
      var match = (this.wearAssemblies || []).filter(function (sa) {
        return String(sa.assemblyId) === String(this.selectedSubAssemblyId);
      }, this);
      return match.length > 0 ? match : this.wearAssemblies;
    },
    // Display label for the insights period toggle — mirrors the
    // dashboard's labeling so users see the same wording in both
    // places.
    insightsPeriodLabel() {
      return this.insightsPeriod === "ytd" ? "YTD" : "All Time";
    },
    // Sliced view of the topParts list, starting at 5 and growing in
    // 5-row chunks each time "See more" is clicked. Defensive against
    // a missing/null list — empty array fallback so the template
    // never sees `undefined`. Slices from the *filtered* list so the
    // search box and pagination cooperate cleanly.
    displayedTopParts() {
      return this.filteredTopParts.slice(0, this.displayLimit);
    },
    // True when there are still hidden rows the user could expand to —
    // again computed against the filtered list.
    canShowMore() {
      return this.filteredTopParts.length > this.displayLimit;
    },
    // History search — case-insensitive substring match against
    // partCode, description, and contextDescription. Empty search
    // returns the full list unchanged so behavior is identical to
    // before the search box was added when nothing's typed.
    filteredTopParts() {
      var all = (this.insights && this.insights.topParts) || [];
      var term = (this.searchTerm || "").trim().toLowerCase();
      if (!term) return all;
      return all.filter(function (p) {
        return (
          (p.partCode && p.partCode.toLowerCase().indexOf(term) !== -1) ||
          (p.description && p.description.toLowerCase().indexOf(term) !== -1) ||
          (p.contextDescription &&
            p.contextDescription.toLowerCase().indexOf(term) !== -1)
        );
      });
    },
    // Group selected parts by PO Number, in stable order of first
    // appearance. The first PO encountered becomes Order #1, the
    // second Order #2, and so on. Drives the tray rendering and the
    // picker modal's existing-order options.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.selectedParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    // Total count across all order groups — used by the Continue
    // button label.
    selectedPartsCount() {
      return (this.selectedParts || []).length;
    },
    // Orders shown in the picker modal. In 'add' mode the user sees
    // every existing order (any of them is a valid destination). In
    // 'edit' mode the part is already somewhere — that order is
    // filtered out so the user only sees other orders to move to.
    pickableOrders() {
      if (this.pickerMode !== "edit" || !this.pendingPart) {
        return this.groupedSelections;
      }
      var pendingId = this.pendingPart.partId;
      return this.groupedSelections.filter(function (g) {
        return !g.parts.some(function (p) {
          return p.partId === pendingId;
        });
      });
    },
    // Convenience: in 'edit' mode, what's the current order for the
    // part being edited? Drives the "Currently in Order #N" hint
    // shown at the top of the picker.
    pendingPartCurrentOrder() {
      if (this.pickerMode !== "edit" || !this.pendingPart) return null;
      var pendingId = this.pendingPart.partId;
      return this.groupedSelections.find(function (g) {
        return g.parts.some(function (p) {
          return p.partId === pendingId;
        });
      });
    },
    // Picker modal title — adapts to add/edit mode + whether the
    // pending part has a code to display.
    pickerModalTitle() {
      var code = this.pendingPart && this.pendingPart.partCode;
      if (this.pickerMode === "edit") {
        return code ? "Edit " + code : "Edit part";
      }
      return code ? "Add " + code + " to which order?" : "Add to which order?";
    },

    // True when the queue has been modified since the last
    // successful saveCart (or sync). Drives the Continue button's
    // text and click behavior — when false, the cart already
    // mirrors the staging tray and the button just navigates;
    // when true, the button needs to fire saveCart again to
    // commit the new state.
    //
    // Differences detected: queue length, missing parts (new
    // additions not yet committed), PO changes, quantity edits.
    hasUnsavedChanges() {
      // No snapshot yet means nothing has been committed; if
      // there's anything queued, it's all unsaved.
      if (!this.committedSnapshot) {
        return this.selectedParts && this.selectedParts.length > 0;
      }
      if (this.selectedParts.length !== this.committedSnapshot.length) {
        return true;
      }
      var snapshot = this.committedSnapshot;
      for (var i = 0; i < this.selectedParts.length; i++) {
        var current = this.selectedParts[i];
        var match = snapshot.find(function (s) {
          return (
            s.partId === current.partId &&
            s.poNumber === current.poNumber
          );
        });
        if (!match) return true;
        if (Number(match.quantity) !== Number(current.quantity)) {
          return true;
        }
      }
      return false;
    },
    // Label for the bottom-of-panel commit button. When the queue
    // is fully committed to the cart already, swap to "Go to Cart"
    // so the user understands clicking it just navigates rather
    // than re-saving. Reverts to "Continue with N parts" the
    // moment they add or change anything.
    continueButtonText() {
      if (this.hasUnsavedChanges) {
        return this.selectedPartsCount === 1
          ? "Continue with 1 part"
          : "Continue with " + this.selectedPartsCount + " parts";
      }
      return "Complete Order(s)";
    },

    // ===== INLINE WEAR PANEL VISIBILITY =====
    // Panel renders only when the user has explicitly opted
    // into wear-only filtering AND has all the context the
    // API needs. Without companyId we can't load assemblies;
    // without machineId there's nothing to scope wear parts
    // to. Both must be set before the panel appears.
    // Step 3 panel visibility. Renders whenever a machine is
    // picked — the "Show wear parts only" toggle no longer
    // gates the whole panel, just whether wear-only filtering
    // applies inside it. companyId is also required because
    // the assembly options API needs both. The panel handles
    // empty states gracefully when the machine has no
    // assemblies on file.
    showInlineWearParts() {
      return (
        !!this.companyId &&
        !!this.machineId
      );
    },

    // Accordion of sub-assemblies + parts only renders once
    // the user picks an assembly from the inline dropdown.
    // Loading and empty states are still rendered above the
    // accordion regardless of this flag.
    showWearPartsList() {
      return !!this.selectedWearAssemblyId;
    },

    // ===== PARTS INQUIRY MODAL =====
    // Send button stays disabled until name/email/description
    // are non-empty AND the email roughly looks like an
    // email. Backend re-validates with [EmailAddress] data
    // annotation; this is just a lightweight UX gate so the
    // user doesn't blast a half-filled form at the server.
    partsInquiryFormValid() {
      var p = this.partsInquiry;
      if (!p.customerName || !p.customerName.trim()) return false;
      if (!p.customerEmail || !p.customerEmail.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.customerEmail)) return false;
      if (!p.description || !p.description.trim()) return false;
      return true;
    },

    // ===== WEAR-ELIGIBILITY SET =====
    // Object-map projection of wearPartIds for O(1) lookup
    // inside isWearEligible(part). Recomputed only when
    // wearPartIds changes (single fetch on mount), so the
    // map-build cost is paid once per page load.
    //
    // Object.create(null) instead of {} so prototype keys
    // (toString, hasOwnProperty, etc.) can't false-positive
    // when a part ID happens to collide with one — a
    // theoretical concern with all-numeric IDs but free
    // protection.
    wearPartIdSet() {
      var s = Object.create(null);
      var arr = this.wearPartIds || [];
      for (var i = 0; i < arr.length; i++) {
        s[String(arr[i])] = true;
      }
      return s;
    },
  },
  mounted() {
    // ===== HYDRATION RACE GUARD =====
    // Flipped to true at the end of syncFromActiveCart (success,
    // early return, or error). Until then, syncQueueToCart's
    // empty-queue → deleteCart branch is gated off so the
    // selectedParts watcher firing on mount-time hydration can't
    // race ahead of the in-flight cart fetch and soft-delete the
    // cart we came here to keep adding to.
    //
    // Why it matters: loadSelectionsFromStorage runs synchronously
    // and sets selectedParts to [] when there's no saved queue
    // for this company/cart — which is the normal case when
    // arriving from /cart → Modify Order → Add More Parts. That
    // assignment fires the deep watcher, which schedules a sync
    // 300ms out. If apiService.getCart (inside syncFromActiveCart)
    // takes longer than 300ms to return — common over typical
    // latencies — syncQueueToCart fires first, sees an empty
    // queue with a non-zero localStorage.cartId, and deletes the
    // cart. The cart then shows up under "Recently Deleted Carts"
    // on /account/savedcarts, the staging tray is empty, and the
    // user is left with no way to continue building.
    //
    // Set non-reactively (direct on instance, not in data()) so
    // changing it doesn't ripple through Vue's reactivity system.
    // Same convention as _syncTimer / _syncDirty.
    this._hydrationComplete = false;

    $(".ui.dropdown").dropdown({
      fullTextSearch: true,
      sortSelect: true,
      match: "text",
      // Force the menu to render below the field. Without this,
      // Semantic UI flips upward when the natural menu height
      // doesn't fit below — and since we removed the 60vh cap
      // (max-height: none on #MachineSelect .menu /
      // #WearAssemblySelect .menu), the menu can easily be tall
      // enough to overflow the top of the viewport instead of
      // the bottom, clipping the first several items. Forcing
      // downward keeps the top of the list anchored where the
      // user clicked; if it overflows the bottom, normal page
      // scroll covers it.
      direction: "downward",
    });
    delete localStorage.code;
    delete localStorage.description;
    if (localStorage.cartId && localStorage.cartId > 0) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }

    if (localStorage.companyId) {
      this.companyId = localStorage.companyId;
      this.getMachinesByCompany();
      // Fire the insights fetch on initial load so the right-side
      // "Frequently Ordered" panel populates from the same data
      // source as the Account Dashboard.
      this.getInsights();
    }

    // Restore any in-progress queue from a previous visit to this
    // page. Runs after companyId is set so the helper can verify
    // the saved queue belongs to the same company; if the user
    // switched companies between visits, the saved queue is
    // discarded rather than restored against the wrong context.
    this.loadSelectionsFromStorage();

    // Cross-page sync: if there's an active order cart linked
    // to this user, re-derive the staging queue from its
    // server-side contents. Cart is the source of truth across
    // the Search page, this page, and SavedCarts → Resume —
    // they all read/write the same cart, so the queue should
    // mirror the cart's current state at mount. This is what
    // makes a part the user added on the Search page show up
    // here ready for further additions to the same Order/PO.
    this.syncFromActiveCart();

    // After hydration, validate that the linked cart still
    // exists with parts. Non-blocking — if validation finds
    // drift (cart deleted, emptied, or otherwise gone), it
    // breaks the local linkage so the button reverts to
    // "Continue with N parts" and a fresh save creates a new
    // cart. See validateLinkedCart for details.
    this.validateLinkedCart();

    if (this.$route.params.fullOrder) {
      this.fullOrder = this.$route.params.fullOrder;
    }

    this.getCompanies();

    // ===== MODIFY-FLOW HYDRATION =====
    // If the user arrived from the Modify Order modal on
    // Cart.vue, the URL carries `modify*` query params
    // identifying which (Order#, machine, assembly) combo
    // they want to keep adding parts to. Walk Steps 1-3
    // through their pre-fill chain. Runs after getCompanies
    // so the company list is at least in flight by the time
    // applyModifyQuery sets companyId (the company select
    // doesn't strictly need its options loaded for the
    // value to bind, just for the dropdown to show the
    // matching label). The existing syncFromActiveCart
    // call above rebuilds the staging tray from the cart's
    // server-side parts so the user sees what they
    // already added.
    this.applyModifyQuery();

    // ===== HEADER-SEARCH HYDRATION =====
    // Restores search context when the user navigated here
    // from a part-suggestion click in the floating header
    // search bar. URL parameters get stripped on a normal
    // /orders/create navigation (the "New Order" nav link
    // goes to a bare URL), so we read from
    // localStorage.headerSearchParams as the navigation-
    // survival source. See applyHeaderSearchQuery for the
    // full URL/storage fallback chain.
    //
    // Runs AFTER applyModifyQuery on purpose:
    //   - applyModifyQuery has its own URL-stripping side
    //     effect that wipes ALL query params, including
    //     anything header-search might have placed there.
    //     For a pure header-search arrival (no modify*),
    //     applyModifyQuery returns early without touching
    //     the URL, so our params survive.
    //   - When BOTH flows could activate (modify URL +
    //     persisted header search in localStorage), modify
    //     wins on collision: applyModifyQuery sets
    //     pendingModify* fields synchronously, and
    //     applyHeaderSearchQuery's first-line guard checks
    //     those fields and defers without touching state.
    this.applyHeaderSearchQuery();

    // Fetch the global wear-eligibility list. Powers the
    // "2% Discount" chip on inline parts. Single fetch per
    // page load; see loadWearPartIds for caching rationale.
    this.loadWearPartIds();

    // ===== INLINE WEAR PANEL BOOTSTRAP =====
    // Vue watchers only fire on CHANGES, not initial
    // values, so when the page mounts with localStorage
    // state already populated (e.g. user navigates back to
    // /orders/create from /cart with company+machine still
    // selected and toggle already on), the watchers stay
    // silent and the wear panel would never auto-populate.
    // Force a load here.
    //
    // Wrapped in a delay because machineId is set by a
    // Semantic UI dropdown which doesn't always fire its
    // change immediately on mount — same dynamic that
    // ShopWearParts.vue handles with a 1500ms retry.
    var self = this;
    setTimeout(function () {
      // Pull machineId from localStorage if v-model didn't
      // already pick it up — needed for the image load and
      // the assembly-options bootstrap below.
      //
      // Carve-out for header-search hydration: when the user
      // arrived via a part-suggestion click and we have a
      // pendingHeaderMachineName waiting for the machines
      // list, DON'T restore the stale localStorage value.
      // applyPendingHeaderMachine (called from
      // getMachinesByCompany's resolve) will set the correct
      // machineId once the list arrives. Restoring here
      // first would briefly load assemblies for the wrong
      // machine before being overridden, producing a visible
      // flicker in Step 3.
      if (!self.machineId
          && !self.pendingHeaderMachineName
          && localStorage.machineId) {
        self.machineId = localStorage.machineId;
      }
      // Validate the just-restored machineId against the
      // (potentially-already-loaded) machines list. Without
      // this, a machineId tagged in localStorage from a
      // different company silently makes Step 2 look complete
      // and unconditionally reveals Step 3 even though the
      // dropdown's placeholder is showing. The earlier
      // validation hook in getMachinesByCompany only catches
      // the case where machineId is already set when the
      // machines list arrives — in this code path the
      // sequence is reversed (machines arrive first, then
      // machineId is restored 600ms later), so we re-run
      // the check here.
      self.validateMachineSelection();
      // Tell Semantic UI to redraw its dropdown label so the
      // restored machineId actually shows. Without this the
      // value is bound on the hidden input but the dropdown
      // reads "Select your machine".
      self.syncMachineDropdownDisplay();
      // Load the machine image on initial mount if a
      // machine is already selected. Watcher only fires on
      // CHANGES, so without this an existing machineId
      // (from localStorage or a return-navigation) would
      // never get its image rendered.
      if (self.machineId) {
        self.loadMachineImage();
      }
      // Bootstrap the inline parts panel. Step 3 is now
      // visible whenever a machine is selected (regardless
      // of configuredWear), so always populate the
      // assembly options if we've got the context.
      //
      // Race-guard for header-search hydration: when the
      // user arrived via a header-search click, the
      // hydration chain (applyPendingHeaderMachine →
      // machineId watcher → loadInlineWearAssemblyOptions)
      // has ALREADY initiated a load. Firing a second one
      // here bumps wearLoadKey, which silently invalidates
      // the in-flight parts response (the one whose
      // selectedWearAssemblyId was set by the deep search),
      // killing the floated-part + sub-assembly hydration.
      // Skip the redundant load when header-search is the
      // arrival path; the watcher already covered it.
      if (self.companyId && self.machineId && !self.headerSearch.active) {
        self.loadInlineWearAssemblyOptions();
      }
    }, 600);
  },
  data() {
    return {
      // Step accordion: which step's full block is expanded. null =
      // "follow completion" (currentStep derives the first incomplete step).
      expandedStep: null,
      assembly: "",
      assemblies: [],
      assemblyId: "",
      machine: "",
      machines: [],
      machineId: "",
      company: "",
      companies: [],
      companyId: "",
      // Friendly display name of the selected company — populated
      // alongside the insights fetch so the panel can read "parts
      // <Company Name> orders most often" rather than a bare ID.
      companyName: "",
      poNumber: "",
      configuredWear: true,
      // ===== MODIFY-QUERY HYDRATION =====
      // Stash the assembly + sub-assembly IDs from the
      // ?modify* query params here while we wait for the
      // assembly options list to populate (loadInline-
      // WearAssemblyOptions). Once options arrive, the
      // loader checks these and applies them, then nulls
      // them out. Keeping it on `data` (not just a local
      // closure) means the loader can pick them up
      // regardless of how it was triggered (mounted, watcher,
      // or manual call).
      pendingModifyAssemblyId: null,
      pendingModifySubAssemblyId: null,

      // ===== HEADER-SEARCH HYDRATION =====
      // Mirrors the implementation in /search (Index_-_-
      // Search.vue): when the user clicked a part suggestion
      // in the floating header search bar and navigated here
      // (either directly or by clicking "New Order" while a
      // search was active), these fields drive the
      // hydration chain:
      //
      //   headerSearch          — display state for the
      //                           banner that renders at the
      //                           top of the right column.
      //                           `active` gates v-if.
      //   pendingHeaderMachine- — stashed until machines load,
      //   Name                    then resolved against
      //                           machines[].text. Cleared
      //                           by applyPendingHeader-
      //                           Machine after consumption.
      //   pendingHeaderAssembly — stashed until assembly
      //   Name                    options load. Top-level
      //                           match first; if that
      //                           misses (the common case
      //                           because the suggestion API
      //                           returns the SUB-assembly
      //                           name), promotes to
      //                           pendingHeaderSubAssembly-
      //                           Name and triggers
      //                           applyHeaderAssemblyDeep-
      //                           Search.
      //   pendingHeaderSubAss-  — populated by the deep-
      //   emblyName               search promotion path.
      //                           Consumed inside the parts
      //                           loaders to set selected-
      //                           SubAssemblyId once the
      //                           matching parent's parts
      //                           have arrived.
      //
      // Same lifecycle and "modify wins on collision"
      // semantics as the pendingModify* family above —
      // applyHeaderSearchQuery's first-line guard checks
      // pendingModify* fields and bails if any are set, so
      // arriving via the cart's Modify Order modal never
      // gets clobbered by a stale header search persisted in
      // localStorage.
      headerSearch: {
        code: "",
        machineName: "",
        assembly: "",
        active: false,
      },
      pendingHeaderMachineName: null,
      pendingHeaderAssemblyName: null,
      pendingHeaderSubAssemblyName: null,

      // ===== WEAR-ELIGIBILITY LOOKUP =====
      // Flat array of PartIds (as strings) that are
      // registered in dbo.Wears. Loaded once on mount via
      // apiService.getWearPartIds (which calls
      // /api/wear/GetWearPartIds — see WearController.cs).
      // Powers the green "2% Discount" chip on the inline
      // parts list: the chip used to be gated on
      // `configuredWear`, which conflated "this part is
      // wear-eligible" (a per-part attribute) with "the
      // user has the wear-only toggle on" (a view filter).
      // Result: parts that ARE wear-eligible got no chip
      // when the user was browsing in all-parts mode.
      //
      // Stored as an array (rather than a Set) because
      // Vue 2 reactivity doesn't track Set mutations. The
      // wearPartIdSet computed wraps it in an object map
      // for O(1) lookups inside isWearEligible(part).
      //
      // Empty array on init means "not loaded yet" — the
      // chip simply won't render until the load completes,
      // which is the same UX as if the part isn't eligible.
      // The fetch is cheap (returns just IDs), so this
      // window is brief.
      wearPartIds: [],

      // ===== MACHINE NAME → CompanyMachine.Id RESOLVER =====
      // Stash the machine description text from
      // ?modifyMachineName here so getMachinesByCompany.then
      // can look up the matching CompanyMachine.Id once the
      // dropdown's options arrive. Required because the
      // ?modifyMachineId query param is the catalog Machine.Id
      // (PurchaseOrder.MachineId in the cart's modifyOptions),
      // not the CompanyMachine.Id Step 2's dropdown is keyed
      // on. Looking up by description text is the only way to
      // bridge the two ID spaces from frontend state alone.
      // applyPendingModifyMachineName() consumes and clears
      // this — running both from getMachinesByCompany.then
      // (the .then path) and from applyModifyQuery itself
      // when machines are already loaded (the
      // already-resolved path).
      pendingModifyMachineName: null,
      // searchTerm is unused now that the keyword-search panel is
      // gone, but keep the property so any external bindings (route
      // query helpers, etc.) don't blow up.
      searchTerm: "",
      companyError: false,
      machineError: false,
      fullOrder: "",
      disabled: false,

      // ===== INSIGHTS PANEL STATE =====
      // Mirrors the Account Dashboard's insights model so the same
      // getInsights() endpoint powers both. Only `topParts` is rendered
      // on this page; other fields are kept on the object so a future
      // expansion (e.g. spend totals) doesn't require a data shape
      // change. addingInsightPart prevents double-clicks during the
      // resolveTopPartContext round trip. displayLimit drives the
      // "See more" pagination — starts at 5 and increments by 5 with
      // each click, resetting whenever the YTD/All Time period flips
      // so a stale expanded view doesn't carry over.
      insights: {
        topParts: [],
      },
      insightsPeriod: "ytd",
      insightsLoading: false,
      addingInsightPart: false,
      displayLimit: 5,
      // History search — case-insensitive substring filter against
      // partCode, description, and contextDescription on
      // insights.topParts. Empty string = no filter (show all).
      searchTerm: "",

      // ===== MULTI-SELECT QUEUE =====
      // Parts the user has tapped Add on but hasn't yet committed.
      // Each entry carries its own poNumber and quantity, so the
      // queue can hold parts spread across multiple Orders. Drives
      // the grouped tray rendering (Order #1, #2, ...) and the
      // "Continue with N parts" button at the bottom. On Continue,
      // the whole queue is shaped into a purchaseOrders payload
      // (one purchaseOrder per PO) and handed to apiService.saveCart.
      selectedParts: [],

      // ===== PO MODAL STATE =====
      // Two distinct modals now (was one):
      //   - showPoPickerModal: shown when at least one Order already
      //     exists in the queue. Lets the user pick which existing
      //     Order to add the part to, OR start a new PO Number.
      //   - showPoExplainerModal: shown when no Order exists yet, OR
      //     when the user picks "Use a New PO Number" from the picker.
      //     Hosts the inline PO input + grouping explanation.
      //
      // pickerMode: 'add' for a brand-new part being queued, 'edit'
      // for a part already in the queue. In 'edit' mode the picker
      // shows a Remove option and hides the part's current Order
      // (since picking the same Order would be a no-op).
      // pendingAction tracks what the user was trying to do so the
      // post-modal logic can resume cleanly (add a queued part vs
      // commit the whole batch).
      showPoPickerModal: false,
      showPoExplainerModal: false,
      poExplainerInput: "",
      pickerMode: "add",
      pendingAction: null,
      pendingPart: null,

      // Snapshot of the queue's parts at the moment of the last
      // successful saveCart (or sync). Used to detect whether
      // the queue has been modified since — when it matches the
      // current selectedParts exactly, the cart and staging tray
      // are in sync and the Continue button can swap to "Go to
      // Cart" instead. Cleared when the cart is deleted.
      //
      // Each entry: { partId, poNumber, quantity }
      // null = nothing has been committed yet (pre-Continue).
      committedSnapshot: null,

      // Debounce + concurrency coordination for the staging tray
      // → cart sync. _syncTimer holds the pending setTimeout id
      // so successive scheduleSyncToCart calls collapse into one
      // fire. _syncDirty flips true when a change comes in
      // mid-flight; the in-flight sync's finally block re-fires
      // after resolving so the cart catches up.
      _syncTimer: null,
      _syncDirty: false,

      // ===== INLINE WEAR PARTS PANEL =====
      // When `configuredWear` is on AND a machine is picked,
      // a wear parts panel renders in the right column above
      // Frequently Ordered. Replaces the navigation to
      // /orders/shopwearparts entirely — users add wear
      // parts straight into the existing staging tray
      // without leaving Create.
      //
      // Two API calls drive it:
      //   1. getSelectItemsByCompanyMachine(machineId)
      //      → fills wearAssemblyOptions (the dropdown)
      //   2. getAssembliesWithWearParts(assemblyId,
      //      companyMachineId) → fills wearAssemblies (the
      //      accordion of sub-assemblies + their wear parts)
      // Same endpoints ShopWearParts.vue uses — no backend
      // changes needed.
      wearAssemblyOptions: [],
      selectedWearAssemblyId: null,
      wearAssemblies: [],
      wearAssembliesLoading: false,
      wearPartsLoading: false,
      // Map of {assemblyId: bool} so the user can collapse /
      // expand each sub-assembly card independently. Default
      // is everything expanded since the panel is small and
      // most users will want to see the parts immediately.
      expandedWearAssemblies: {},
      // Monotonic key — bumped every time loadWearAssemblyOptions
      // or loadWearPartsForAssembly fires. The async callbacks
      // compare the captured key against the current one and
      // bail if it's stale. Prevents race conditions when the
      // user switches machines or assemblies faster than the
      // network responds.
      // NOTE: must NOT be prefixed with `_` — Vue 2 reserves the
      // `_` and `$` prefixes for internal use and will not proxy
      // them onto the component instance, which would make
      // `this.wearLoadKey` permanently `undefined` and break
      // the staleness comparison (NaN !== NaN is always true,
      // so every response would be discarded).
      wearLoadKey: 0,

      // ===== MACHINE IMAGE =====
      // Loaded from getCompanyMachineDetails when a machine
      // is picked. Mirrors the canonical pattern used in
      // ShopWearParts.vue: image path is appended to the
      // Steele blob storage URL. Click-to-zoom uses the
      // same modal + pan/zoom controls as ShopWearParts so
      // the UX is consistent across pages.
      machineImage: null,
      machineImageUrl: "https://steeleimages.blob.core.windows.net/images/",
      machineImageLoading: false,
      // ===== PARTS INQUIRY MODAL =====
      // Drives the "Don't see your machine? Email Parts
      // Team" popup that opens from the machine dropdown
      // footer. Form fields back the modal inputs;
      // submission state flags drive the send button +
      // success/error banners.
      showPartsInquiryModal: false,
      partsInquirySending: false,
      partsInquirySuccess: false,
      partsInquiryError: "",
      partsInquiryFile: null,
      partsInquiryFileError: "",
      partsInquiry: {
        customerName: "",
        customerEmail: "",
        customerCompany: "",
        machineDescription: "",
        description: "",
      },
      // Assembly image — loaded when the user picks an
      // assembly in Step 3. Comes from getAssemblyDetails
      // (same call ShopWearParts.vue uses for its assembly
      // image). Reuses the same zoom modal as the machine
      // image — clicking either opens the same overlay.
      assemblyImage: null,
      assemblyImageLoading: false,
      // ===== SUB-ASSEMBLY =====
      // Optional drill-down: when wearAssemblies (the
      // sub-assembly array for the picked top-level
      // assembly) has multiple entries, the user can pick
      // one from a "Jump to Sub-Assembly" dropdown to
      // collapse the accordion view to just that
      // sub-assembly. Null = show all.
      selectedSubAssemblyId: null,
      subAssemblyImage: null,
      subAssemblyImageLoading: false,
      // Modal + zoom state — ported verbatim from ShopWearParts
      // so users see the same affordances on both pages.
      showImageModal: false,
      modalImage: "",

      // ===== IN-PLACE HOVER ZOOM =====
      // transform-origin string per image instance, updated by
      // inPlaceZoomMove() on mousemove. The CSS rule
      // `.machine-image-wrap:hover .machine-preview-img` applies
      // `transform: scale(...)`, and the inline transform-origin
      // (set from this map) controls WHICH point stays anchored,
      // so as the user moves the cursor the zoomed view "follows"
      // the cursor — classic Amazon/Etsy hover-magnify pattern.
      // Defaults to '50% 50%' so the very first hover (before
      // mousemove fires) zooms from the image center rather than
      // from a stale prior position.
      inPlaceZoomOrigin: {
        machine: "50% 50%",
        assembly: "50% 50%",
      },

      // ===== IN-PLACE LOCK STATE =====
      // Toggled by onImageClick after a single-click is confirmed
      // (i.e., after the 300ms double-click detection window
      // closes without a second click). When `true` for a given
      // key:
      //   - inPlaceZoomMove suppresses transform-origin updates
      //     (the lock holds the cursor's position at click time)
      //   - .machine-image-wrap.is-zoom-locked CSS kicks in:
      //     position:sticky so the wrap follows the viewport on
      //     scroll, plus a red border + crosshair cursor as a
      //     visual signal
      // Click again to unlock; double-click anywhere on the image
      // to open the full popup modal.
      inPlaceZoomLocked: {
        machine: false,
        assembly: false,
      },

      // ===== LOCKED-PANEL VIEWPORT GEOMETRY =====
      // Three pixel values that position the locked image
      // panel over the .insights-panel (Frequently Ordered)
      // container on the right side of the page. Set by
      // onImageClick on lock activation: it measures the
      // .insights-panel's bounding rect and stashes top,
      // left, and width here. The wrap reads them via inline
      // CSS custom properties (--lock-top / --lock-left /
      // --lock-width) so the fixed-positioned wrap adopts
      // the right column's geometry exactly.
      //
      // Defaults are intentionally non-destructive: if the
      // measurement fails (insights-panel not found, edge
      // case timing), CSS fallbacks (top:100px, left:auto,
      // width:auto) keep the panel visible somewhere
      // reasonable rather than vanishing offscreen.
      //
      // Single set of values shared between machine and
      // assembly locks — only one is locked at a time.
      lockedStickyTop: 100,
      lockedStickyLeft: 0,
      lockedStickyWidth: 0,
      scale: 1,
      minScale: 1,
      maxScale: 5,
      pointX: 0,
      pointY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,

      clear: "",
    };
  },
  watch: {
    // Reset pagination on each new search so users always see the
    // first 5 matches of the filtered list, not a stale expanded
    // view from a previous filter.
    searchTerm() {
      this.displayLimit = 5;
    },

    // ===== INLINE WEAR PANEL: assembly-options reload =====
    // Three inputs determine whether the dropdown should be
    // populated and what it should contain:
    //   - configuredWear: panel visibility gate
    //   - machineId: filter for assemblies (machine-specific)
    //   - companyId: filter for assemblies (company-specific)
    // Any change to any of these resets the picker and
    // re-loads.
    configuredWear() {
      // Toggling the "Show wear parts only" checkbox doesn't
      // change the assembly options (the dropdown is the
      // full machine assembly list either way) — it only
      // changes which API gets called for the selected
      // assembly's parts. Reload parts in-place so the user
      // sees the new mode reflected immediately.
      if (this.selectedWearAssemblyId) {
        this.loadInlinePartsForSelectedAssembly();
      }
      // Defensive re-bind: if Vue's diff replaced the
      // assembly dropdown's DOM node when sibling v-ifs
      // around it changed, Semantic UI's event handlers
      // are now bound to a discarded node. Re-running
      // .dropdown() on the (current) #WearAssemblySelect
      // node restores click-to-open behavior. Idempotent
      // when the binding is already healthy.
      var self = this;
      this.$nextTick(function () {
        try {
          $("#WearAssemblySelect").dropdown({
            fullTextSearch: true,
            direction: "downward", // see main init — keep menu below the field
          });
          if (self.selectedWearAssemblyId) {
            $("#WearAssemblySelect").dropdown(
              "set selected",
              self.selectedWearAssemblyId
            );
          }
        } catch (e) {
          // Step 3 not in DOM — harmless.
        }
      });
    },
    machineId() {
      // Switching machines invalidates everything — the
      // assembly list, the picker selection, and the parts
      // accordion all need to reset. Then reload the
      // assembly options regardless of configuredWear, since
      // Step 3 is now always visible whenever a machine is
      // selected.
      this.resetInlineWearState();
      if (this.machineId) {
        this.loadInlineWearAssemblyOptions();
        // Auto-advance the step accordion to the part picker.
        this.expandedStep = 3;
      }
      // Refresh the machine image alongside the wear panel.
      // No-ops cleanly when machineId is empty.
      this.loadMachineImage();
    },
    // When the visible build step changes (e.g. clicking a "Completed
    // Steps" circle to revise), re-report the locked image to App.vue so
    // the floating cart stack stays correctly offset (or rises back if the
    // locked image's step is now hidden).
    currentStep() {
      this._emitLockedImage();
    },
    // Reload assemblies on company change — `companyId` has
    // its own existing watcher above for queue-stashing; we
    // hook the wear panel into that same change via a
    // separate handler. Vue allows multiple watchers when
    // they're on different keys, but here we share the same
    // key so we splice into the existing one below instead.

    // ===== INLINE WEAR PANEL: parts-accordion reload =====
    // When the user picks an assembly from the inline
    // dropdown, fetch its sub-assemblies + wear parts.
    selectedWearAssemblyId(newId) {
      // Switching the parent assembly resets the sub-
      // assembly picker — sub-assembly options come from
      // wearAssemblies which is about to be reloaded.
      this.selectedSubAssemblyId = null;
      this.subAssemblyImage = null;
      if (newId) {
        this.loadInlinePartsForSelectedAssembly();
        this.loadAssemblyImage(newId);
      } else {
        this.wearAssemblies = [];
        this.assemblyImage = null;
      }
    },

    // Sub-assembly drill-down. When the user picks one,
    // load its image and auto-expand its accordion card
    // (the user clearly wants to see those parts). Clearing
    // the picker (back to null) restores the all-sub-
    // assemblies view but doesn't change which cards are
    // expanded — that belongs to the user.
    selectedSubAssemblyId(newId) {
      if (newId) {
        this.loadSubAssemblyImage(newId);
        this.$set(this.expandedWearAssemblies, newId, true);
      } else {
        this.subAssemblyImage = null;
      }
    },
    // Persist the queue to localStorage on every change so it
    // survives navigation away and back via breadcrumbs (or any
    // other route change). Deep-watched because we mutate
    // individual queue rows (PO change, qty change) via $set —
    // shallow watch would miss those. Also schedules a debounced
    // cart sync so qty edits in the staging tray flow back to
    // the server cart instead of dying as local-only state.
    // scheduleSyncToCart bails cleanly when there's no linked
    // cart, so this is safe to fire on every mutation including
    // mount-time hydration.
    selectedParts: {
      deep: true,
      handler() {
        this.saveSelectionsToStorage();
        this.scheduleSyncToCart();
      },
    },
    // When the user switches companies, we DON'T wipe the queue —
    // we stash it under the outgoing company's slot and hydrate
    // whatever was previously saved under the incoming company.
    // This mirrors the cart's per-company persistence so an admin
    // shopping across multiple customers can jump between contexts
    // without losing in-progress selections in either.
    companyId(newId, oldId) {
      // Skip the initial empty → real-id assignment that happens
      // during mount-time hydration; that's handled by the explicit
      // loadSelectionsFromStorage call in mounted().
      if (!oldId || !newId || String(oldId) === String(newId)) return;

      // Persist the current queue under the OUTGOING company's key
      // so the user can return to it later. Pass oldId explicitly
      // because this.companyId has already moved on to newId.
      if (this.selectedParts && this.selectedParts.length > 0) {
        this.saveSelectionsToStorage(oldId);
      }

      // Hydrate the queue for the INCOMING company. If they have
      // no saved queue, the helper resets selectedParts to [] so we
      // start clean rather than inheriting the previous company's
      // queue.
      this.loadSelectionsFromStorage();

      // Inline wear panel — the assembly list is scoped to
      // (company, machine) so a company switch invalidates
      // it. Reset the picker selection and re-fetch options
      // if the panel is currently visible.
      this.resetInlineWearState();
      if (this.configuredWear === true) {
        this.loadInlineWearAssemblyOptions();
      }

      // Reload the company-scoped wear list that drives the
      // "2% Discount" chip so it reflects the INCOMING
      // company's own wear parts.
      this.loadWearPartIds();
    },

    // ===== HEADER-SEARCH WATCHERS =====
    // Three safety nets for the header-search hydration
    // chain. None of these are the primary trigger path
    // (mounted + applyPendingHeaderMachine + the loader
    // hooks do the work), but they catch the timing edges
    // where the primary path's call site hasn't fired yet:
    //
    //   - machines: applyPendingHeaderMachine bails
    //     silently if _machinesLoaded is false. This
    //     watcher refires it once the list arrives, in
    //     case the mounted call ran before machines
    //     resolved AND neither getMachinesByCompany.then
    //     nor getMachineSelectedItems.then ended up firing
    //     (e.g. company-from-localStorage didn't trigger
    //     a fresh fetch).
    //
    //   - wearAssemblyOptions: applyHeaderAssemblyDeepSearch
    //     aborts when wearAssemblyOptions is empty. This
    //     watcher refires the deep search once options
    //     populate, covering the path where machineId
    //     bound after the deep search's first attempt.
    //
    //   - $route: when the user navigates within /orders/
    //     create with new query params (rare here, but
    //     possible if Head.vue ever re-targets the create
    //     pages directly), re-run the hydration chain
    //     against the fresh URL.
    machines(newList) {
      if (this.pendingHeaderMachineName
          && Array.isArray(newList) && newList.length > 0
          && this._machinesLoaded) {
        this.applyPendingHeaderMachine();
      }
    },
    wearAssemblyOptions(newOpts) {
      if (this.pendingHeaderSubAssemblyName
          && Array.isArray(newOpts) && newOpts.length > 0
          && !this.selectedWearAssemblyId) {
        this.applyHeaderAssemblyDeepSearch();
      }
    },
    $route(to, from) {
      // Same-path navigations with changed query params
      // count as a re-hydration trigger. Different paths
      // mean the user left this view; nothing to do here.
      if (to.path === from.path
          && JSON.stringify(to.query) !== JSON.stringify(from.query)) {
        this.applyHeaderSearchQuery();
      }
    },
  },
  methods: {
    getCompanies() {
      apiService.getCompanyList().then((data) => {
        this.companies = data;

        if (localStorage.companyId) {
          this.companyId = localStorage.companyId;
          var companyId = this.companyId;
          // Find a friendly display name for the panel header. The
          // company list returns objects shaped like { value, text }
          // where `text` is "COMPANY NAME - (CODE)". Use the full
          // text — the panel sentence reads naturally with it.
          var match = (data || []).find(function (c) {
            return String(c.value) === String(companyId);
          });
          if (match) this.companyName = match.text;
          this.$nextTick(function () {
            setTimeout(function() {
              $("#CompanySelect").dropdown("set selected", companyId);
              $(".ui.dropdown").dropdown({ fullTextSearch: true, direction: "downward" });
            }, 500);
          });
          this.getMachinesByCompany();
        }
      });
    },
    downloadOrderPdf() {
      downloadPdf("order");
    },
    getMachineSelectedItems() {
      apiService.getMachinesList().then((data) => {
        this.machines = data;
        this._machinesLoaded = true;
        this.validateMachineSelection();
        this.syncMachineDropdownDisplay();
        // Header-search hydration — same hook as the
        // primary getMachinesByCompany path below. Either
        // loader path can be the one that arrives last;
        // applyPendingHeaderMachine is idempotent and
        // bails cleanly when there's nothing pending.
        this.applyPendingHeaderMachine();
      });
    },
    getMachinesByCompany() {
      apiService
        .getMachinesByCompany({
          id: this.companyId,
        })
        .then((data) => {
          this.machines = data;
          this._machinesLoaded = true;
          // ===== MODIFY HYDRATION: NAME-BASED MACHINE LOOKUP =====
          // Resolve a stashed modifyMachineName (set by
          // applyModifyQuery when the user arrived from /cart →
          // Modify Order → Add More Parts) to the matching
          // CompanyMachine.Id from the just-loaded machines list.
          // Must run BEFORE validateMachineSelection — without
          // this ordering, validateMachineSelection would clear
          // any previously-bound (but now stale or wrong-ID-space)
          // machineId before the resolver had a chance to set
          // the correct one. See applyPendingModifyMachineName
          // for the full lookup logic and pendingModifyMachineName
          // in data() for the stashing rationale.
          this.applyPendingModifyMachineName();
          this.validateMachineSelection();
          this.syncMachineDropdownDisplay();
          // Header-search hydration. Runs AFTER the modify
          // resolver because modify wins on collision —
          // applyPendingModifyMachineName might have set
          // machineId already, in which case applyPending-
          // HeaderMachine's first-line check (machineId
          // already truthy) makes it a no-op. When modify
          // is NOT in play, this is the path that resolves
          // the header-search machine name.
          this.applyPendingHeaderMachine();
        });
    },
    // ===== applyPendingModifyMachineName =====
    // Consumes pendingModifyMachineName and binds machineId to
    // the CompanyMachine.Id of the matching dropdown option.
    //
    // Why a helper (not inlined): two callsites — the
    // getMachinesByCompany.then path (machines loaded after
    // applyModifyQuery already stashed the name) and
    // applyModifyQuery itself (machines already loaded by the
    // time the modify params landed, e.g. when the company
    // didn't change and the existing list is still valid). One
    // function, two trigger points, eliminates a timing race
    // between the two.
    //
    // ===== Match strategy (3-pass) =====
    // The cart emits modifyMachineName from
    // assembly.machine.description (catalog Machine.Description),
    // but the dropdown's `machine.text` is built server-side by
    // /CompanyMachine/GetSelectItems and the exact format isn't
    // guaranteed to be byte-for-byte identical — it might
    // include a CompanyMachine.Name suffix, a plant/location tag,
    // or different whitespace. Three passes from strict to
    // forgiving:
    //
    //   1. Exact match (case-insensitive, trimmed) — the happy
    //      path when the backend returns the bare description.
    //   2. text contains target — handles "EXTRUDER: 90ADEX
    //      (Plant 2)" containing "EXTRUDER: 90ADEX".
    //   3. target contains text — handles the inverse where
    //      the dropdown's text is shorter than what the cart
    //      emitted (e.g. "90ADEX" vs "EXTRUDER: 90ADEX").
    //
    // If all three miss, the diagnostic log below dumps the
    // target name and the loaded options so the developer can
    // see exactly what came back from the API.
    applyPendingModifyMachineName() {
      if (!this.pendingModifyMachineName) return;
      if (!this._machinesLoaded) return;
      var raw = String(this.pendingModifyMachineName).trim();
      var target = raw.toLowerCase();
      if (!target) {
        this.pendingModifyMachineName = null;
        return;
      }
      var machines = this.machines || [];

      // Pass 1: exact (case-insensitive, trimmed)
      var match = machines.find(function (m) {
        return (
          m &&
          m.text &&
          String(m.text).trim().toLowerCase() === target
        );
      });

      // Pass 2: machine.text contains the target description
      if (!match) {
        match = machines.find(function (m) {
          if (!m || !m.text) return false;
          return String(m.text).trim().toLowerCase().indexOf(target) !== -1;
        });
      }

      // Pass 3: target description contains machine.text
      if (!match) {
        match = machines.find(function (m) {
          if (!m || !m.text) return false;
          var t = String(m.text).trim().toLowerCase();
          if (!t) return false;
          return target.indexOf(t) !== -1;
        });
      }

      if (match) {
        console.log(
          "[Create] modify hydration: matched machine '" +
            raw +
            "' → CompanyMachine.Id=" +
            match.value +
            " (text='" +
            match.text +
            "')"
        );
        this.machineId = String(match.value);
        try {
          localStorage.machineId = match.value;
        } catch (e) {
          // localStorage write can throw in incognito / quota
          // contexts; the in-memory machineId binding is
          // sufficient for the dropdown to display correctly,
          // so swallow and continue.
        }
        // Push the visible label into Semantic UI's cached
        // dropdown state immediately. The .then() callsite
        // already calls this once after applyPendingModify-
        // MachineName returns, but the applyModifyQuery
        // callsite (machines already loaded) does too — and
        // calling it twice from one of those paths is harmless
        // since the underlying jQuery call is idempotent.
        // Calling here in addition guarantees the label
        // refreshes regardless of which path was taken.
        this.syncMachineDropdownDisplay();
      } else {
        console.warn(
          "[Create] modify hydration: NO MATCH for machine '" +
            raw +
            "'. Loaded options:",
          machines.map(function (m) {
            return { value: m && m.value, text: m && m.text };
          })
        );
      }

      // Clear regardless of match — a non-match means the
      // emitting page named a machine this company doesn't
      // own, which is unrecoverable here. Leaving the value
      // set would cause every subsequent machines refresh to
      // re-attempt the same failed lookup.
      this.pendingModifyMachineName = null;
    },
    // Push the current machineId into the Semantic UI dropdown's
    // visible state. Vue's v-model sets the underlying hidden
    // <input>'s value, but Semantic UI's dropdown widget doesn't
    // observe input changes — it caches its visible label
    // internally. So programmatic machineId assignments (from the
    // Add More Parts query handler, from a localStorage restore,
    // from the deep-link reorder flow, etc.) update the bound
    // value but the dropdown still displays the placeholder
    // "Select your machine".
    //
    // Calling dropdown('set selected', value) forces Semantic UI
    // to look up the matching menu item by data-value and refresh
    // its label to match. Mirrors what getCompaniesSuccessful
    // does for CompanySelect right after the company list loads.
    //
    // Defer to nextTick so the v-for over `machines` has rendered
    // its menu items first — set selected can only match against
    // items that exist in the DOM.
    syncMachineDropdownDisplay() {
      var self = this;
      this.$nextTick(function () {
        try {
          if (self.machineId
              && window.$
              && window.$("#MachineSelect").length) {
            window.$("#MachineSelect").dropdown(
              "set selected",
              String(self.machineId)
            );
          }
        } catch (e) {
          // Dropdown may not be initialized yet (rare, but the
          // mount-time setTimeout chain in this component has
          // odd timing); fail silent — the next caller will
          // pick up the work.
        }
      });
    },
    // Verify the currently-bound machineId actually corresponds
    // to a machine in the loaded list. If it doesn't (e.g. the
    // value was restored from localStorage but belongs to a
    // different company, or to a machine that has since been
    // removed), clear it. Without this, Step 2's number circle
    // would flip to "complete" and Step 3 would unconditionally
    // render even though the dropdown's placeholder is showing
    // — `machineId` would be truthy but visibly nothing is
    // selected. The localStorage tag is cleared too so the
    // stale value doesn't keep getting re-restored on each
    // mount.
    //
    // Guarded against the "machines haven't loaded yet" case
    // via _machinesLoaded — without that guard, the mount-time
    // call (from the setTimeout that restores machineId from
    // localStorage) could fire before getMachinesByCompany
    // resolves and clear a perfectly valid machineId because
    // the find ran against an empty list. The matching hook
    // inside getMachinesByCompany picks up the validation
    // once machines actually arrive.
    validateMachineSelection() {
      if (!this.machineId) return;
      if (!this._machinesLoaded) return;
      var self = this;
      var match = (this.machines || []).find(function (m) {
        return String(m.value) === String(self.machineId);
      });
      if (!match) {
        this.machineId = "";
        try { delete localStorage.machineId; } catch (e) {}
      }
    },
    createOrder() {
      if (this.machineId && this.companyId) {
        // ===== TOGGLE ON (wear parts) =====
        // The inline wear parts panel (right column above
        // Frequently Ordered) handles part selection now.
        // The Continue button just routes to /cart so users
        // can review whatever they queued via the inline
        // ADD buttons. Skips the old navigation to
        // /orders/shopwearparts entirely.
        if (this.configuredWear == true) {
          // Persist the context so the cart page can resolve
          // any references back to (company, machine) the
          // user was working in.
          localStorage.machineId = this.machineId;
          localStorage.poNumber = this.poNumber;
          localStorage.configuredWear = this.configuredWear;
          localStorage.companyId = this.companyId;
          this.$router.push({ name: "Cart" });
          return;
        }

        // ===== TOGGLE OFF (full assembly browse) =====
        // Existing behavior — route to Choose Assembly.
        // Phase 2 will inline this flow as well.
        if (this.fullOrder) {
          this.$router.push({
            name: "Create Order - Choose Assembly",
            params: {
              number: this.poNumber,
              machineId: this.machineId,
              configuredWear: this.configuredWear,
              companyId: this.companyId,
              fullOrder: this.fullOrder,
            },
          });
        } else {
          this.$router.push({
            name: "Create Order - Choose Assembly",
            params: {
              number: this.poNumber,
              machineId: this.machineId,
              configuredWear: this.configuredWear,
              companyId: this.companyId,
            },
          });
        }

        localStorage.machineId = this.machineId;
        localStorage.poNumber = this.poNumber;
        localStorage.configuredWear = this.configuredWear;
        localStorage.companyId = this.companyId;
      } else {
        this.machineError = true;
        this.companyError = true;
        return false;
      }
    },
    // ===== INSIGHTS PANEL METHODS =====
    // Mirrors the Account Dashboard's insights logic so this page
    // benefits from any backend improvements there for free. Single
    // API endpoint (apiService.getInsights), single resolution flow
    // (apiService.resolveTopPartContext), single data shape on
    // `insights.topParts`.
    getInsights() {
      if (!this.companyId) return;
      this.insightsLoading = true;
      // Reset progressive disclosure — a fresh fetch should show
      // the first 5 rows again, not whatever expanded state was
      // left over from a previous load.
      this.displayLimit = 5;
      apiService
        .getInsights({
          companyId: this.companyId,
          allTime: this.insightsPeriod === "alltime",
          // Request a larger pool than the dashboard's default 5 so
          // the See more pagination has something to reveal. If the
          // backend ignores this param, the response shape is
          // unchanged (we still slice client-side via displayLimit).
          take: 50,
        })
        .then((data) => {
          this.insights = data || { topParts: [] };
        })
        .catch(() => {
          // Soft-fail to an empty list — the empty-state UI will
          // surface gracefully without a toast for what's really a
          // best-effort sidebar.
          this.insights = { topParts: [] };
        })
        .finally(() => {
          this.insightsLoading = false;
        });
    },
    switchInsightsPeriod(period) {
      if (this.insightsPeriod === period) return;
      this.insightsPeriod = period;
      // Reset on toggle so the user starts at 5 in the new period.
      this.displayLimit = 5;
      this.getInsights();
    },
    // Bump the visible row count by 5 — purely client-side, the
    // full topParts array is already in memory from the initial
    // getInsights() so no extra API call.
    showMoreParts() {
      this.displayLimit = this.displayLimit + 5;
    },
    // ===== MULTI-SELECT QUEUE =====
    // Each part in the queue carries its own poNumber AND quantity
    // so a single queue can hold parts spread across multiple Orders
    // with per-part quantities. The Add button on each row no longer
    // toggles directly — it opens either the picker (existing Orders
    // to choose from + "use a new PO" option) or the explainer
    // (first-time, or "new PO" pick) so the PO context is always
    // explicit.
    isPartSelected(part) {
      if (!part) return false;
      // True if this part is in any Order, under any PO.
      return this.selectedParts.some(function (p) {
        return p.partId === part.partId;
      });
    },
    // Sensible default qty when a part is added — uses the historical
    // average per-order from insights data (totalQuantity / timesOrdered)
    // since "frequently ordered" parts are likely ordered in similar
    // quantities each time. Falls back to 1 if the math doesn't yield
    // a usable number.
    // Compute the default qty for a part being added to the
    // staging tray. Three signals, in priority order:
    //
    //   1. part.quantityPerMachine — engineering spec from the
    //      wear-parts catalog ("this assembly needs N of this
    //      part per machine"). Authoritative when present;
    //      that's the number on the product card the user
    //      just clicked Add on.
    //   2. totalQuantity / timesOrdered — Frequently Ordered
    //      insight, "the user typically orders this many at
    //      a time." Reasonable fallback for non-wear parts
    //      that have order history.
    //   3. 1 — when neither signal is available.
    //
    // The wear-parts panel was previously falling through to
    // the insights branch (which always returned 1 since
    // wear parts don't carry totalQuantity), so users had to
    // manually edit qty after every add. The cart-side
    // payload assembly already supports any qty value, so
    // surfacing the right default here is the only change
    // needed.
    defaultQuantityFor(part) {
      if (!part) return 1;
      var perMachine = Number(part.quantityPerMachine || part.QuantityPerMachine) || 0;
      if (perMachine > 0) return perMachine;
      var total = Number(part.totalQuantity) || 0;
      var times = Number(part.timesOrdered) || 1;
      if (total <= 0 || times <= 0) return 1;
      return Math.max(1, Math.round(total / times));
    },
    // ===== INLINE WEAR PARTS PANEL METHODS =====
    // These power the wear parts panel that appears in the
    // right column above Frequently Ordered when toggle is
    // on + machine selected. Replaces navigation to
    // /orders/shopwearparts entirely.

    // Reset all panel-local state. Called on toggle-off,
    // machine change, and company change so stale data
    // never bleeds into the next selection cycle. The
    // wearLoadKey bump invalidates any in-flight responses
    // — see loadInlineWearAssemblyOptions for how callers
    // check it.
    resetInlineWearState() {
      this.wearLoadKey++;
      this.wearAssemblyOptions = [];
      this.selectedWearAssemblyId = null;
      this.wearAssemblies = [];
      this.expandedWearAssemblies = {};
      this.wearAssembliesLoading = false;
      this.wearPartsLoading = false;
      this.assemblyImage = null;
      this.selectedSubAssemblyId = null;
      this.subAssemblyImage = null;
      // Clear Semantic UI's displayed text on the assembly
      // dropdown — setting selectedWearAssemblyId = null
      // syncs the hidden input via v-model but doesn't
      // touch the visible label. Wrapped because the
      // dropdown may not be mounted (Step 3 hidden when
      // wear-parts-only toggle is off, or panel not yet
      // rendered on first reset).
      try {
        $("#WearAssemblySelect").dropdown("clear");
      } catch (e) {
        // Dropdown not in DOM — harmless.
      }
    },

    // Populate the assembly dropdown for the current
    // machine. Uses the same endpoint the canonical
    // ShopWearParts.vue uses to populate its assembly picker
    // (apiService.getSelectItemsByCompanyMachine →
    // /assembly/GetSelectItemsByCompanyMachine/{machineId}).
    // Returns the FULL assembly list for the machine in
    // {value, text} shape — wear-eligibility filtering happens
    // downstream when the user picks an assembly and we call
    // getAssembliesWithWearParts to load the actual parts.
    //
    // Earlier wired this to apiService.getAssembliesByMachine,
    // which hits /wear/GetAssemblyByMachineAndCompany — wrong
    // endpoint (singular Assembly, returns empty/wrong shape
    // for this UI). The dropdown was always empty as a result.
    async loadInlineWearAssemblyOptions() {
      console.log("[Create] inline wear: loadInlineWearAssemblyOptions called", {
        companyId: this.companyId,
        machineId: this.machineId,
        configuredWear: this.configuredWear,
      });
      if (!this.machineId) {
        console.log("[Create] inline wear: bailing — missing machineId");
        return;
      }
      var key = ++this.wearLoadKey;
      this.wearAssembliesLoading = true;
      try {
        var data = await apiService.getSelectItemsByCompanyMachine({
          id: this.machineId,
        });
        console.log("[Create] inline wear: API response", { key, data });
        // Stale response — user changed the machine before
        // this resolved. Bail without touching state.
        if (key !== this.wearLoadKey) {
          console.log("[Create] inline wear: stale key, ignoring response");
          return;
        }
        // getSelectItemsByCompanyMachine returns {value, text}
        // pairs already, but tolerate alternate shapes in case
        // the backend ever changes serialization.
        var rows = Array.isArray(data) ? data : [];
        var opts = rows.map(function (row) {
          return {
            value: row.value || row.assemblyId || row.id,
            text: row.text || row.assemblyName || row.name,
          };
        }).filter(function (r) {
          return r.value && r.text;
        });
        console.log("[Create] inline wear: normalized options", opts);
        this.wearAssemblyOptions = opts;
        // Initialize / re-bind the Semantic UI dropdown now
        // that Vue has rendered the new <div class="item">
        // children. Calling .dropdown() is idempotent —
        // first run initializes, subsequent runs re-read
        // the menu DOM. The $nextTick wait is required
        // because the v-for items haven't entered the DOM
        // until the current tick finishes. Wrapped in
        // try/catch in case Step 3 isn't mounted yet (the
        // wear-parts-only toggle controls visibility).
        var self = this;
        this.$nextTick(function () {
          try {
            $("#WearAssemblySelect").dropdown({
              fullTextSearch: true,
              direction: "downward", // see main init — keep menu below the field
            });
          } catch (e) {
            // Step 3 may not be in the DOM — harmless,
            // dropdown will init when it appears.
          }
        });
        // Modify-flow hydration step 3 (Step 3 of the
        // create page, fittingly): if we arrived via the
        // Modify Order modal with a target assembly id,
        // pick it up now that options are available. The
        // selectedWearAssemblyId watcher will then trigger
        // the parts loader, which is itself the place
        // pendingModifySubAssemblyId gets consumed.
        if (this.pendingModifyAssemblyId) {
          var target = String(this.pendingModifyAssemblyId);
          var match = opts.find(function (o) {
            return String(o.value) === target;
          });
          if (match) {
            this.selectedWearAssemblyId = match.value;
            // Programmatic selection — Semantic UI's
            // displayed text won't follow the hidden input
            // change on its own, so nudge it.
            this.$nextTick(function () {
              try {
                $("#WearAssemblySelect").dropdown("set selected", match.value);
              } catch (e) {}
            });
          } else {
            console.warn(
              "[Create] inline wear: pendingModifyAssemblyId " + target +
              " did not match any loaded option; clearing"
            );
          }
          this.pendingModifyAssemblyId = null;
        }

        // ===== HEADER-SEARCH ASSEMBLY HYDRATION =====
        // Mirrors the implementation in Index_-_Search.vue.
        // The suggestion API returns the SUB-assembly name
        // (e.g. "28PC WEDGE GROOVE LINER"), but
        // wearAssemblyOptions only holds top-level
        // assemblies (e.g. "HCV AUGERS AND LINERS"). Try
        // top-level first via a multi-strategy match; if
        // that misses (the typical case), promote the name
        // to pendingHeaderSubAssemblyName and trigger
        // applyHeaderAssemblyDeepSearch to fan out across
        // every top-level looking for a child match.
        //
        // Skipped when modify already picked an assembly
        // (selectedWearAssemblyId truthy) — modify wins on
        // collision per the existing pattern.
        if (this.pendingHeaderAssemblyName && !this.selectedWearAssemblyId) {
          var headerAsmTarget = String(this.pendingHeaderAssemblyName);

          var asmNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var asmNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var asmTextCandidate = function (o) {
            return o.text || o.name || o.assemblyName || "";
          };

          var nAsmTarget = asmNorm(headerAsmTarget);
          var nAsmTargetStrong = asmNormStrong(headerAsmTarget);

          console.log("[Create] header search: resolving assembly", {
            target: headerAsmTarget,
            normalized: nAsmTarget,
            optionCount: opts.length,
            sample: opts.slice(0, 8).map(function (o) {
              return {
                value: o.value,
                text: o.text,
                name: o.name,
                assemblyName: o.assemblyName,
              };
            }),
          });

          var asmMatch = null;
          var asmStrategy = null;

          asmMatch = opts.find(function (o) {
            return asmNorm(asmTextCandidate(o)) === nAsmTarget;
          });
          if (asmMatch) asmStrategy = "exact";

          if (!asmMatch) {
            asmMatch = opts.find(function (o) {
              return asmNormStrong(asmTextCandidate(o)) === nAsmTargetStrong;
            });
            if (asmMatch) asmStrategy = "alphanumeric";
          }

          if (!asmMatch) {
            asmMatch = opts.find(function (o) {
              var n = asmNorm(asmTextCandidate(o));
              if (!n || !nAsmTarget) return false;
              return n.indexOf(nAsmTarget) >= 0
                  || nAsmTarget.indexOf(n) >= 0;
            });
            if (asmMatch) asmStrategy = "substring";
          }

          if (asmMatch) {
            console.log(
              "[Create] header search: matched assembly via '" +
              asmStrategy + "' strategy:",
              {
                value: asmMatch.value,
                text: asmTextCandidate(asmMatch),
              }
            );
            this.selectedWearAssemblyId = asmMatch.value;
            this.$nextTick(function () {
              try {
                $("#WearAssemblySelect").dropdown(
                  "set selected", asmMatch.value
                );
              } catch (e) {}
            });
            this.pendingHeaderAssemblyName = null;
          } else {
            console.log(
              "[Create] header search: no top-level match for '" +
              headerAsmTarget + "' — promoting to sub-assembly " +
              "deep search across " + opts.length + " top-level " +
              "assemblies. Available top-level labels:",
              opts.map(function (o) { return asmTextCandidate(o); })
            );
            this.pendingHeaderSubAssemblyName = String(this.pendingHeaderAssemblyName);
            this.pendingHeaderAssemblyName = null;
            this.applyHeaderAssemblyDeepSearch();
          }
        }
      } catch (e) {
        console.warn("[Create] inline wear: assembly options load failed:", e);
        if (key !== this.wearLoadKey) return;
        this.wearAssemblyOptions = [];
      } finally {
        // Always clear the loading flag — the stale-key
        // guard above protects wearAssemblyOptions, but
        // loading is UI state and a transient under-loading
        // flicker is better than infinite "Loading…" if
        // multiple watchers fire on mount and key bumping
        // strands the flag.
        this.wearAssembliesLoading = false;
      }
    },

    // Load the sub-assemblies + wear parts for the picked
    // assembly. Same endpoint ShopWearParts.vue uses —
    // returns sub-assemblies grouped, each with a parts
    // array of wear-eligible parts. Defaults every sub-
    // assembly to expanded so the user sees parts
    // immediately without an extra click.
    async loadInlineWearPartsForAssembly(assemblyId) {
      if (!assemblyId || !this.machineId) return;
      var key = ++this.wearLoadKey;
      this.wearPartsLoading = true;
      try {
        var data = await apiService.getAssembliesWithWearParts({
          assemblyId: assemblyId,
          companyMachineId: this.machineId,
        });
        if (key !== this.wearLoadKey) return;
        // Sort sub-assemblies alphabetically — the API
        // returns them in part-sequence order which isn't
        // useful when there are 6+ groups.
        var list = (data || []).slice().sort(function (a, b) {
          return (a.assemblyName || "").localeCompare(b.assemblyName || "");
        });
        // Header-search part surfacing — pulls the searched
        // part code to the top of its sub-assembly's parts
        // list. Mutates `list` in place BEFORE the reactive
        // assignment below so the reorder rides on a single
        // reactive update, and stashes _pendingHeaderExpand-
        // AssemblyId for the post-assignment auto-expand.
        this._floatHeaderSearchPartToTop(list);
        this.wearAssemblies = list;
        // Auto-expand the first sub-assembly so the user
        // immediately sees parts rather than a stack of
        // collapsed cards (which is easy to miss). When
        // there's more than one, the template renders a
        // "more categories below" hint so users know to
        // expand the rest. When there's only one, expand-
        // ing it is the obvious default — there's nothing
        // else to choose.
        var initialExpanded = {};
        if (list.length > 0) {
          initialExpanded[list[0].assemblyId] = true;
        }
        // If the part-floater stashed a sub-assembly id,
        // expand that card too. Merge into the same
        // initialExpanded map BEFORE assignment so both
        // expands ride on the single reactive update.
        if (this._pendingHeaderExpandAssemblyId != null) {
          initialExpanded[this._pendingHeaderExpandAssemblyId] = true;
          this._pendingHeaderExpandAssemblyId = null;
        }
        this.expandedWearAssemblies = initialExpanded;
        // Modify-flow hydration step 4: pick up a pending
        // sub-assembly target if we arrived via the Modify
        // Order modal. Setting selectedSubAssemblyId fires
        // its watcher which auto-expands that card and
        // loads its image — same effect as the user
        // picking it from the dropdown manually.
        if (this.pendingModifySubAssemblyId) {
          var subTarget = String(this.pendingModifySubAssemblyId);
          var subMatch = list.find(function (sa) {
            return String(sa.assemblyId) === subTarget;
          });
          if (subMatch) {
            this.selectedSubAssemblyId = subMatch.assemblyId;
          }
          this.pendingModifySubAssemblyId = null;
        }

        // Header-search sub-assembly hydration. The deep
        // search sets selectedWearAssemblyId to the parent
        // it identified as containing the target sub-
        // assembly; this is the loader run for that parent.
        // Multi-strategy match against the freshly-loaded
        // sub list, set selectedSubAssemblyId on hit, auto-
        // expand its card so the floated part is visible
        // without an extra click.
        if (this.pendingHeaderSubAssemblyName
            && !this.selectedSubAssemblyId) {
          var hsTarget = String(this.pendingHeaderSubAssemblyName);
          var hsNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var hsNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var hsNT = hsNorm(hsTarget);
          var hsNTS = hsNormStrong(hsTarget);

          var hsMatch = list.find(function (sa) {
            return hsNorm(sa.assemblyName) === hsNT;
          }) || list.find(function (sa) {
            return hsNormStrong(sa.assemblyName) === hsNTS;
          }) || list.find(function (sa) {
            var n = hsNorm(sa.assemblyName);
            return n && hsNT && (
              n.indexOf(hsNT) >= 0 || hsNT.indexOf(n) >= 0
            );
          });

          if (hsMatch) {
            console.log(
              "[Create] header search (wear loader): " +
              "matched sub-assembly '" + hsMatch.assemblyName +
              "' (id " + hsMatch.assemblyId + ")"
            );
            this.selectedSubAssemblyId = hsMatch.assemblyId;
            this.$set(
              this.expandedWearAssemblies,
              hsMatch.assemblyId,
              true
            );
          } else {
            console.warn(
              "[Create] header search (wear loader): no " +
              "sub-assembly match for '" + hsTarget +
              "'. Available sub-assembly names:",
              list.map(function (sa) { return sa.assemblyName; })
            );
          }
          this.pendingHeaderSubAssemblyName = null;
        }
      } catch (e) {
        if (key !== this.wearLoadKey) return;
        console.warn("[Create] inline wear: parts load failed:", e);
        this.wearAssemblies = [];
      } finally {
        // Same always-clear pattern as above.
        this.wearPartsLoading = false;
      }
    },

    // Dispatcher — picks between the wear-only loader and
    // the all-parts loader based on the configuredWear
    // toggle. Both populate the same `wearAssemblies` array
    // (the shape is identical: array of {assemblyId,
    // assemblyName, parts: [...]}), so the accordion
    // template doesn't care which path filled it.
    loadInlinePartsForSelectedAssembly() {
      if (!this.selectedWearAssemblyId) return;
      if (this.configuredWear) {
        this.loadInlineWearPartsForAssembly(this.selectedWearAssemblyId);
      } else {
        this.loadInlineAllPartsForAssembly(this.selectedWearAssemblyId);
      }
    },

    // All-parts variant — calls getAssemblyWithParts (the
    // same API ShopWearParts.vue uses when "Show wear parts
    // only" is OFF). Returns sub-assemblies grouped, each
    // with a parts array of every part on the assembly,
    // wear-eligible or not. Normalizes to the same shape
    // wear-parts uses so the accordion template can render
    // both transparently.
    async loadInlineAllPartsForAssembly(assemblyId) {
      if (!assemblyId || !this.machineId) return;
      var key = ++this.wearLoadKey;
      this.wearPartsLoading = true;
      try {
        var data = await apiService.getAssemblyWithParts({
          id: assemblyId,
        });
        console.log("[Create] inline parts: getAssemblyWithParts response", { key, data });
        if (key !== this.wearLoadKey) return;
        // Normalize each sub-assembly to the shape the
        // accordion expects. The all-parts response uses
        // `text` for the display name; wear-parts uses
        // `assemblyName`. Map both to assemblyId/assemblyName.
        var rows = Array.isArray(data) ? data : [];
        var list = rows.map(function (item, idx) {
          return {
            assemblyId: item.assemblyId || item.id || item.value || ("subassembly-" + idx),
            assemblyName: item.assemblyName || item.text || item.name || "Sub-assembly " + (idx + 1),
            parts: (item.parts || []).map(function (p) {
              // Mirror ShopWearParts' default-quantity
              // behavior: pre-fill each row with
              // quantityPerMachine so users can hit Add
              // without having to fiddle with quantities.
              return Object.assign({}, p, {
                quantity: p.quantity || p.quantityPerMachine || 1,
              });
            }),
          };
        }).sort(function (a, b) {
          return (a.assemblyName || "").localeCompare(b.assemblyName || "");
        });
        // Header-search part surfacing — see wear-loader
        // copy above for full reasoning. Same operation:
        // mutate the local list before the wearAssemblies
        // assignment so the reorder rides on the single
        // reactive update.
        this._floatHeaderSearchPartToTop(list);
        this.wearAssemblies = list;
        // Auto-expand the first sub-assembly, same as the
        // wear-parts path. Template hint covers the >1 case.
        var initialExpanded = {};
        if (list.length > 0) {
          initialExpanded[list[0].assemblyId] = true;
        }
        if (this._pendingHeaderExpandAssemblyId != null) {
          initialExpanded[this._pendingHeaderExpandAssemblyId] = true;
          this._pendingHeaderExpandAssemblyId = null;
        }
        this.expandedWearAssemblies = initialExpanded;
        // Modify-flow hydration step 4 (all-parts variant) —
        // mirrors the wear-parts loader's sub-assembly pickup.
        if (this.pendingModifySubAssemblyId) {
          var subTarget = String(this.pendingModifySubAssemblyId);
          var subMatch = list.find(function (sa) {
            return String(sa.assemblyId) === subTarget;
          });
          if (subMatch) {
            this.selectedSubAssemblyId = subMatch.assemblyId;
          }
          this.pendingModifySubAssemblyId = null;
        }

        // Header-search sub-assembly hydration (all-parts
        // variant) — mirrors the wear loader's version.
        if (this.pendingHeaderSubAssemblyName
            && !this.selectedSubAssemblyId) {
          var hsTarget = String(this.pendingHeaderSubAssemblyName);
          var hsNorm = function (s) {
            return String(s == null ? "" : s)
              .trim().toLowerCase().replace(/\s+/g, " ");
          };
          var hsNormStrong = function (s) {
            return String(s == null ? "" : s)
              .toLowerCase().replace(/[^a-z0-9]/g, "");
          };
          var hsNT = hsNorm(hsTarget);
          var hsNTS = hsNormStrong(hsTarget);

          var hsMatch = list.find(function (sa) {
            return hsNorm(sa.assemblyName) === hsNT;
          }) || list.find(function (sa) {
            return hsNormStrong(sa.assemblyName) === hsNTS;
          }) || list.find(function (sa) {
            var n = hsNorm(sa.assemblyName);
            return n && hsNT && (
              n.indexOf(hsNT) >= 0 || hsNT.indexOf(n) >= 0
            );
          });

          if (hsMatch) {
            console.log(
              "[Create] header search (all-parts loader): " +
              "matched sub-assembly '" + hsMatch.assemblyName +
              "' (id " + hsMatch.assemblyId + ")"
            );
            this.selectedSubAssemblyId = hsMatch.assemblyId;
            this.$set(
              this.expandedWearAssemblies,
              hsMatch.assemblyId,
              true
            );
          } else {
            console.warn(
              "[Create] header search (all-parts loader): " +
              "no sub-assembly match for '" + hsTarget +
              "'. Available sub-assembly names:",
              list.map(function (sa) { return sa.assemblyName; })
            );
          }
          this.pendingHeaderSubAssemblyName = null;
        }
      } catch (e) {
        if (key !== this.wearLoadKey) return;
        console.warn("[Create] inline parts: load failed:", e);
        this.wearAssemblies = [];
      } finally {
        this.wearPartsLoading = false;
      }
    },

    // Expand/collapse one sub-assembly card.
    toggleInlineWearAssembly(assemblyId) {
      var current = this.expandedWearAssemblies[assemblyId];
      this.$set(this.expandedWearAssemblies, assemblyId, !current);
    },

    // Read expanded state — defaults to FALSE so a freshly
    // loaded panel starts with all sub-assemblies collapsed.
    // Users expand the ones they care about.
    isInlineWearAssemblyExpanded(assemblyId) {
      return this.expandedWearAssemblies[assemblyId] === true;
    },

    // ADD button for a wear part. Maps the wear-parts-API
    // part object to the shape addToOrderClick expects
    // (partId, partCode, description, ...) and hands off.
    // addToOrderClick takes care of:
    //   - the PO picker modal (add-mode or edit-mode)
    //   - pushing into selectedParts
    //   - debounced syncToCart + cart-bus emit (header
    //     counter refresh)
    // No new staging plumbing needed — this is the whole
    // point of routing through the existing gate.
    addInlineWearPart(part, subAssembly) {
      if (!part) return;
      var mapped = {
        partId: part.partId || part.id || part.Id || part.PartId,
        partCode: part.code || part.Code || part.partCode,
        description: part.description || part.Description,
        // The picker doesn't strictly need these but they
        // help downstream code that introspects the part
        // (e.g. row rendering on the cart page).
        price: part.price || part.Price,
        quantityPerMachine: part.quantityPerMachine || part.QuantityPerMachine || 1,
        // Tag the source so analytics / cart-page logic
        // can tell where the part was added from. Reflects
        // the active mode — wear when the toggle is on,
        // false when the user is browsing all parts.
        sourceAssemblyId: subAssembly && subAssembly.assemblyId,
        sourceAssemblyName: subAssembly && subAssembly.assemblyName,
        isWearPart: this.configuredWear === true,
      };
      // Bail with a friendly toast if the API gave us a
      // shape we can't normalize (shouldn't happen but
      // defensive against schema drift).
      if (!mapped.partId || !mapped.partCode) {
        console.warn("[Create] inline wear: unrecognized part shape", part);
        return;
      }
      this.addToOrderClick(mapped);
    },

    addToOrderClick(part) {
      if (!part || !part.partId || !part.partCode) return;

      this.pendingAction = "addPart";
      this.pendingPart = part;

      // ===== Already in the queue → EDIT mode =====
      // The part has one current Order. The picker now offers:
      // move to a different existing Order, move to a brand-new PO,
      // or remove from the queue. Duplicates are impossible from
      // this gate because we always pass through here.
      if (this.isPartSelected(part)) {
        this.pickerMode = "edit";
        // Even with no other orders to move to (e.g. the part is in
        // the only Order), the picker still shows New PO + Remove
        // options, which is useful — so always open the picker, not
        // the explainer, in edit mode.
        this.showPoPickerModal = true;
        return;
      }

      // ===== Not in the queue → ADD mode =====
      this.pickerMode = "add";

      // If the queue already has at least one Order, give the user
      // the choice: add to an existing Order or start a new PO.
      // Otherwise jump straight to the explainer — there's nothing
      // to pick from yet.
      if (this.groupedSelections.length > 0) {
        this.showPoPickerModal = true;
      } else {
        // Start with a clean input — the form's PO field is for
        // the manual flow only and shouldn't bleed into the
        // insights flow.
        this.poExplainerInput = "";
        this.showPoExplainerModal = true;
      }
    },
    // Used by Tray chips and the Clear-all action to remove queued
    // parts. Each chip × removes a single (part, PO) pairing — same
    // part code under a different PO stays put.
    removeQueuedPart(part) {
      if (!part) return;
      this.selectedParts = this.selectedParts.filter(function (p) {
        // Match on both partId and poNumber so the right chip is
        // pulled out when the same part exists in multiple Orders.
        return !(p.partId === part.partId && p.poNumber === part.poNumber);
      });
      // If the queue is mirroring an active cart, propagate the
      // removal back to the cart. No-op when there's no linked
      // cart (pre-Continue queues are local-only).
      this.scheduleSyncToCart();
    },
    clearSelectedParts() {
      this.selectedParts = [];
      this.clearSelectionsStorage();
      // If a cart is linked, sync (which will detect the empty
      // queue and delete the cart entirely). Otherwise this is a
      // no-op.
      this.scheduleSyncToCart();
    },

    // ===== LOCAL STORAGE PERSISTENCE FOR THE QUEUE =====
    // The queue lives in component state, which is wiped whenever
    // the user navigates away from /orders/create (cart →
    // breadcrumb → back, or any router push). Persisting it to
    // localStorage makes the staging tray survive navigation so
    // the user can pop over to the cart, come back, and continue
    // building their order.
    //
    // STORAGE SHAPE: per-company keys —
    //   jcsteele.insights.selections.<companyId>
    // This matches the cart's per-company behavior, so an admin
    // who shops as multiple companies has an independent queue
    // for each one. Switching contexts saves the outgoing queue
    // under its company's key and hydrates the incoming queue
    // from the new company's key.
    //
    // Cleared explicitly (per company) when:
    //   - the user clicks "Clear all" (clearSelectedParts)
    //   - saveCart succeeds for that company (parts now committed)
    _selectionsStorageKey(companyId) {
      var cid = companyId || this.companyId;
      if (!cid) return null;
      return "jcsteele.insights.selections." + cid;
    },
    saveSelectionsToStorage(forCompanyId) {
      try {
        // Allow the caller to specify which company's slot to write
        // to — the companyId watcher uses this to persist the
        // outgoing queue under the OLD company's key after
        // this.companyId has already moved on to the new one.
        var key = this._selectionsStorageKey(forCompanyId);
        if (!key) return;

        if (!this.selectedParts || this.selectedParts.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        var payload = {
          companyId: forCompanyId || this.companyId,
          // The cart this queue is currently linked to (if any).
          // Captured at save time so hydration on next mount can
          // detect whether the cart still exists. When the user
          // places the order or saves the cart, localStorage.cartId
          // changes (new cart) or is cleared, and that mismatch
          // is our signal to drop the queue rather than restore
          // it against a cart that's no longer relevant.
          //
          // null until the user clicks Continue with N parts the
          // first time — pre-Continue queues persist freely
          // because they aren't linked to any cart yet.
          cartId: localStorage.cartId
            ? String(localStorage.cartId)
            : null,
          // Persisted committed snapshot — restored on hydration
          // so the "Go to Cart" / "Continue with N parts" button
          // state survives navigation. Without persisting this,
          // clicking back into the page would reset the snapshot
          // to null and the button would always show "Continue".
          committedSnapshot: this.committedSnapshot,
          // Strip any internal-resolution fields
          // (_companyMachineId, _assemblyId, _resolveDiagnostics)
          // before persisting. They're recomputed via
          // getPartContext on each save attempt, and storing them
          // would just leak transient resolution state across
          // navigations.
          selectedParts: this.selectedParts.map(function (p) {
            return {
              partId: p.partId,
              partCode: p.partCode,
              description: p.description,
              poNumber: p.poNumber,
              quantity: p.quantity,
            };
          }),
          savedAt: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        // Quota errors or disabled storage shouldn't break the
        // page — worst case the queue won't persist, which is the
        // pre-feature behavior anyway.
        console.warn("[Insights] Could not persist selections", e);
      }
    },
    loadSelectionsFromStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (!key) return;

        var raw = localStorage.getItem(key);
        if (!raw) {
          // No saved queue for the active company — reset the
          // in-memory queue so a previous company's queue doesn't
          // bleed into this context.
          this.selectedParts = [];
          this.committedSnapshot = null;
          return;
        }
        var saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.selectedParts)) {
          this.selectedParts = [];
          this.committedSnapshot = null;
          return;
        }

        // Cart-linkage check: if this queue was tagged with a
        // cartId at save time, that means the user had clicked
        // Continue and committed the parts to a specific cart.
        // We only restore the queue if that same cart is still
        // the user's active cart — any of the following kill
        // the link and clear the queue:
        //
        //   - localStorage.cartId is missing      (cart cleared)
        //   - localStorage.cartId differs         (cart placed →
        //     new cart started, or cart saved →
        //     archived to saved-carts list)
        //
        // Pre-Continue queues (cartId === null) aren't linked to
        // any cart yet, so they bypass this check entirely and
        // restore as long as the company matches.
        if (saved.cartId) {
          var currentCartId = localStorage.cartId
            ? String(localStorage.cartId)
            : null;
          if (
            !currentCartId ||
            String(saved.cartId) !== currentCartId
          ) {
            // The cart this queue mirrored is no longer the
            // active cart — order was placed, cart was saved,
            // or some other action moved on from it. Drop the
            // staging tray so it doesn't reappear stale.
            localStorage.removeItem(key);
            this.selectedParts = [];
            this.committedSnapshot = null;
            return;
          }
        }

        this.selectedParts = saved.selectedParts;
        // Restore the committed snapshot so the Continue button
        // reflects the correct state immediately. Older payloads
        // that pre-date this field will leave it null, which
        // hasUnsavedChanges interprets as "everything is unsaved"
        // — that's the safe default since the next click will
        // re-save the queue and capture a fresh snapshot anyway.
        this.committedSnapshot = saved.committedSnapshot || null;
      } catch (e) {
        console.warn("[Insights] Could not restore selections", e);
      }
    },
    clearSelectionsStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (!key) return;
        localStorage.removeItem(key);
      } catch (e) {
        // No-op: same reasoning as saveSelectionsToStorage.
      }
    },

    // ===== CART LINKAGE VALIDATION =====
    // Runs after the queue hydrates from storage. The
    // committedSnapshot is a client-side belief about what's in
    // the cart, but it can drift from server-side reality —
    // notably after a company-switch round-trip (Head.vue's
    // per-company cart storage may restore a stale cartId), or
    // when other flows (Save Cart, Place Order, manual Shop
    // flow, team-cart pickup) modify the cart out-of-band.
    //
    // If the linked cart no longer exists, is empty, or fails
    // to load, we break the local linkage by clearing
    // committedSnapshot. The selectedParts queue stays — so the
    // user doesn't lose their staging work — but the button
    // flips back to "Continue with N parts" so the next click
    // creates a fresh cart rather than trying to update a
    // phantom one. This also clears the cartId tag in storage.
    //
    // Non-blocking: runs as a fire-and-forget async task. If
    // the cart fetch is slow, the user briefly sees "Go to
    // Cart" before it potentially flips to "Continue with N
    // parts" — that's acceptable, since the alternative is
    // blocking page render on a network call.
    // ===== SYNC FROM ACTIVE CART =====
    // Fetches the active order cart (localStorage.cartId) and
    // rebuilds selectedParts from its server-side contents.
    // Cart is the source of truth across pages — Search adds,
    // Create-Order adds, and SavedCarts Resume Cart all write
    // to the same cart row. Without this sync, a part added
    // on Search wouldn't appear here, the user would re-add it
    // under "the same PO 12345", and SaveOldCart would 400 with
    // "this part is already added".
    //
    // Filters out quote carts (Discriminator === Rfq) so an
    // active RFQ doesn't accidentally surface on the Create
    // Order page; quote staging is owned by /cart/rfqsaveditems.
    //
    // Non-blocking: runs as fire-and-forget. If the cart fetch
    // is slow, the user briefly sees the localStorage-cached
    // queue before it gets replaced — acceptable tradeoff
    // versus blocking page render on a network call.
    async syncFromActiveCart() {
      // The hydration race guard (this._hydrationComplete) MUST
      // flip to true on every exit path of this function — early
      // return for missing cartId, missing companyId, quote-cart
      // skip, empty PO list, network error, or success. The
      // try/finally below is what makes that bullet-proof
      // regardless of which return path triggers. Without that
      // guarantee, any early-out leaves the flag stuck at false
      // and a later legitimate "user cleared the staging tray"
      // action would incorrectly skip the cart-delete logic in
      // syncQueueToCart.
      try {
        if (!localStorage.cartId || String(localStorage.cartId) === "0") {
          return;
        }
        if (!this.companyId) return;

        var cart = await apiService.getCart({ id: localStorage.cartId });
        if (!cart) return;

        // Filter: skip quote carts. On the slim chance
        // localStorage.cartId points to a row that's actually
        // an Rfq (e.g. the discriminator was flipped manually
        // in SQL), we don't want to ingest it here.
        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) return;

        // Convert cart shape → selectedParts shape. The
        // serializer surfaces each PurchaseOrderPart with
        // p.id === underlying Part.Id (verified in the cart
        // response projection), and p.part carries the code
        // and description for UI rendering.
        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              partId: p.id,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        if (derived.length === 0) return;

        // Replace whatever was loaded from localStorage with
        // the cart-derived snapshot. Set committedSnapshot to
        // the same data so hasUnsavedChanges returns false —
        // everything in the queue at this point is already
        // saved server-side.
        this.selectedParts = derived;
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Persist the new state with the cart linkage tag so
        // a refresh hydrates correctly.
        this.saveSelectionsToStorage();
      } catch (e) {
        console.warn(
          "[CreateOrder] cart-derivation on mount failed:",
          e
        );
      } finally {
        // Hydration is now considered complete. The empty-queue
        // delete branch in syncQueueToCart is unblocked, so any
        // SUBSEQUENT user action that empties the staging tray
        // (Clear all, removing the last chip) will correctly
        // delete the cart. See _hydrationComplete init in
        // mounted() for the full rationale.
        this._hydrationComplete = true;
      }
    },

    async validateLinkedCart() {
      // Nothing to validate if we never committed anything.
      if (!this.committedSnapshot) return;

      // No active cart in localStorage means the linkage is
      // already broken — clear the snapshot so the button
      // matches reality.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        console.log(
          "[Insights] No active cartId — breaking queue linkage"
        );
        this.committedSnapshot = null;
        this.saveSelectionsToStorage();
        return;
      }

      // Cart exists in localStorage; verify it on the backend.
      // Empty purchaseOrders means the cart was gutted by another
      // flow (cleared, items removed, etc.) — break linkage.
      try {
        var cart = await apiService.getCart({
          id: localStorage.cartId,
        });
        if (
          !cart ||
          !cart.purchaseOrders ||
          cart.purchaseOrders.length === 0
        ) {
          console.log(
            "[Insights] Linked cart " +
              localStorage.cartId +
              " is empty or missing — breaking linkage"
          );
          this.committedSnapshot = null;
          this.saveSelectionsToStorage();
        }
        // If cart has parts, leave linkage alone. Even if the
        // contents have drifted from committedSnapshot, the
        // hasUnsavedChanges check will catch the difference and
        // show "Continue with N parts" — which is correct.
      } catch (e) {
        console.warn(
          "[Insights] Linked cart fetch failed — breaking linkage",
          e
        );
        this.committedSnapshot = null;
        this.saveSelectionsToStorage();
      }
    },

    // ===== STAGING TRAY ↔ CART SYNC =====
    // Once the user clicks Continue with N parts, the staging
    // tray and the cart are mirrors of each other. After that
    // initial commit, edits to the staging tray (removing a
    // queued chip, clicking Clear all) need to flow back to the
    // cart so the two stay in sync — otherwise the cart stays
    // stale until the user clicks Continue again.
    //
    // Triggered from removeQueuedPart and clearSelectedParts.
    // Debounced through scheduleSyncToCart so rapid changes
    // (e.g. removing two chips in quick succession) coalesce
    // into a single round-trip rather than firing one per
    // keystroke. Pre-Continue queues (no localStorage.cartId
    // tag) bypass sync entirely — there's no cart to mirror
    // until Continue is clicked the first time.
    scheduleSyncToCart() {
      // Bail if there's no cart to sync TO. Pre-Continue queues
      // are local-only and don't need a backend round-trip when
      // the user edits them.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
      }
      var self = this;
      this._syncTimer = setTimeout(function () {
        self._syncTimer = null;
        self.syncQueueToCart();
      }, 300);
    },
    async syncQueueToCart() {
      // Guard against concurrent syncs. If a save is already in
      // flight when another change comes in, mark the queue as
      // dirty so we re-fire after the current save resolves.
      if (this.addingInsightPart) {
        this._syncDirty = true;
        return;
      }
      // Re-check the cart link — between scheduling and firing,
      // the user might have placed the order or otherwise
      // cleared the cart, in which case there's nothing to sync.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // ===== HYDRATION RACE GUARD =====
        // An empty selectedParts during page hydration does NOT
        // mean the user cleared their queue — it means
        // loadSelectionsFromStorage ran synchronously, found no
        // saved queue for this (company, cart) pair, set
        // selectedParts to [], and the deep watcher fired
        // scheduleSyncToCart before syncFromActiveCart's
        // apiService.getCart had a chance to come back and
        // repopulate the queue.
        //
        // Falling through to the delete branch in that window
        // would soft-delete the cart the user came from /cart →
        // Modify Order → Add More Parts to keep building, and
        // dump it into "Recently Deleted Carts" on the saved-
        // carts page.
        //
        // Skip the delete this round; once syncFromActiveCart
        // finishes (the finally there flips the flag) it will
        // overwrite selectedParts with the cart's actual parts,
        // the watcher fires again, and a fresh sync runs against
        // the populated queue and short-circuits as a no-op
        // (snapshot match). If syncFromActiveCart returns early
        // (no cartId, wrong companyId, etc.) the flag still
        // flips and any LATER user action that empties the queue
        // — Clear all, removing the last chip — flows through
        // the delete branch normally.
        if (
          !this._hydrationComplete &&
          (!this.selectedParts || this.selectedParts.length === 0)
        ) {
          return;
        }

        // Empty queue → the user removed everything that was in
        // the cart. Delete the cart entirely; this is the same
        // outcome as if the user had clicked Clear Cart on the
        // /cart page.
        if (!this.selectedParts || this.selectedParts.length === 0) {
          var cartIdToDelete = parseInt(localStorage.cartId, 10);
          try {
            await apiService.deleteCart({ id: cartIdToDelete });
          } catch (e) {
            // If the cart was already gone (user cleared it
            // server-side via another tab), the sync is still
            // effectively successful — local state catches up.
            console.warn(
              "[Insights → sync] deleteCart returned error " +
                "(possibly already deleted):",
              e
            );
          }
          localStorage.cartId = 0;
          this.clearSelectionsStorage();
          this.committedSnapshot = null;
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Non-empty queue → re-resolve every part and send a
        // fresh saveCart payload. Same machinery as
        // proceedWithSelected, but without the navigation step
        // at the end — the user stays on the create page.
        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          // If any part fails to resolve, skip the sync rather
          // than send bad IDs that would NRE the backend. Log
          // for diagnostics; the staging tray still reflects
          // the user's intent locally and on next mount or next
          // Continue click we'll try again.
          console.warn(
            "[Insights → sync] skipped — some parts failed to resolve:",
            missing.map(function (m) { return m.partCode; }).join(", ")
          );
          return;
        }

        // ===== ADD vs REMOVE vs QTY-CHANGE DELTA =====
        // The backend's SaveOldCart is fundamentally add-only: it
        // either adds a part to an existing PO or creates a new PO,
        // but it can't delete or update an existing row's quantity.
        // DoesPartExistInTheCart 400s any part already on the
        // server, so we can't just send the full queue. We compute
        // additions, removals, and quantity changes separately.
        //
        //   ADDS only           → send delta to SaveOldCart (cheap)
        //   REMOVALS / QTY edit → delete cart + recreate via
        //                         SaveNewCart (the only way to drop
        //                         a part or change its quantity in
        //                         one round-trip)
        //   NEITHER             → no-op, just refresh the badge
        //
        // Match key for adds/removals is partId + poNumber.
        // Quantity is checked separately against the snapshot so a
        // qty-only edit in the staging tray triggers a recreate
        // instead of falling through as a silent no-op.
        var snapshotKeys = {};
        var snapshotQtys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          var k = s.partId + "|" + (s.poNumber || "");
          snapshotKeys[k] = true;
          snapshotQtys[k] = Number(s.quantity);
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = (this.committedSnapshot || []).some(function (s) {
          var key = s.partId + "|" + (s.poNumber || "");
          return !currentKeys[key];
        });
        // Detect qty-only changes — same partId+poNumber pair on
        // both sides but a different quantity. Without this
        // check, qty edits in the staging tray never reach the
        // server (the prior delta logic ignored quantity).
        var hasQtyChange = resolved.some(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          if (!(key in snapshotQtys)) return false;
          return Number(p.quantity) !== snapshotQtys[key];
        });

        // No additions, no removals, no qty changes — true no-op.
        // Refresh local snapshot, ping the badge, and bail.
        if (addedParts.length === 0 && !hasRemovals && !hasQtyChange) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        // Removals OR qty changes both need the full resolved
        // queue (cart gets recreated from scratch). Add-only
        // cases use the addedParts subset (incremental
        // SaveOldCart add).
        var needsRecreate = hasRemovals || hasQtyChange;
        var partsForPayload = needsRecreate ? resolved : addedParts;

        var poGroups = {};
        var poOrder = [];
        partsForPayload.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        // ===== If removals or qty changes detected, delete + recreate =====
        // SaveOldCart can't drop parts or update existing
        // quantities. Delete the existing cart first, then save
        // with id=0 so the request hits SaveNewCart (which has
        // no duplicate check). The user's localStorage.cartId
        // rotates to the new cart's id — a small cost in
        // exchange for keeping queue and cart in sync.
        if (needsRecreate) {
          var oldCartId = parseInt(localStorage.cartId, 10) || 0;
          if (oldCartId > 0) {
            try {
              await apiService.deleteCart({ id: oldCartId });
            } catch (e) {
              // Soft-fail: a delete error doesn't block the
              // recreate (worst case the old cart lingers as
              // a duplicate in /account/savedcarts; the new
              // cart still gets created and is the active one).
              console.warn(
                "[Insights → sync] deleteCart before recreate failed:",
                e
              );
            }
          }
        }

        // saveCartId: 0 = recreate via SaveNewCart (removals or
        // qty changes), or the existing cart id for incremental
        // add via SaveOldCart.
        var saveCartId = needsRecreate
          ? 0
          : parseInt(localStorage.cartId, 10) || 0;
        var savePayload = {
          id: saveCartId,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        // Update the committed snapshot to match what's now
        // in the cart. Without this, the button would still
        // say "Continue with N parts" because the snapshot
        // would be stale from before the sync.
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Re-persist with the (possibly updated) cartId tag.
        this.saveSelectionsToStorage();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        console.warn("[Insights → sync] saveCart failed:", err);
        // Show a non-blocking warning. The user's changes are
        // still reflected in the staging tray and saved to
        // localStorage — only the cart didn't update. They can
        // refresh the page to retry, or click Continue again
        // which will go through proceedWithSelected's surfacing.
        this.$bvToast.toast(
          "Couldn't sync the change to your cart. The staging tray " +
            "is up to date — refresh the page or click Continue to retry.",
          {
            title: "Cart sync failed",
            autoHideDelay: 6000,
            variant: "warning",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
        // If a change came in while the previous sync was still
        // running, fire one more pass now so we don't end up
        // with the cart lagging behind the staging tray.
        if (this._syncDirty) {
          this._syncDirty = false;
          this.scheduleSyncToCart();
        }
      }
    },

    // ===== PO PICKER MODAL =====
    // Shown when the queue already has Orders. Each existing PO is
    // an actionable button; "Use a New PO Number" chains over to
    // the explainer modal.
    onPoPickerChoose(poNumber) {
      this.showPoPickerModal = false;
      var pending = this.pendingPart;
      var wasAdd = this.pendingAction === "addPart";

      if (this.pickerMode === "edit" && pending) {
        // Move the existing queue entry to the chosen PO. Use $set
        // so Vue 2 reactivity picks up the property change without
        // a forceUpdate.
        var idx = this.selectedParts.findIndex(function (p) {
          return p.partId === pending.partId;
        });
        if (idx >= 0) {
          this.$set(this.selectedParts, idx, Object.assign(
            {},
            this.selectedParts[idx],
            { poNumber: poNumber }
          ));
        }
        this.resetModalState();
        // Sync the move to the cart so the part lands in the
        // right PO group server-side too. Debounced.
        this.scheduleSyncToCart();
        return;
      } else if (wasAdd && pending) {
        // Brand-new add — duplicates are impossible because the
        // gate (addToOrderClick) routes already-queued parts into
        // edit mode instead of here.
        this.selectedParts.push(
          Object.assign({}, pending, {
            poNumber: poNumber,
            quantity: this.defaultQuantityFor(pending),
          })
        );
      }
      this.resetModalState();

      // Auto-commit on add: same behavior as onPoModalConfirm —
      // first part creates the cart via proceedWithSelected,
      // subsequent adds sync via the debounced helper.
      if (wasAdd) {
        if (
          !localStorage.cartId ||
          String(localStorage.cartId) === "0"
        ) {
          this.proceedWithSelectedInPlace();
        } else {
          this.scheduleSyncToCart();
        }
      }
    },
    onPoPickerNewPo() {
      // Hand off to the explainer modal — keep pendingAction +
      // pendingPart + pickerMode intact so the explainer's save
      // handler picks up the same context.
      this.showPoPickerModal = false;
      this.poExplainerInput = "";
      this.showPoExplainerModal = true;
    },
    // Edit-mode only — pull the part out of the queue entirely.
    onPoPickerRemove() {
      if (this.pendingPart) {
        var pendingId = this.pendingPart.partId;
        this.selectedParts = this.selectedParts.filter(function (p) {
          return p.partId !== pendingId;
        });
      }
      this.showPoPickerModal = false;
      this.resetModalState();
      // Sync the removal back to the cart. Otherwise the part
      // stays in the cart even though the staging tray drops
      // it. Same debounced sync used by removeQueuedPart and
      // clearSelectedParts.
      this.scheduleSyncToCart();
    },
    onPoPickerCancel() {
      this.showPoPickerModal = false;
      this.resetModalState();
    },

    // ===== PO EXPLAINER MODAL =====
    // Hosts both the explanation of the PO grouping rule and an
    // inline PO Number input. Saving validates a non-empty value,
    // mirrors it into this.poNumber (which v-models the form's PO
    // field on the left), and resumes whichever action queued up.
    onPoModalConfirm(bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault();
      }
      var trimmed = (this.poExplainerInput || "").trim();
      if (!trimmed) {
        // Don't close — focus the input and let them try again.
        this.$nextTick(() => {
          var el = document.getElementById("poExplainerInput");
          if (el) el.focus();
        });
        return;
      }

      // Don't mirror to the form field on the left — the manual
      // flow ("Proceed with Order") and the insights flow are fully
      // independent now. Each queued part keeps its own PO; the
      // form's PO field is reserved for whatever the user typed
      // there themselves for the manual machine-browse flow.
      this.showPoExplainerModal = false;

      var pending = this.pendingPart;

      // ===== EDIT mode: move existing entry to the new PO =====
      if (this.pickerMode === "edit" && pending) {
        var idx = this.selectedParts.findIndex(function (p) {
          return p.partId === pending.partId;
        });
        if (idx >= 0) {
          this.$set(this.selectedParts, idx, Object.assign(
            {},
            this.selectedParts[idx],
            { poNumber: trimmed }
          ));
        }
        this.resetModalState();
        // Sync the PO change back to the cart so the move is
        // reflected server-side. Debounced — if the user is
        // editing several PO assignments in quick succession,
        // they collapse into a single round-trip.
        this.scheduleSyncToCart();
        return;
      }

      // ===== ADD mode: push a new entry =====
      if (this.pendingAction === "addPart" && pending) {
        this.selectedParts.push(
          Object.assign({}, pending, {
            poNumber: trimmed,
            quantity: this.defaultQuantityFor(pending),
          })
        );
        // Auto-commit: the manual "Continue with N parts"
        // button is gone — every successful Add now flows
        // straight to the cart. If this is the first part
        // (no cart yet), fire proceedWithSelected to create
        // the cart and capture the snapshot. If a cart is
        // already linked, debounced sync handles the update.
        if (
          !localStorage.cartId ||
          String(localStorage.cartId) === "0"
        ) {
          this.proceedWithSelectedInPlace();
        } else {
          this.scheduleSyncToCart();
        }
      } else if (this.pendingAction === "continue") {
        this.proceedWithSelected();
      }

      this.resetModalState();
    },
    onPoModalCancel() {
      this.showPoExplainerModal = false;
      this.resetModalState();
    },
    // Centralized cleanup so a stale mode/pendingPart can't leak
    // from one interaction into the next.
    resetModalState() {
      this.pickerMode = "add";
      this.pendingAction = null;
      this.pendingPart = null;
    },
    // Autofocus the PO input when the modal becomes visible — saves
    // the user a click before they can start typing.
    onPoModalShown() {
      var el = document.getElementById("poExplainerInput");
      if (el && el.focus) el.focus();
    },

    // ===== COMMIT THE QUEUE =====
    // "Continue with N parts" — each part already has its own PO
    // attached so no further prompting is needed. Just hand off
    // to the navigation logic.
    continueWithSelected() {
      if (this.selectedParts.length === 0) return;
      this.proceedWithSelected();
    },
    // Build a snapshot of the current queue suitable for storing
    // as committedSnapshot. Strips everything except the fields
    // that determine whether a part is "the same" between two
    // moments in time: partId + poNumber identify it, quantity is
    // tracked because qty changes count as unsaved edits.
    buildCommittedSnapshot() {
      return (this.selectedParts || []).map(function (p) {
        return {
          partId: p.partId,
          poNumber: p.poNumber,
          quantity: p.quantity,
        };
      });
    },
    // Single click handler for the bottom commit button. If
    // there are unsaved changes, fire the save flow. Otherwise
    // just navigate — no point in re-saving an unchanged cart.
    // Cancels any debounced sync since the user's explicit
    // action supersedes it.
    handleContinueButtonClick() {
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
        this._syncTimer = null;
      }
      if (this.hasUnsavedChanges) {
        this.continueWithSelected();
      } else {
        this.$router.push({ name: "Cart" });
      }
    },
    // Commit the queue: resolve each queued part's machine/assembly
    // context, build a purchaseOrders payload grouped by PO matching
    // the backend's CartModel/PurchaseOrderModel shape (flat fields,
    // not nested objects), and hand it to apiService.saveCart.
    //
    // Backend payload contract (verified against CartController.Save
    // → SaveNewCart → groupedPo):
    //
    //   {
    //     id: 0,
    //     companyId: <int>,
    //     specialInstructions: "",
    //     CreateQuote: false,
    //     purchaseOrders: [
    //       {
    //         number: "<PO>",
    //         assemblyId: <int>,
    //         machineId:  <int>,   // ← CompanyMachine ID (NOT raw Machine ID)
    //         configuredWear: true,
    //         parts: [
    //           { id: <partId>, quantity: <int>, quantityPerMachine: 0 }
    //         ]
    //       }
    //     ]
    //   }
    //
    // The frontend ID resolution path tries, in order:
    //   1. apiService.getPartContext({partId, companyId}) — preferred,
    //      returns { assemblyId, companyMachineId } directly. Needs
    //      a small backend endpoint (see the response message that
    //      shipped with this commit for the C# code).
    //   2. apiService.resolveTopPartContext + URL param parsing —
    //      best-effort; only works if the redirectUrl carries the
    //      IDs as query params (it currently goes to /search/index
    //      so it likely doesn't).
    // If neither yields valid IDs, we surface a precise error listing
    // which parts failed and bail before calling saveCart.
    // Variant of proceedWithSelected that performs the save
    // but stays on the current page. Used by the auto-add
    // flow where every Add click commits to the cart but
    // doesn't yank the user away from the create page —
    // they should keep browsing the Frequently Ordered list
    // and add more parts. The Saved Quotes / View Cart
    // header badges update via the cart-updated event so
    // the user has the visual confirmation without leaving
    // the page.
    async proceedWithSelectedInPlace() {
      this._suppressCartNav = true;
      try {
        await this.proceedWithSelected();
      } finally {
        this._suppressCartNav = false;
      }
    },
    async proceedWithSelected() {
      if (this.addingInsightPart || this.selectedParts.length === 0) return;
      if (!this.companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // ===== Step 1: Resolve every queued part's context =====
        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        // Pre-flight: every part must have a CompanyMachine ID and
        // an Assembly ID. Without both, the backend NREs in
        // _purchaseOrderManager.CreatePurchaseOrder when it does the
        // CompanyMachine lookup.
        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          var codes = missing.map(function (p) { return p.partCode; }).join(", ");
          this.$bvToast.toast(
            "Could not resolve the machine and assembly context for: " +
              codes +
              ". The backend needs a getPartContext endpoint — see the " +
              "console log for the payload we tried to send.",
            {
              title: "Cart update blocked",
              autoHideDelay: 12000,
              variant: "warning",
              solid: true,
            }
          );
          console.warn(
            "[Insights → saveCart] could not resolve context for parts:",
            missing
          );
          return;
        }

        // ===== ADD vs REMOVE DELTA =====
        // Same logic as syncQueueToCart. SaveOldCart can't drop
        // parts, so when the queue has shrunk relative to
        // committedSnapshot we delete+recreate via SaveNewCart
        // (id=0). When only adds, send the delta to SaveOldCart.
        // When neither, navigate without saving.
        var existingCartId = parseInt(localStorage.cartId, 10) || 0;
        var snapshotKeys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          snapshotKeys[s.partId + "|" + (s.poNumber || "")] = true;
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = existingCartId > 0
          && (this.committedSnapshot || []).some(function (s) {
              var key = s.partId + "|" + (s.poNumber || "");
              return !currentKeys[key];
            });

        // Nothing new and nothing removed — navigate as if the
        // save succeeded. Avoids a wasted (and failing) round-trip
        // on a no-op commit.
        if (addedParts.length === 0 && !hasRemovals) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          if (!this._suppressCartNav) {
            this.$router.push({ name: "Cart" });
          }
          return;
        }

        // For removals, recreate uses the FULL queue. For adds-only
        // on an existing cart, send only the new parts. For a fresh
        // cart (existingCartId === 0), send the full queue —
        // SaveNewCart has no duplicate check.
        var partsForSave;
        if (hasRemovals || existingCartId === 0) {
          partsForSave = resolved;
        } else {
          partsForSave = addedParts;
        }

        // ===== Step 2: Group resolved parts by PO =====
        var poGroups = {};
        var poOrder = [];
        partsForSave.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        // ===== Step 3: Build purchaseOrders payload (FLAT shape) =====
        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        // ===== Step 3.5: Delete existing cart if removals detected =====
        // SaveOldCart can't drop parts. To remove parts the user took
        // out of the queue, we delete the existing cart first then
        // recreate from scratch via SaveNewCart (id=0).
        if (hasRemovals && existingCartId > 0) {
          try {
            await apiService.deleteCart({ id: existingCartId });
          } catch (e) {
            console.warn(
              "[Insights → saveCart] deleteCart before recreate failed:",
              e
            );
          }
        }

        // ===== Step 4: Save cart =====
        // id=0 forces SaveNewCart on the recreate path (after a
        // removal-triggered delete) or on a fresh queue. For
        // incremental adds against an existing cart, we keep its
        // id so SaveOldCart adds the delta.
        var saveCartIdForPayload = hasRemovals ? 0 : existingCartId;
        var savePayload = {
          id: saveCartIdForPayload,
          purchaseOrders: purchaseOrders,
          companyId: Number(this.companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        console.log("[Insights → saveCart] sending payload:", savePayload);
        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        // Capture the current queue as the committed snapshot.
        // This is what the Continue button's "Go to Cart" vs
        // "Continue with N parts" decision compares against —
        // the moment any part is added/removed/edited, the
        // button reverts to "Continue with N parts".
        this.committedSnapshot = this.buildCommittedSnapshot();

        // Re-persist the queue WITH the linked cart's id so
        // hydration on next mount can detect cart placement (id
        // gone or changed → user placed/saved/abandoned the cart
        // → clear queue) vs. ongoing work (id still matches →
        // restore queue so the staging tray mirrors the cart).
        // We deliberately do NOT clear selectedParts here — the
        // user expects the queue to stay visible alongside the
        // cart until the order is placed or the cart is saved.
        this.saveSelectionsToStorage();

        // Notify the header so the View Cart badge re-fetches
        // its count immediately. Without this, the count is
        // stale until the next page navigation triggers a
        // re-mount of Head.vue. The event bus is set up in
        // Head.vue's mounted() hook on Vue.prototype.$cartBus.
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }

        // Successful add. Navigate to /cart unless the caller
        // set _suppressCartNav (the auto-add flow keeps the user
        // on the create page so they can keep adding parts).
        if (!this._suppressCartNav) {
          this.$router.push({ name: "Cart" });
        }
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            console.error("[Insights → saveCart] backend response:", resp.data);
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            console.error("[Insights → saveCart] error:", err);
            details = (err && err.message) || String(err);
          }
        } catch (e) {
          console.error("[Insights → saveCart] error (unparseable):", err);
        }

        this.$bvToast.toast(
          "Could not add the selected parts to your cart. " +
            (details
              ? "Server said: " + details
              : "Check the browser console for details."),
          {
            title: "Cart update failed",
            autoHideDelay: 9000,
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
      }
    },
    // Tries multiple paths to look up a part's machine/assembly
    // context. Returns the part with _companyMachineId and
    // _assemblyId set, or nulls if everything failed. Logs each
    // step so the user can pinpoint where the chain breaks.
    async resolvePartContext(p) {
      var enriched = Object.assign({}, p, {
        _companyMachineId: null,
        _assemblyId: null,
        _resolveDiagnostics: [],
      });

      // Path 1 (preferred): apiService.getPartContext
      var hasGetPartContext = typeof apiService.getPartContext === "function";
      if (!hasGetPartContext) {
        enriched._resolveDiagnostics.push(
          "apiService.getPartContext is not defined — add the wrapper to FeApiService.js"
        );
      } else {
        try {
          var ctx = await apiService.getPartContext({
            partId: p.partId,
            companyId: this.companyId,
          });
          if (ctx) {
            enriched._companyMachineId =
              ctx.companyMachineId || ctx.CompanyMachineId || null;
            enriched._assemblyId = ctx.assemblyId || ctx.AssemblyId || null;
            if (enriched._companyMachineId && enriched._assemblyId) {
              enriched._resolveDiagnostics.push(
                "getPartContext OK: companyMachineId=" +
                  enriched._companyMachineId +
                  ", assemblyId=" +
                  enriched._assemblyId
              );
              return enriched;
            }
            enriched._resolveDiagnostics.push(
              "getPartContext returned but missing IDs: " +
                JSON.stringify(ctx)
            );
          } else {
            enriched._resolveDiagnostics.push(
              "getPartContext returned empty/null"
            );
          }
        } catch (e) {
          var status = e && e.error && e.error.response && e.error.response.status;
          enriched._resolveDiagnostics.push(
            "getPartContext threw" +
              (status ? " (HTTP " + status + ")" : "") +
              " — likely the PartController.GetPartContext endpoint isn't deployed yet"
          );
          console.warn(
            "[Insights] getPartContext failed for " + p.partCode,
            e
          );
        }
      }

      // Path 2 (fallback): URL parsing on resolveTopPartContext.
      try {
        var result = await apiService.resolveTopPartContext({
          partId: p.partId,
          partCode: p.partCode,
          companyId: this.companyId,
        });
        if (result && result.redirectUrl) {
          enriched._resolveDiagnostics.push(
            "resolveTopPartContext URL: " + result.redirectUrl
          );
          enriched._companyMachineId = this.parseUrlId(result.redirectUrl, [
            "companyMachineId",
            "machineId",
            "machineid",
          ]);
          enriched._assemblyId = this.parseUrlId(result.redirectUrl, [
            "assemblyId",
            "assemblyid",
          ]);
          if (!enriched._companyMachineId || !enriched._assemblyId) {
            enriched._resolveDiagnostics.push(
              "URL parsing produced: companyMachineId=" +
                enriched._companyMachineId +
                ", assemblyId=" +
                enriched._assemblyId +
                " — URL doesn't carry the IDs we need"
            );
          }
        }
      } catch (e) {
        enriched._resolveDiagnostics.push(
          "resolveTopPartContext threw — see console"
        );
        console.warn(
          "[Insights] resolveTopPartContext failed for " + p.partCode,
          e
        );
      }

      console.log(
        "[Insights] resolved " + p.partCode + ":",
        enriched._resolveDiagnostics
      );
      return enriched;
    },
    // Helper: pull a numeric ID out of a redirectUrl by trying each
    // candidate query-param name in order. Returns null if none of
    // them match. Defensive about malformed URLs.
    parseUrlId(url, paramNames) {
      if (!url || !paramNames || !paramNames.length) return null;
      try {
        var parsed = new URL(url, window.location.origin);
        for (var i = 0; i < paramNames.length; i++) {
          var v = parsed.searchParams.get(paramNames[i]);
          if (v) return v;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    // ===== MACHINE IMAGE =====
    // Pulls the machine's imagePath via getCompanyMachineDetails
    // and prefixes it with the Steele blob storage URL.
    // Mirrors the canonical pattern in ShopWearParts.vue. Sets
    // machineImage to null on missing path or fetch failure so
    // the template falls back to the placeholder.
    loadMachineImage() {
      if (!this.machineId) {
        this.machineImage = null;
        this.machineImageLoading = false;
        return;
      }
      var self = this;
      this.machineImageLoading = true;
      apiService
        .getCompanyMachineDetails({ id: this.machineId })
        .then(function (request) {
          console.log("[Create] machine image: getCompanyMachineDetails response", request);
          if (request && request.imagePath) {
            self.machineImage = self.machineImageUrl + request.imagePath;
          } else {
            self.machineImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] machine image: load failed", e);
          self.machineImage = null;
        })
        .finally(function () {
          self.machineImageLoading = false;
        });
    },

    // Pulls the assembly's imagePath via getAssemblyDetails
    // (the same call ShopWearParts.vue uses for its assembly
    // image). Different endpoint, same blob URL prefix —
    // assemblies and machines share the steeleimages
    // container. Sets assemblyImage to null on missing path
    // or fetch failure so the template hides the image area
    // entirely (no placeholder).
    loadAssemblyImage(assemblyId) {
      if (!assemblyId) {
        this.assemblyImage = null;
        this.assemblyImageLoading = false;
        return;
      }
      var self = this;
      this.assemblyImageLoading = true;
      apiService
        .getAssemblyDetails({ id: assemblyId })
        .then(function (request) {
          console.log("[Create] assembly image: getAssemblyDetails response", request);
          if (request && request.imagePath) {
            self.assemblyImage = self.machineImageUrl + request.imagePath;
          } else {
            self.assemblyImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] assembly image: load failed", e);
          self.assemblyImage = null;
        })
        .finally(function () {
          self.assemblyImageLoading = false;
        });
    },

    // Sub-assembly image loader — same endpoint as the
    // assembly image (the assembly table is unified;
    // sub-assemblies are rows in dbo.Assembly with a parent
    // FK), just stored in a separate state field so both
    // images can render side by side without one clobbering
    // the other.
    loadSubAssemblyImage(subAssemblyId) {
      if (!subAssemblyId) {
        this.subAssemblyImage = null;
        this.subAssemblyImageLoading = false;
        return;
      }
      var self = this;
      this.subAssemblyImageLoading = true;
      apiService
        .getAssemblyDetails({ id: subAssemblyId })
        .then(function (request) {
          console.log("[Create] sub-assembly image: getAssemblyDetails response", request);
          if (request && request.imagePath) {
            self.subAssemblyImage = self.machineImageUrl + request.imagePath;
          } else {
            self.subAssemblyImage = null;
          }
        })
        .catch(function (e) {
          console.warn("[Create] sub-assembly image: load failed", e);
          self.subAssemblyImage = null;
        })
        .finally(function () {
          self.subAssemblyImageLoading = false;
        });
    },

    // ===== IMAGE ZOOM MODAL =====
    // Same modal + zoom controls as ShopWearParts.vue so users
    // see consistent affordances on both pages.
    // ===== inPlaceZoomMove =====
    // Update the transform-origin for `key`'s image based on cursor
    // position relative to the image element. Maps cursor position
    // to percentage values (0%–100% across width and height) so the
    // CSS `transform: scale(...)` zooms toward the cursor, giving
    // a "magnifying glass" feel — moving the cursor pans the
    // visible area without requiring drag or extra clicks.
    //
    // Bail-out cases:
    //   - rect.width / rect.height could be 0 if the image has not
    //     fully laid out yet (e.g., during the brief loading flicker
    //     or in a hidden tab). Skip the update in that case so we
    //     don't divide by zero.
    inPlaceZoomMove(key, e) {
      // Locked: hold the captured origin from the click; ignore
      // any further cursor motion. This is what lets the user
      // mouse off the image (to scroll the parts list, click
      // around the form, whatever) without the zoomed view
      // shifting away from the spot they wanted to study.
      if (this.inPlaceZoomLocked[key]) return;
      var rect = e.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      this.inPlaceZoomOrigin[key] = x + "% " + y + "%";
    },

    // ===== onImageClick =====
    // Distinguishes single click (toggle lock) from double click
    // (open the existing popup modal) using the standard timer-
    // based pattern: the first click starts a 300ms timer; if a
    // second click arrives before it fires, we treat the pair as
    // a double-click — cancel the pending lock toggle and open
    // the modal. If the timer expires, the click was a true
    // single click and we toggle the lock.
    //
    // Snapshotting transform-origin at click time means that even
    // if the user's cursor moves during the 300ms detection
    // window, the lock anchors at the click position (the spot
    // the user actually wanted), not at wherever the cursor
    // happened to land when the timer fired. We only restore the
    // snapshot when transitioning into the locked state — when
    // unlocking, we leave the origin as-is so hover-zoom can
    // pick up smoothly.
    //
    // _imageClickTimer is an instance field rather than a data
    // property — it doesn't need reactivity, just identity for
    // clearing.
    onImageClick(key, src) {
      // When locked, ALL gesture handling lives in
      // onLockedMouseDown's click-count machine — so this
      // method exits immediately. Click-to-lock (single click
      // while UNLOCKED) and click-to-modal (double-click while
      // unlocked) still flow through here.
      if (this.inPlaceZoomLocked[key]) return;
      var self = this;
      if (this._imageClickTimer) {
        // Second click within 300ms — open the modal
        clearTimeout(this._imageClickTimer);
        this._imageClickTimer = null;
        self.openImageModal(src);
        return;
      }
      var snapshot = this.inPlaceZoomOrigin[key];
      this._imageClickTimer = setTimeout(function () {
        self._imageClickTimer = null;
        var willLock = !self.inPlaceZoomLocked[key];
        if (willLock) {
          self.inPlaceZoomOrigin[key] = snapshot;
          // Position the locked panel over .insights-panel
          // (the Frequently Ordered container on the right
          // column). Capture top, left, and width from the
          // panel's bounding rect at lock-activation time so
          // the fixed-positioned wrap adopts the right
          // column's exact geometry — sits where Frequently
          // Ordered sits, same width.
          //
          // Min-clamp on top to 10px guards against locking
          // while scrolled past the panel (negative rect.top
          // would pin the image above viewport).
          // Pin the locked image into the RIGHT column, at the cart's
          // position. The old anchor (.insights-panel / Frequently Ordered)
          // was removed from this page, which is why the lock used to
          // collapse to zero width and "disappear" — target the floating
          // cart stack instead, falling back to the right viewport edge if
          // it isn't mounted (e.g. cart empty / collapsed).
          var anchorEl = document.querySelector('.floating-widget-stack');
          if (anchorEl && anchorEl.getBoundingClientRect().width > 0) {
            var rect = anchorEl.getBoundingClientRect();
            self.lockedStickyTop = Math.max(rect.top, 10);
            self.lockedStickyLeft = rect.left;
            self.lockedStickyWidth = rect.width;
          } else {
            self.lockedStickyWidth = 360;
            self.lockedStickyLeft = Math.max(window.innerWidth - 360 - 24, 10);
            self.lockedStickyTop = 130;
          }
        }
        self.inPlaceZoomLocked[key] = willLock;
        // Drop the floating cart/Top-5 stack below the now-locked image.
        self._emitLockedImage();
      }, 300);
    },

    // ===== onLockedMouseDown =====
    // Click-count gesture machine for the locked image panel.
    // Runs only when locked (returns early otherwise). Each
    // mousedown increments _lockClickCount; the count drives
    // which gesture mode is active for the ensuing drag:
    //
    //   1 click + drag  =  pan zoom inside the box (mode 1).
    //                     Updates inPlaceZoomOrigin so the
    //                     transform-origin follows the
    //                     cursor — natural-scroll style:
    //                     drag right shows what was on the
    //                     left. Used to find part numbers
    //                     that are clipped by the visible
    //                     window at 4x zoom.
    //
    //   2 clicks + drag =  move the whole panel around the
    //                     page (mode 2). Updates
    //                     lockedStickyTop / lockedStickyLeft.
    //
    //   3 clicks        =  close the lock (mode 3). Fires
    //                     immediately on the 3rd mousedown,
    //                     no drag needed.
    //
    // Click counting:
    //   - A drag "consumes" the count — onUp resets it to 0
    //     after a drag so back-to-back drags don't escalate.
    //   - A click without drag schedules a 400ms reset timer;
    //     another mousedown within the window cancels the
    //     timer and bumps the count toward 2 or 3.
    //
    // Listeners attach to document (not the wrap) so dragging
    // continues even if the cursor leaves the wrap — important
    // at 4x zoom where the visible window is small and users
    // will routinely drag past the edges.
    onLockedMouseDown(key, e) {
      if (!this.inPlaceZoomLocked[key]) return;

      // Block the browser's native HTML5 image drag (the
      // ghost-image-follows-cursor behavior). Without this,
      // mousedown on an <img> kicks off the OS-level drag
      // which intercepts mousemove events and makes our
      // custom drag feel like it "doesn't start" until the
      // user releases the mouse. preventDefault here +
      // draggable="false" on the img tag together cover both
      // major browser paths.
      if (e && e.preventDefault) e.preventDefault();

      // Cancel any pending count-reset; this click extends
      // the rapid-click window.
      if (this._lockClickResetTimer) {
        clearTimeout(this._lockClickResetTimer);
        this._lockClickResetTimer = null;
      }
      this._lockClickCount = (this._lockClickCount || 0) + 1;
      var clickNum = this._lockClickCount;
      var self = this;

      // Closing the lock is now done by the explicit × button
      // in the panel's top-right corner (see onCloseLock).
      // Triple-click unlock has been retired in favor of that
      // visible affordance, so we cap meaningful click counts
      // at 2 (mode 1 = pan inside, mode 2 = move panel) and
      // ignore any escalation past that.
      if (clickNum > 2) {
        this._lockClickCount = 2;
        clickNum = 2;
      }

      // Snapshot starting state for delta-based drag math.
      var startX = e.clientX;
      var startY = e.clientY;
      var startTop = this.lockedStickyTop;
      var startLeft = this.lockedStickyLeft;
      var wrap = e.currentTarget;
      // Parse the current transform-origin so mode-1 pan can
      // delta from it (natural-scroll feel: visible content
      // moves opposite the drag direction).
      var origin = (this.inPlaceZoomOrigin[key] || "50% 50%")
        .split(" ");
      var startOriginX = parseFloat(origin[0]);
      var startOriginY = parseFloat(origin[1]);
      if (isNaN(startOriginX)) startOriginX = 50;
      if (isNaN(startOriginY)) startOriginY = 50;
      // Zoom scale matches the CSS transform: scale(4) on the
      // locked image. Hardcoded mirror — if CSS scale changes,
      // update here too.
      var ZOOM_SCALE = 4;
      var moved = false;

      // Tag the wrap with a mode-specific class so the cursor
      // can flip to crosshair (pan) or grabbing (move).
      var modeClass = clickNum === 1
        ? "is-panning-internal"
        : "is-dragging";

      function onMove(ev) {
        var dx = ev.clientX - startX;
        var dy = ev.clientY - startY;
        if (!moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
          moved = true;
          if (wrap && wrap.classList) {
            wrap.classList.add(modeClass);
          }
        }
        if (!moved) return;

        if (clickNum === 1) {
          // Mode 1: pan internally. Delta cursor pixels →
          // delta percentage of zoomed-image dimensions.
          // Subtract from startOrigin so visible content
          // moves opposite the drag (natural scroll).
          if (!wrap || !wrap.getBoundingClientRect) return;
          var rect = wrap.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;
          var dxPct = (dx / (rect.width * ZOOM_SCALE)) * 100;
          var dyPct = (dy / (rect.height * ZOOM_SCALE)) * 100;
          var nx = startOriginX - dxPct;
          var ny = startOriginY - dyPct;
          // Clamp to image bounds so dragging past the edge
          // doesn't push origin into negative / >100% (which
          // would show empty space outside the image).
          nx = Math.max(0, Math.min(100, nx));
          ny = Math.max(0, Math.min(100, ny));
          self.inPlaceZoomOrigin[key] = nx + "% " + ny + "%";
        } else if (clickNum === 2) {
          // Mode 2: move whole panel.
          self.lockedStickyTop = startTop + dy;
          self.lockedStickyLeft = startLeft + dx;
        }
      }

      function onUp() {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (wrap && wrap.classList) {
          wrap.classList.remove(modeClass);
        }
        if (moved) {
          // A drag happened — the gesture has been consumed.
          // Reset count so the NEXT click starts fresh at 1
          // rather than escalating into mode 2 or 3 unwanted.
          self._lockClickCount = 0;
        } else {
          // No drag — give the user time to add another click
          // before we conclude the gesture sequence. 400ms is
          // a typical multi-click window (slightly more than
          // the system double-click default to be forgiving).
          self._lockClickResetTimer = setTimeout(function () {
            self._lockClickCount = 0;
          }, 400);
        }
      }

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },

    // ===== onCloseLock =====
    // Wired to the × button rendered in the locked panel's
    // top-right corner (template) — replaces the previous
    // triple-click unlock gesture with an always-visible
    // affordance. Resets the click-count machine state too,
    // so a fresh click sequence starts on the next lock
    // (otherwise a stale _lockClickResetTimer could leave
    // _lockClickCount at 1 or 2 going into the next session).
    onCloseLock(key) {
      this.inPlaceZoomLocked[key] = false;
      this._lockClickCount = 0;
      if (this._lockClickResetTimer) {
        clearTimeout(this._lockClickResetTimer);
        this._lockClickResetTimer = null;
      }
      // Image gone → let the floating stack rise back to the content top.
      this._emitLockedImage();
    },

    // ===== STEP ACCORDION methods =====
    // Expand a step from the "Completed Steps" circle row.
    goToStep(n) {
      this.expandedStep = n;
    },
    isStepDone(n) {
      if (n === 1) return this.step1Done;
      if (n === 2) return this.step2Done;
      if (n === 3) return this.step3Done;
      return false;
    },
    // Hover tooltip for a circle — selection detail + "click to revise".
    circleTitle(n) {
      var rev = " — click to revise";
      if (n === 1) return "Company: " + (this.companyDisplayName || "(selected)") + rev;
      if (n === 2) return "Machine: " + (this.machineDisplayName || "(selected)") + rev;
      if (n === 3) return "Assembly: " + (this.assemblyDisplayName || "(selected)") + rev;
      return "Click to expand";
    },
    // Tell App.vue whether a zoom image is locked and how tall it is, so the
    // global floating cart/Top-5 stack drops below it (small gap) or sits
    // flush at the content top when none is locked.
    _emitLockedImage() {
      var self = this;
      this.$nextTick(function () {
        var lw = document.querySelector(".machine-image-wrap.is-zoom-locked");
        if (!self.$cartBus) return;
        self.$cartBus.$emit("locked-image-change", {
          active: !!lw,
          height: lw ? Math.round(lw.getBoundingClientRect().height) : 0,
        });
      });
    },

    openImageModal(src) {
      this.modalImage = src;
      this.resetZoom();
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.resetZoom();
    },

    // ===== PARTS INQUIRY MODAL =====
    // Open the modal AND close the Semantic UI dropdown
    // that contains the trigger button — otherwise the
    // dropdown menu sits open behind the modal backdrop.
    //
    // `context` switches the pre-filled message between
    // "missing machine" and "missing assembly" copy. Both
    // paths submit to the same /Email/parts-inquiry
    // endpoint — the parts team gets one inbox either way.
    // Defaults to "machine" if called from a v-on handler
    // that passes a MouseEvent rather than a string.
    openPartsInquiryModal(context) {
      if (typeof context !== "string") context = "machine";
      try {
        $("#MachineSelect").dropdown("hide");
      } catch (e) {
        // Dropdown not initialized yet — harmless.
      }
      try {
        $("#WearAssemblySelect").dropdown("hide");
      } catch (e) {
        // Same — assembly dropdown may not exist yet.
      }
      // Pre-fill name/email/company from the logged-in
      // user + selected company so the user doesn't retype
      // info we already have. Each field guards against
      // overwriting a value the user may have already
      // entered (e.g. they opened the modal, typed
      // something, closed it, reopened it).
      if (this.currentUser) {
        if (!this.partsInquiry.customerName) {
          var first = this.currentUser.firstName || "";
          var last = this.currentUser.lastName || "";
          this.partsInquiry.customerName = (first + " " + last).trim();
        }
        if (!this.partsInquiry.customerEmail && this.currentUser.email) {
          this.partsInquiry.customerEmail = this.currentUser.email;
        }
      }
      // Company comes from the company selector in Step 1
      // (this.companyName), populated whenever the user
      // picks a company. Falls through silently if no
      // company is selected yet.
      if (!this.partsInquiry.customerCompany && this.companyName) {
        this.partsInquiry.customerCompany = this.companyName;
      }
      // For the assembly path, pre-fill the equipment
      // field with the currently-selected machine's name
      // — saves the user retyping it. We look up the
      // selected machine from the machines array (which
      // already populated when the user picked it in
      // Step 2). Guarded behind the same "don't overwrite
      // existing input" rule as the other fields above.
      if (
        context === "assembly" &&
        !this.partsInquiry.machineDescription &&
        this.machineId
      ) {
        var self = this;
        var selectedMachine = (this.machines || []).find(function (m) {
          return String(m.value) === String(self.machineId);
        });
        if (selectedMachine && selectedMachine.text) {
          this.partsInquiry.machineDescription = selectedMachine.text;
        }
      }
      // Pre-fill the description with a sensible default —
      // most users opening this modal are reporting either
      // a missing machine or a missing assembly, depending
      // on which footer they clicked. The text is editable
      // so they can replace or extend it before sending.
      if (!this.partsInquiry.description) {
        if (context === "assembly") {
          this.partsInquiry.description =
            "I'm missing an assembly on my machine. Please reach " +
            "out to me for more information or to provide me " +
            "with a status update.\n\nThanks!";
        } else {
          this.partsInquiry.description =
            "I'm missing a machine on my account. Please reach " +
            "out to me for more information or to provide me " +
            "with a status update.\n\nThanks!";
        }
      }
      this.showPartsInquiryModal = true;
    },

    onPartsInquiryFileChange(e) {
      var files = e.target.files || (e.dataTransfer && e.dataTransfer.files);
      if (!files || !files.length) {
        this.partsInquiryFile = null;
        return;
      }
      var fileTypes = ["jpg", "jpeg", "png"];
      var ext = files[0].name.split(".").pop().toLowerCase();
      if (fileTypes.indexOf(ext) === -1) {
        this.$refs.partsInquiryFile.value = null;
        this.partsInquiryFile = null;
        this.partsInquiryFileError = "Only JPEG, JPG and PNG files are supported.";
        return;
      }
      if (files[0].size > 1024 * 1024) {
        this.$refs.partsInquiryFile.value = null;
        this.partsInquiryFile = null;
        this.partsInquiryFileError = "Maximum file size of 1 MB.";
        return;
      }
      this.partsInquiryFile = files[0];
      this.partsInquiryFileError = "";
    },

    submitPartsInquiry(bvModalEvent) {
      // Keep the modal open while the request is in flight
      // — the OK button label flips to "Sending…" via the
      // partsInquirySending flag, and the success/error
      // banner takes over the body when the call resolves.
      if (bvModalEvent) bvModalEvent.preventDefault();
      if (!this.partsInquiryFormValid || this.partsInquirySending) return;

      this.partsInquirySending = true;
      this.partsInquiryError = "";

      var fd = new FormData();
      fd.append("CustomerName", this.partsInquiry.customerName.trim());
      fd.append("CustomerEmail", this.partsInquiry.customerEmail.trim());
      if (this.partsInquiry.customerCompany) {
        fd.append("CustomerCompany", this.partsInquiry.customerCompany.trim());
      }
      if (this.partsInquiry.machineDescription) {
        fd.append("MachineDescription", this.partsInquiry.machineDescription.trim());
      }
      fd.append("Description", this.partsInquiry.description.trim());
      if (this.partsInquiryFile) {
        fd.append("InquiryFile", this.partsInquiryFile);
      }

      var self = this;
      apiService.submitPartsInquiry(fd)
        .then(function () {
          self.partsInquirySending = false;
          self.partsInquirySuccess = true;
          // Auto-close the modal after a short delay so the
          // user sees the confirmation but doesn't have to
          // manually dismiss.
          setTimeout(function () {
            self.showPartsInquiryModal = false;
          }, 1800);
        })
        .catch(function (err) {
          self.partsInquirySending = false;
          var msg = "Please try again or call (800) 278-3353.";
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : null;
          if (traceId) {
            msg = "Please try again or call (800) 278-3353. (TraceID: " + traceId + ")";
          }
          self.partsInquiryError = msg;
        });
    },

    resetPartsInquiryForm() {
      // Fired when the modal closes (any path: OK/cancel/X
      // /backdrop). Clears the form so the next open
      // starts clean — except we leave name/email alone so
      // a returning user doesn't have to re-enter them.
      this.partsInquirySending = false;
      this.partsInquirySuccess = false;
      this.partsInquiryError = "";
      this.partsInquiryFile = null;
      this.partsInquiryFileError = "";
      this.partsInquiry.customerCompany = "";
      this.partsInquiry.machineDescription = "";
      this.partsInquiry.description = "";
      if (this.$refs.partsInquiryFile) {
        this.$refs.partsInquiryFile.value = null;
      }
    },
    resetZoom() {
      this.scale = 1;
      this.pointX = 0;
      this.pointY = 0;
    },
    zoomIn() {
      this.scale = Math.min(this.maxScale, this.scale + 0.25);
    },
    zoomOut() {
      this.scale = Math.max(this.minScale, this.scale - 0.25);
      if (this.scale === 1) {
        this.pointX = 0;
        this.pointY = 0;
      }
    },
    handleWheel(event) {
      var delta = event.deltaY < 0 ? 0.2 : -0.2;
      var nextScale = Math.min(
        this.maxScale,
        Math.max(this.minScale, this.scale + delta)
      );
      this.scale = nextScale;
      if (this.scale === 1) {
        this.pointX = 0;
        this.pointY = 0;
      }
    },
    startDrag(event) {
      if (this.scale <= 1) return;
      this.isDragging = true;
      this.startX = event.clientX - this.pointX;
      this.startY = event.clientY - this.pointY;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      this.pointX = event.clientX - this.startX;
      this.pointY = event.clientY - this.startY;
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },

    // ===== MODIFY-FLOW HYDRATION =====
    // Reads the `modify*` query params planted by the
    // Modify Order modal on Cart.vue and walks Steps 1-3
    // through their pre-fill sequence:
    //
    //   1. companyId    → set immediately, fires
    //                     getMachinesByCompany so the
    //                     machine dropdown can populate.
    //   2. machineId    → set after a brief delay so the
    //                     dropdown's options exist; the
    //                     machineId watcher then fires
    //                     loadInlineWearAssemblyOptions.
    //   3. assemblyId   → stashed in pendingModifyAssemblyId;
    //                     loadInlineWearAssemblyOptions
    //                     consumes it once options arrive,
    //                     setting selectedWearAssemblyId.
    //   4. subAssemblyId→ stashed in pendingModifySub-
    //                     AssemblyId; the parts loader
    //                     (wear or all-parts) consumes it
    //                     after sub-assemblies populate.
    //
    // PO Number, wear toggle, and the cart's existing
    // staging tray are restored alongside — staging via
    // syncFromActiveCart which already runs in mounted().
    //
    // Query params get stripped from the URL after
    // application via router.replace so a refresh doesn't
    // re-trigger pre-fill against potentially stale state.
    applyModifyQuery() {
      var q = this.$route.query || {};
      if (!q.modifyMachineId && !q.modifyCompanyId && !q.modifyMachineName) return;

      console.log("[Create] modify hydration: applying", q);

      // Wear toggle — applied first so the right loader
      // (wear vs all-parts) gets used when parts populate.
      if (q.modifyConfiguredWear !== undefined) {
        this.configuredWear = q.modifyConfiguredWear === "1";
      }

      // PO Number — keeps the user adding to the same
      // Order. Both the form field and localStorage so the
      // PO picker modal that opens on each Add starts on
      // this PO.
      if (q.modifyPoNumber) {
        this.poNumber = String(q.modifyPoNumber);
        try { localStorage.poNumber = q.modifyPoNumber; } catch (e) {}
      }

      // Stash assembly + sub-assembly targets — picked up
      // by the loaders down the chain.
      if (q.modifyAssemblyId) {
        this.pendingModifyAssemblyId = String(q.modifyAssemblyId);
      }
      if (q.modifySubAssemblyId) {
        this.pendingModifySubAssemblyId = String(q.modifySubAssemblyId);
      }

      // ===== Machine description text → CompanyMachine.Id =====
      // The Modify Order modal on /cart emits modifyMachineName
      // because the cart's PurchaseOrder.MachineId is the
      // catalog Machine.Id, not the CompanyMachine.Id Step 2's
      // dropdown is keyed on. Stash here; the resolver
      // (applyPendingModifyMachineName) consumes it from
      // getMachinesByCompany.then once the dropdown's options
      // arrive. If the machines list is already loaded by the
      // time we get here (e.g. mounted's earlier
      // getMachinesByCompany call already resolved), trigger
      // the resolver immediately so we don't wait for a
      // company-change to refire it.
      if (q.modifyMachineName) {
        this.pendingModifyMachineName = String(q.modifyMachineName);
        if (this._machinesLoaded) {
          this.applyPendingModifyMachineName();
          this.syncMachineDropdownDisplay();
        }
      }

      // Company first — synchronous setter, async fetch.
      if (q.modifyCompanyId) {
        try { localStorage.companyId = q.modifyCompanyId; } catch (e) {}
        this.companyId = String(q.modifyCompanyId);
        this.getMachinesByCompany();
        // Also refresh insights so Frequently Ordered shows
        // the right company while the user adds more parts.
        if (typeof this.getInsights === "function") {
          this.getInsights();
        }
      }

      // ===== Legacy modifyMachineId fallback =====
      // ONLY runs when no modifyMachineName was provided. The
      // raw modifyMachineId path below sets machineId to the
      // catalog Machine.Id, which validateMachineSelection
      // promptly clears because it doesn't match any
      // CompanyMachine.Id in the dropdown. That's incorrect
      // for the /cart Modify-Order flow but kept here as a
      // safety net for any caller that constructs the query
      // string by hand without a name.
      //
      // Skipping it when a name IS provided is critical: if
      // both ran, the 800ms setTimeout would overwrite
      // applyPendingModifyMachineName's correct CompanyMachine.Id
      // with the wrong catalog ID after the fact, undoing the
      // fix.
      var self = this;
      if (q.modifyMachineId && !q.modifyMachineName) {
        setTimeout(function () {
          try { localStorage.machineId = q.modifyMachineId; } catch (e) {}
          self.machineId = String(q.modifyMachineId);
          // Force Semantic UI to redraw the dropdown's visible
          // label. Without this, machineId is bound on the
          // hidden input but the dropdown shows "Select your
          // machine" — looks like the modify chain failed even
          // though it succeeded. The matching call inside
          // getMachinesByCompany.then handles the case where the
          // API resolves AFTER this setTimeout; this one handles
          // the case where it resolved BEFORE.
          self.syncMachineDropdownDisplay();
        }, 800);
      }

      // Strip query params so a refresh doesn't re-trigger.
      // Catch is a no-op — vue-router throws on identical
      // navigations and we don't care.
      this.$router.replace({ query: {} }).catch(function () {});
    },

    // ===== loadWearPartIds =====
    // Fetches THIS company's wear-eligible PartIds (its
    // dbo.Wears = the Account Setup wear list) and caches
    // them on the component for use by isWearEligible(part).
    // The "2% Discount" chip must reflect the customer's OWN
    // wear parts, not the global wear-eligible catalog — so
    // we use the company-scoped endpoint keyed by
    // localStorage.companyId. Called on mount and again from
    // the companyId watcher when the user switches company.
    //
    // No companyId → empty list (chip simply won't render).
    //
    // Failure handling: silently swallow (empty list). The
    // chip just won't render — same UX as if the part isn't
    // eligible — plus a console warning for diagnosis.
    // Nothing else on the page depends on this list, so a
    // transient API blip (or a pre-deploy 404 on the new
    // endpoint) doesn't block anything else.
    async loadWearPartIds() {
      var companyId = localStorage.companyId || "";
      if (!companyId || !apiService.getCompanyWearPartIds) {
        this.wearPartIds = [];
        return;
      }
      try {
        var data = await apiService.getCompanyWearPartIds(companyId);
        if (Array.isArray(data)) {
          this.wearPartIds = data.map(function (id) {
            return String(id);
          });
        } else {
          this.wearPartIds = [];
          console.warn(
            "[Create] company wear part IDs: expected array, got",
            data
          );
        }
      } catch (e) {
        this.wearPartIds = [];
        console.warn(
          "[Create] company wear part IDs: load failed", e
        );
      }
    },

    // ===== isWearEligible =====
    // True iff `part` is registered in dbo.Wears (active,
    // any company). Used by the template's "2% Discount"
    // chip. Replaces the old `v-if="configuredWear"`
    // gating, which conflated wear-eligibility (per-part)
    // with the wear-only filter toggle (per-view) and so
    // hid the chip whenever the user browsed in all-parts
    // mode.
    //
    // Defensive checks:
    //   - falsy `part` returns false (defensive against a
    //     v-for race where the loop runs once with an
    //     undefined entry during sub-assembly hydration).
    //   - both partId and id checked because the wear /
    //     all-parts loaders shape parts slightly
    //     differently (wear loader emits both `partId`
    //     and `Id`; all-parts loader emits `id`).
    isWearEligible(part) {
      if (!part) return false;
      var pid = String(part.partId || part.id || "");
      if (!pid) return false;
      return !!this.wearPartIdSet[pid];
    },

    applyHeaderSearchQuery() {
      // Activity trail: this funnel is a VERBATIM clone of the Search page one (the
      // documented three-file contract), so one header search can re-execute here too.
      // Its own source id is what keeps that replication distinguishable from a real
      // re-search — without it every search count inflates.
      try {
        var _aq = this.$route.query || {};
        if (_aq.code || _aq.description) recordSearch("order-create", _aq.code || _aq.description);
      } catch (e) { /* never break the search */ }
      // ===== MODIFY-FLOW COLLISION GUARD =====
      // applyModifyQuery (which runs just before us in
      // mounted) sets pendingModify* fields synchronously
      // when the user arrived via Cart's "Modify Order"
      // modal. Modify intent supersedes any persisted
      // header-search context — wiping localStorage isn't
      // enough on its own because applyModifyQuery itself
      // strips the URL, leaving applyHeaderSearchQuery to
      // see a "URL bare → fall back to localStorage"
      // pattern that would re-hydrate the dismissed
      // search. Bailing here when ANY modify pending field
      // is set keeps the modify chain clean.
      //
      // pendingModifyMachineName is the Create-flow's
      // signature field (Search.vue uses direct machineId
      // assignment instead). Checking all three covers
      // both code paths defensively.
      if (this.pendingModifyAssemblyId
          || this.pendingModifySubAssemblyId
          || this.pendingModifyMachineName) {
        return;
      }

      var q = this.$route.query || {};
      var hasUrlParams = !!(q.code || q.machineName || q.assembly);
      var source = q;
      var sourceLabel = "URL";

      if (!hasUrlParams) {
        // Try localStorage as the navigation-survival
        // fallback. Wrapped in try/catch because
        // localStorage can throw in private browsing or
        // when quota is hit, and a JSON.parse of malformed
        // data can throw too. Either failure mode just
        // means "no persisted search" — fall through to
        // the early return below.
        var raw = null;
        try { raw = localStorage.headerSearchParams; } catch (e) {}
        if (raw) {
          try {
            var stored = JSON.parse(raw);
            if (stored && (stored.code || stored.machineName || stored.assembly)) {
              source = stored;
              sourceLabel = "localStorage";
            }
          } catch (e) {
            // Malformed — wipe so we don't keep choking on
            // it across loads.
            try { delete localStorage.headerSearchParams; } catch (ee) {}
          }
        }
      }

      if (!source.code && !source.machineName && !source.assembly) return;

      console.log("[Create] header search query (source: " + sourceLabel + "):", {
        code: source.code,
        machineName: source.machineName,
        assembly: source.assembly,
        allParts: source.allParts,
      });

      this.headerSearch = {
        code: source.code ? String(source.code) : "",
        machineName: source.machineName ? String(source.machineName) : "",
        assembly: source.assembly ? String(source.assembly) : "",
        active: true,
      };

      // allParts=true is the explicit "show every part"
      // intent set by Head.vue when the user picks a code
      // from autocomplete. Without flipping configuredWear
      // off here, the inline parts list would silently hide
      // any non-wear part — including the very part the
      // user clicked.
      if (source.allParts === "true") {
        this.configuredWear = false;
      }

      // Stash names for the loaders. Both consumers null
      // these out after applying.
      if (source.machineName) {
        this.pendingHeaderMachineName = String(source.machineName);
      }
      if (source.assembly) {
        this.pendingHeaderAssemblyName = String(source.assembly);
      }

      // ===== MIRROR TO LOCALSTORAGE =====
      // Persist the applied source params so that subsequent
      // navigations across pages (Search → Create Order →
      // Create RFQ via the nav tabs) can re-hydrate from
      // storage even though those nav clicks don't carry URL
      // params. Each destination page calls
      // applyHeaderSearchQuery on mount; when the URL has no
      // params, it falls back to reading this localStorage
      // entry.
      //
      // Stale-state safety: an earlier round of this code
      // tried "delete after apply" (one-shot semantics) to
      // protect against a bad suggestion from one search
      // corrupting the next. That defense is no longer
      // needed because Head.vue's autocomplete now filters
      // suggestions by the current company's machine list
      // (see Head.vue:loadUserMachineNames + the filter step
      // in getSearchedParts) — so storage can only ever
      // contain params for machines the user actually owns.
      // Bad state can't enter the system in the first place.
      //
      // Re-saves on every application — including
      // localStorage-sourced ones — so the storage timestamp
      // implicit in "most recently saved" tracks reality.
      //
      // Wrapped because localStorage can throw under quota /
      // private-browsing conditions; failing to persist is a
      // soft degradation (search still works for this
      // session, just doesn't survive cross-page nav).
      try {
        localStorage.headerSearchParams = JSON.stringify({
          code: source.code || "",
          machineName: source.machineName || "",
          assembly: source.assembly || "",
          allParts: source.allParts || "",
        });
      } catch (e) {}

      // Try the machine application immediately — when this
      // method runs from the $route watcher, machines are
      // already loaded and we can resolve right away. From
      // mount the machines list is usually still in flight,
      // so this no-ops via the _machinesLoaded guard inside
      // applyPendingHeaderMachine, and the
      // getMachinesByCompany hook picks up later.
      this.applyPendingHeaderMachine();
    },

    // Resolve pendingHeaderMachineName → machineId by
    // matching against the loaded machines list. Called
    // from three places (because any of them can be the
    // one that arrives last):
    //   - applyHeaderSearchQuery (mount/$route watcher),
    //     for the case where machines are already loaded.
    //   - getMachinesByCompany / getMachineSelectedItems,
    //     for the (more common) case where the URL params
    //     are read first and the machines arrive later.
    //   - the `machines` watcher, as a safety net for any
    //     code path that swaps the array without going
    //     through one of those two loaders.
    //
    // Idempotent and silent when there's nothing pending —
    // every call site can fire it freely without guarding.
    //
    // Match strategy: cascading from strict to loose, so a
    // perfect match wins but minor formatting drift between
    // the part-suggestion API (source of pendingHeaderMachine-
    // Name) and the machines API (source of machines[].text)
    // doesn't fail the auto-fill. Differences we've actually
    // seen: punctuation ("EXTRUDER: 90ADEX" vs "EXTRUDER
    // 90ADEX"), whitespace runs, casing.
    //
    // Each step logs to console so the user can read in
    // DevTools why a match did or didn't happen — important
    // because this is a silent UX behavior with no on-page
    // error surface when the strings disagree.
    applyPendingHeaderMachine() {
      if (!this.pendingHeaderMachineName) return;
      if (!this._machinesLoaded) {
        console.log(
          "[Create] header search: machines not loaded yet, " +
          "deferring machine hydration for '" +
          this.pendingHeaderMachineName + "'"
        );
        return;
      }

      var target = String(this.pendingHeaderMachineName);
      var machines = this.machines || [];

      // Normalization helpers — declared inline because
      // they're only useful here. `norm` keeps the string
      // mostly recognizable (whitespace + casing only);
      // `normStrong` strips ALL non-alphanumerics so e.g.
      // "EXTRUDER: 90ADEX" and "EXTRUDER 90ADEX" and
      // "extruder-90adex" all collapse to "extruder90adex".
      var norm = function (s) {
        return String(s == null ? "" : s)
          .trim().toLowerCase().replace(/\s+/g, " ");
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };

      var nTarget = norm(target);
      var nTargetStrong = normStrong(target);

      console.log("[Create] header search: resolving machine", {
        target: target,
        normalized: nTarget,
        machineCount: machines.length,
        sample: machines.slice(0, 8).map(function (m) {
          // Surface every plausible label field at once so
          // the console log shows whether the API actually
          // returns `text`, `name`, `machineName`, or some
          // other key we haven't accounted for.
          return {
            value: m.value,
            text: m.text,
            name: m.name,
            machineName: m.machineName,
          };
        }),
      });

      // Cascading match. Each strategy looks at a different
      // shape of the data; we stop at the first hit and
      // record which one fired. textCandidate() pulls the
      // first non-empty label-ish field from a row so
      // strategies don't have to know which key the API
      // chose.
      var textCandidate = function (m) {
        return m.text || m.name || m.machineName || "";
      };

      var match = null;
      var strategy = null;

      // 1. Exact text match (case-insensitive, whitespace-
      //    collapsed). Should fire on a clean match.
      match = machines.find(function (m) {
        return norm(textCandidate(m)) === nTarget;
      });
      if (match) strategy = "exact";

      // 2. Strong-normalized match — strips all punctuation
      //    and whitespace. Catches "EXTRUDER: 90ADEX" vs
      //    "EXTRUDER 90ADEX" type drift.
      if (!match) {
        match = machines.find(function (m) {
          return normStrong(textCandidate(m)) === nTargetStrong;
        });
        if (match) strategy = "alphanumeric";
      }

      // 3. Substring match either direction. Last resort
      //    before giving up — handles cases where one side
      //    has extra context (e.g. "EXTRUDER 90ADEX -
      //    BRICK PLANT" vs "EXTRUDER: 90ADEX").
      if (!match) {
        match = machines.find(function (m) {
          var nText = norm(textCandidate(m));
          if (!nText || !nTarget) return false;
          return nText.indexOf(nTarget) >= 0
              || nTarget.indexOf(nText) >= 0;
        });
        if (match) strategy = "substring";
      }

      if (match) {
        console.log(
          "[Create] header search: matched machine via '" +
          strategy + "' strategy:",
          {
            value: match.value,
            text: textCandidate(match),
          }
        );
        this.machineId = String(match.value);
        try { localStorage.machineId = this.machineId; } catch (e) {}
        var self = this;
        this.$nextTick(function () {
          self.syncMachineDropdownDisplay();
        });
      } else {
        // Surface every machine text in the console so the
        // user can spot what the actual labels look like
        // and we can adjust the match (or fix the upstream
        // data) if needed.
        console.warn(
          "[Create] header search: NO MATCH for machine '" +
          target + "' after exact/alphanumeric/substring " +
          "strategies. Available machine labels:",
          machines.map(function (m) {
            return textCandidate(m);
          })
        );
      }
      this.pendingHeaderMachineName = null;
    },

    // Dismiss the banner, clear the form's hydrated machine /
    // assembly state, AND notify Head.vue to clear its search
    // input. Per-user-request: the × should fully reset the
    // header search context, not just hide the banner.
    //
    // What gets cleared:
    //   - this.headerSearch state object (banner hide)
    //   - pending hydration vars
    //   - localStorage.headerSearchParams (cross-page persist)
    //   - localStorage.searchTerm + .description (Head.vue's
    //     own persistence keys for its input field)
    //   - machineId / selectedWearAssemblyId /
    //     selectedSubAssemblyId (cascade-clears Find Your
    //     Machine + Choose an Assembly + Sub-Assembly via
    //     existing watchers)
    //   - Emits $headerSearchBus 'cleared' so Head.vue
    //     resets its searchTerm input field LIVE — without
    //     this, the input keeps showing the dismissed term
    //     until the next page load.
    //
    // Company is left intact intentionally — Step 1 is a
    // user-level setting that shouldn't be perturbed by a
    // search dismiss.
    clearHeaderSearch() {
      this.headerSearch = {
        code: "",
        machineName: "",
        assembly: "",
        active: false,
      };
      this.pendingHeaderMachineName = null;
      this.pendingHeaderAssemblyName = null;
      this.pendingHeaderSubAssemblyName = null;

      // Wipe persisted copies so navigating away and back
      // doesn't re-hydrate the dismissed search.
      try { delete localStorage.headerSearchParams; } catch (e) {}
      try { delete localStorage.searchTerm; } catch (e) {}
      try { delete localStorage.description; } catch (e) {}

      // Cross-page machine/assembly persistence cleanup.
      // Head.vue's selectCompany manages four localStorage keys
      // that persist the user's selected machine + assembly
      // across page navigation (machineId / machineIdRFQ for
      // Order vs RFQ, assemblyId / assemblyIdRFQ likewise).
      // Without wiping these, clicking × on the Search page
      // and then navigating to /orders/create or /rfq/create
      // would re-populate those pages with the dismissed
      // machine — exactly the bug reported. Clearing all four
      // ensures the dismiss is fully effective cross-page.
      //
      // Cart-related keys (cartId, cartIdRFQ, poNumber, etc.)
      // are intentionally NOT cleared here — those represent
      // the user's actual cart state, not search context.
      try { delete localStorage.machineId; } catch (e) {}
      try { delete localStorage.machineIdRFQ; } catch (e) {}
      try { delete localStorage.assemblyId; } catch (e) {}
      try { delete localStorage.assemblyIdRFQ; } catch (e) {}

      // Cascade-clear the form's hydrated state. Setting
      // machineId to null fires the existing machineId watcher
      // which clears the assembly options + parts list
      // downstream. Explicitly nulling the assembly + sub-
      // assembly selections too in case the watcher debouncing
      // leaves residual state.
      this.machineId = null;
      this.selectedWearAssemblyId = null;
      this.selectedSubAssemblyId = null;

      // Force the Semantic UI dropdowns to update their visible
      // labels back to placeholder text ('Select your machine',
      // 'Select an assembly'). SUI manages its display state
      // independently of the hidden <input v-model="machineId">,
      // so the Vue data nullification above doesn't refresh
      // the visible chip — without these explicit dropdown
      // clear() calls, the user sees 'EXTRUDER: 90ADEX...'
      // hanging around even though machineId is null underneath.
      // (Mirrors $("#WearAssemblySelect").dropdown("clear")
      // pattern used elsewhere in this file's reset paths.)
      // Wrapped because jQuery may not be available in fast-
      // nav edge cases — silent failure beats a crash here.
      try { $("#MachineSelect").dropdown("clear"); } catch (e) {}
      try { $("#WearAssemblySelect").dropdown("clear"); } catch (e) {}

      // Notify Head.vue (across components) to clear its own
      // searchTerm input field. Lazy-init the bus the same way
      // mounted does for $cartBus. Head.vue's mounted hook
      // subscribes to this event; see handleHeaderSearchCleared.
      Vue.prototype.$headerSearchBus =
        Vue.prototype.$headerSearchBus || new Vue();
      Vue.prototype.$headerSearchBus.$emit('cleared');

      var q = this.$route.query || {};
      var nextQuery = {};
      Object.keys(q).forEach(function (k) {
        if (k !== "code" && k !== "machineName"
            && k !== "assembly" && k !== "allParts") {
          nextQuery[k] = q[k];
        }
      });
      this.$router.replace({ query: nextQuery })
        .catch(function () {});
    },

    // ===== HEADER-SEARCH DEEP SEARCH =====
    // Triggered from loadInlineWearAssemblyOptions when the
    // top-level match misses. The suggestion API returns
    // the sub-assembly name (e.g. "28PC WEDGE GROOVE LINER")
    // which lives one level below wearAssemblyOptions
    // (e.g. "HCV AUGERS AND LINERS"). To resolve, fan out
    // parallel calls to the parts API for each top-level
    // assembly — first one whose sub-list contains a name
    // match wins.
    //
    // Picks the right endpoint based on configuredWear:
    //   - all-parts (configuredWear=false) — the path the
    //     header search lands in by default since
    //     applyHeaderSearchQuery sets configuredWear=false
    //     when allParts=true is on the URL. Uses
    //     getAssemblyWithParts; sub-assembly names are at
    //     row.text || row.assemblyName || row.name.
    //   - wear-only (configuredWear=true) — uncommon but
    //     supported for completeness. Uses
    //     getAssembliesWithWearParts; sub-assembly names
    //     at row.assemblyName.
    //
    // First success sets selectedWearAssemblyId to the
    // matching parent. The selectedWearAssemblyId watcher
    // then triggers the regular parts-loader, which sees
    // pendingHeaderSubAssemblyName still on the queue and
    // selects the sub-assembly via its hydration block.
    // The duplicate fetch (deep search fetched the parent
    // already; loader fetches it again) is intentional —
    // letting the loader own the wearAssemblies state keeps
    // sub-assembly selection out of this method's
    // responsibility and avoids racing the loader's
    // wearLoadKey guard.
    //
    // The wearLoadKey is captured at start so a stale deep-
    // search response (user changed machine mid-flight)
    // doesn't override their newer selection.
    applyHeaderAssemblyDeepSearch() {
      if (!this.pendingHeaderSubAssemblyName) return;
      if (this.selectedWearAssemblyId) {
        // User picked something while the search was in
        // flight — don't overwrite.
        console.log(
          "[Create] header search: deep search aborted, " +
          "user selected an assembly first"
        );
        this.pendingHeaderSubAssemblyName = null;
        return;
      }
      var opts = this.wearAssemblyOptions || [];
      if (opts.length === 0) {
        console.warn(
          "[Create] header search: deep search aborted, " +
          "no top-level assemblies available"
        );
        this.pendingHeaderSubAssemblyName = null;
        return;
      }

      var self = this;
      // ===== STALE-CHECK STRATEGY =====
      // Originally this captured `wearLoadKey`, but that
      // value gets bumped by mounted()'s 600ms setTimeout
      // when it calls loadInlineWearAssemblyOptions a
      // second time as part of its bootstrap chain. That
      // second call is harmless to the assembly options
      // themselves (same machine, same data), but it
      // invalidated every deep-search response that landed
      // after T=600ms — making the entire fan-out silently
      // fail when the API was fast enough to put deep-
      // search start before the 600ms boundary (which is
      // exactly the localStorage-restore path's typical
      // timing because nothing else is competing for the
      // network).
      //
      // Capturing machineId instead means we ONLY abort
      // when the user genuinely switches machines (the
      // case we actually care about), not when the same
      // machine's bootstrap chatter fires a redundant
      // assembly-options reload. machineId only changes
      // when applyPendingHeaderMachine, the dropdown's
      // change handler, or a localStorage restore moves
      // it — none of which fire from the bootstrap
      // setTimeout.
      var startMachineId = String(this.machineId || "");
      var target = String(this.pendingHeaderSubAssemblyName);

      // Local normalization helpers. Same shape as the
      // top-level matcher above but inlined since closure-
      // capture is the cleanest way to hand them to the
      // promise chain.
      var norm = function (s) {
        return String(s == null ? "" : s)
          .trim().toLowerCase().replace(/\s+/g, " ");
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };
      var nT = norm(target);
      var nTS = normStrong(target);

      console.log(
        "[Create] header search: deep search starting for " +
        "sub-assembly '" + target + "' across " +
        opts.length + " top-level assemblies, " +
        "configuredWear=" + this.configuredWear +
        ", startMachineId=" + startMachineId
      );

      // Pick the API based on configuredWear, with a
      // shape-normalizing extractor that pulls a list of
      // sub-assembly objects with at least { id, name }
      // from whatever the API returned.
      var fetchSubs;
      if (this.configuredWear) {
        fetchSubs = function (parentValue) {
          return apiService.getAssembliesWithWearParts({
            assemblyId: parentValue,
            companyMachineId: self.machineId,
          }).then(function (data) {
            var rows = Array.isArray(data) ? data : [];
            return rows.map(function (sa) {
              return {
                id: sa.assemblyId,
                name: sa.assemblyName || "",
              };
            });
          });
        };
      } else {
        fetchSubs = function (parentValue) {
          return apiService.getAssemblyWithParts({
            id: parentValue,
          }).then(function (data) {
            var rows = Array.isArray(data) ? data : [];
            return rows.map(function (sa, idx) {
              return {
                id: sa.assemblyId || sa.id || sa.value
                    || ("subassembly-" + idx),
                name: sa.assemblyName || sa.text
                    || sa.name || "",
              };
            });
          });
        };
      }

      // Issue all parent calls in parallel. We use a
      // hand-rolled "first match wins" since Promise.race
      // would resolve on the first response regardless of
      // whether it had a hit. We resolve as soon as any
      // promise's result contains the target sub-assembly,
      // and ignore later resolutions.
      var settled = false;
      var pending = opts.length;
      opts.forEach(function (parent) {
        fetchSubs(parent.value).then(function (subs) {
          if (settled) return;
          // Stale-response guard. machineId changes only
          // on user action (machine switch) or another
          // applyPendingHeaderMachine pass. Internal
          // bootstrap reloads keep it stable, so this
          // doesn't false-positive the way wearLoadKey
          // used to.
          if (String(self.machineId || "") !== startMachineId) {
            console.log(
              "[Create] header search: deep search response " +
              "for parent '" + parent.text + "' dropped — " +
              "machineId changed from '" + startMachineId +
              "' to '" + self.machineId + "' (user switched " +
              "machines mid-search)"
            );
            settled = true;
            return;
          }
          // First-hit-wins match across the three
          // strategies, applied in order to each sub.
          var hit = subs.find(function (s) {
            return norm(s.name) === nT;
          }) || subs.find(function (s) {
            return normStrong(s.name) === nTS;
          }) || subs.find(function (s) {
            var n = norm(s.name);
            return n && nT && (
              n.indexOf(nT) >= 0 || nT.indexOf(n) >= 0
            );
          });
          pending--;
          if (hit) {
            settled = true;
            console.log(
              "[Create] header search: deep search HIT — " +
              "parent '" + parent.text + "' (id " +
              parent.value + ") contains sub-assembly '" +
              hit.name + "' (id " + hit.id + ")"
            );
            // Set the parent. The selectedWearAssemblyId
            // watcher fires loadInlinePartsForSelected-
            // Assembly, which fetches sub-assemblies +
            // parts. Its hydration block consumes
            // pendingHeaderSubAssemblyName (which we leave
            // on the queue) and selects the sub.
            self.selectedWearAssemblyId = parent.value;
            self.$nextTick(function () {
              try {
                $("#WearAssemblySelect").dropdown(
                  "set selected", parent.value
                );
              } catch (e) {}
            });
          } else if (pending === 0) {
            // Every parent has reported back without a
            // hit. Give up cleanly — leave selections
            // untouched, log so the user can see what
            // every sub looked like.
            settled = true;
            console.warn(
              "[Create] header search: deep search " +
              "exhausted, no parent contains a sub-" +
              "assembly matching '" + target + "'"
            );
            self.pendingHeaderSubAssemblyName = null;
          }
        }).catch(function (e) {
          pending--;
          console.warn(
            "[Create] header search: deep search fetch " +
            "failed for parent '" + parent.text + "':", e
          );
          if (pending === 0 && !settled) {
            settled = true;
            self.pendingHeaderSubAssemblyName = null;
          }
        });
      });
    },

    // ===== HEADER-SEARCH PART SURFACING =====
    // Once the parts data has loaded for the selected
    // assembly, find the specific part code the user
    // clicked from the header search and pull it to the
    // top of its sub-assembly's parts array. Without this,
    // the searched-for part can land mid-list (parts are
    // ordered by part-sequence on the API side) which
    // visually buries the very thing the user navigated
    // here to act on.
    //
    // Operates on the local list BEFORE assignment to
    // wearAssemblies so the splice/unshift happens off the
    // reactive graph, and the eventual assignment to
    // wearAssemblies triggers exactly one reactive update
    // showing the final ordering.
    //
    // Also auto-expands the sub-assembly card containing
    // the matched part — the existing pendingHeaderSub-
    // AssemblyName block already does this when the deep
    // search route fired, but this covers the path where
    // the top-level matched directly (no deep search) and
    // we still want the floated part visible without an
    // extra click. $set is used so the auto-expand happens
    // on the final reactive expandedWearAssemblies map
    // after this method returns; the loaders assign that
    // map in the same window so a direct mutation here
    // would just get clobbered.
    //
    // No-op when:
    //   - no headerSearch.code on state (banner not active)
    //   - list is empty
    //   - the part isn't in any sub-assembly's parts list
    //     (logged so we can spot data drift between
    //     suggestion API's `code` and the parts API's
    //     `code`/`partCode` fields)
    _floatHeaderSearchPartToTop(list) {
      if (!this.headerSearch || !this.headerSearch.code) return;
      if (!Array.isArray(list) || list.length === 0) return;

      var target = String(this.headerSearch.code);
      var norm = function (s) {
        return String(s == null ? "" : s).trim().toLowerCase();
      };
      var normStrong = function (s) {
        return String(s == null ? "" : s)
          .toLowerCase().replace(/[^a-z0-9]/g, "");
      };
      var nT = norm(target);
      var nTS = normStrong(target);

      // The parts API uses `code` in the wear-loader path
      // and `code`/`partCode`/`partNumber` interchangeably
      // in the all-parts path depending on which downstream
      // service the response went through. Probe all three
      // so this doesn't quietly miss when one's the empty
      // string.
      var partCodeOf = function (p) {
        return p.code || p.partCode || p.partNumber || "";
      };

      var foundInAssemblyId = null;
      var foundInAssemblyName = null;
      var foundCode = null;

      list.forEach(function (sa) {
        if (!sa || !Array.isArray(sa.parts)) return;
        if (sa.parts.length === 0) return;

        // Cascading match. We don't fall through to a
        // substring strategy on parts because part codes
        // are tightly formatted; a substring hit is
        // probably wrong (e.g. "NDH S 2012" would
        // partial-match "NDH S 20121" too). Exact and
        // alphanumeric are enough to absorb real-world
        // formatting drift without false positives.
        var idx = sa.parts.findIndex(function (p) {
          return norm(partCodeOf(p)) === nT;
        });
        if (idx === -1) {
          idx = sa.parts.findIndex(function (p) {
            return normStrong(partCodeOf(p)) === nTS;
          });
        }

        if (idx > 0) {
          // Mutate in place — at this stage `list` is the
          // local pre-assignment array, so this doesn't
          // poke Vue's reactivity. The eventual
          // wearAssemblies = list assignment is the
          // single reactive trigger.
          var moved = sa.parts.splice(idx, 1)[0];
          sa.parts.unshift(moved);
          foundInAssemblyId = sa.assemblyId;
          foundInAssemblyName = sa.assemblyName;
          foundCode = partCodeOf(moved);
        } else if (idx === 0) {
          // Already at the top — nothing to move, but
          // still record the location for the auto-expand
          // step below.
          foundInAssemblyId = sa.assemblyId;
          foundInAssemblyName = sa.assemblyName;
          foundCode = partCodeOf(sa.parts[0]);
        }
      });

      if (foundInAssemblyId != null) {
        console.log(
          "[Create] header search: floated part '" +
          foundCode + "' to top of sub-assembly '" +
          foundInAssemblyName + "' (id " + foundInAssemblyId + ")"
        );
        // Stash for the caller to apply after assigning
        // wearAssemblies — both loaders assign
        // expandedWearAssemblies right after the
        // wearAssemblies assignment, and we want our
        // expand to ride on that same reactive update,
        // not get overwritten by it.
        this._pendingHeaderExpandAssemblyId = foundInAssemblyId;
      } else {
        console.log(
          "[Create] header search: part '" + target +
          "' not found in any loaded sub-assembly's parts " +
          "list. This is fine if the user expanded a " +
          "different assembly than the one their part lives " +
          "in. Available codes by sub-assembly:",
          list.map(function (sa) {
            return {
              subAssembly: sa.assemblyName,
              codes: (sa.parts || [])
                .map(function (p) { return partCodeOf(p); })
            };
          })
        );
      }
    },

    onMachineChange(event) {
      this.machineId = event.target.value;
    },
    // Mirrors onMachineChange — fires from the hidden
    // input inside the Semantic UI dropdown when the user
    // picks an assembly from the menu. An empty string
    // (Semantic UI's representation of "no selection")
    // gets normalized to null so downstream watchers and
    // truthiness checks behave the same as they did when
    // this was a native <select> bound to v-model="null".
    onWearAssemblyChange(event) {
      var v = event.target.value;
      this.selectedWearAssemblyId = v === "" ? null : v;
    },
    onAssemblyChange(event) {
      this.assemblyId = event.target.value;
    },
    onCompanyChange(event) {
      this.companyId = event.target.value;
      // Update display name + insights to follow the new company.
      var match = (this.companies || []).find(function (c) {
        return String(c.value) === String(event.target.value);
      });
      this.companyName = match ? match.text : "";
      this.getMachinesByCompany();
      this.getInsights();
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(" ", {
          title: "DELETE ALL ITEMS IN CART",
          id: "deleteModal",
          size: "md",
          buttonSize: "sm",
          okVariant: "dark",
          okTitle: "Yes, please clear cart",
          cancelVariant: "outline",
          cancelTitle: "No, please don’t",
          hideHeaderClose: true,
          centered: true,
        })
        .then((clear) => {
          if (clear && localStorage.cartId) {
            apiService
              .deleteCart({
                id: localStorage.cartId,
              })
              .then((request) => {
                this.deleteCartSuccessful(request);
                this.disabled = false;
              })
              .catch();
          }
        })
        .catch();
    },
    deleteCartSuccessful() {
      // See RFQSavedItems.vue's deleteCartSuccessful for
      // the full rationale: companyId is shared user-state,
      // not cart-state, and clearing it as a side-effect
      // of a cart action makes the user appear logged out
      // (greeting disappears, navbar resets, create-page
      // guards bounce them). Cart deletion shouldn't touch
      // it.
      localStorage.cartId = 0;

      delete localStorage.poNumber;
      delete localStorage.machineId;
      delete localStorage.assemblyId;

      // Notify the global cart bus so the header badge
      // refreshes after an order-cart wipe. Same pattern
      // as Cart.vue's deleteCartSuccessful and the
      // matching RFQ-side handlers.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }

      this.$router.push({
        name: "Create New Order",
      });
    },
  },
};
</script>

<style scoped>
#createOrder {
  padding-bottom: 15px;
}

/* Page breadcrumb spacing */
#createOrder #breadcrumb {
  margin-bottom: 8px;
}

#createOrder #breadcrumb .container {
  padding-left: 0;
  padding-right: 0;
}

/* Left and right columns align better */
#createOrder .row.align-items-stretch {
  align-items: stretch;
}

/* Main order form panel */
#orderForm {
  height: 100%;
}

/* Title spacing */
#orderForm h1 {
  margin-bottom: 12px;
  text-transform: uppercase;
}

#orderForm h2 {
  margin-bottom: 0;
}

/* Update banner */
#updateBanner {
  cursor: pointer;
  height: 100%;
  min-height: 92px;
}

#updateBanner .impUpdate {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Inputs and dropdown rhythm */
#orderForm label {
  display: block;
  margin-bottom: 6px;
}

#orderForm input[type="text"] {
  width: 100%;
}

/* Semantic dropdowns in this page */
#CompanySelect,
#MachineSelect {
  min-height: 44px;
}

/* Let the machine and assembly dropdown menus grow with
   their content rather than capping at Semantic UI's
   default ~200px (which forces scroll even on short
   lists). Cap at 60vh so a very long list still can't
   push other UI off-screen — past that, the menu's own
   scrollbar takes over. The "Don't see your machine?" /
   "Don't see your assembly?" help footers are the last
   children of .menu, so this rule applies to the items +
   footer together. */
#MachineSelect .menu,
#WearAssemblySelect .menu {
  max-height: none !important;
}

/* Checkbox block */
#configuredParts {
  margin-top: 18px;
}

#configuredParts + label,
#configuredParts ~ label,
#createOrder .custom-control-label {
  line-height: 1.35;
}

/* Primary CTA */
#orderForm .btn.btn-primary {
  width: 100%;
  min-height: 48px;
  text-transform: uppercase;
}

/* ===== FREQUENTLY ORDERED INSIGHTS PANEL =====
   Visual treatment mirrors the assembly cards used elsewhere on the
   site: white card, light border, small red top accent, subtle
   shadow. Each row is a flex row with the part info on the left and
   a compact Add button on the right. Empty/loading states are
   centered italic gray so they read as transient rather than
   structural. */
.insights-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 18px 20px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* ===== STICKY RIGHT COLUMN =====
   On large screens the right column (staging tray on top
   + Frequently Ordered below) follows the user as they
   scroll the long left column.

   Two things to be careful about:
     1. align-self: flex-start so the column doesn't stretch
        to match the left column's height — sticky elements
        only work when shorter than their scroll container.
     2. `top` offset is large enough to clear the site's
        fixed header + breadcrumb row. Anything smaller and
        the panel pins behind them, which is what hid the
        staging tray earlier.

   No internal scrollbar (overflow-y: auto) on the column —
   that lets the panel be scrolled independently of the
   page, which lets the staging tray disappear off the top
   of the panel even though it should always be visible at
   the top. If the panel grows taller than the viewport,
   sticky simply stops working past the bottom edge — that's
   fine, the user can keep scrolling the page normally.

   Disabled on smaller screens (col-lg- collapses to
   stacked) so the panel doesn't pin awkwardly above the
   form steps. */
@media (min-width: 992px) {
  .create-order-row > .col-lg-5 {
    position: sticky;
    top: 130px;
    align-self: flex-start;
  }
  /* Stop the left column from stretching to match the
     (often-tall) right panel. Without this, the form card's
     grey form-step boxes balloon to fill all the empty
     vertical space below the dropdowns when the user hasn't
     selected a machine yet (no Step 3 to push the form down).
     align-self: flex-start makes the left column take its
     natural content height — empty space below it is just
     page background, not stretched form chrome. */
  .create-order-row > .col-lg-7 {
    align-self: flex-start;
  }
}

.insights-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  margin-bottom: 6px;
  border-bottom: 1px dashed #d8d8d8;
}

.insights-panel-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

/* Header right-side group: YTD/All Time toggle + search input.
   Sits to the right of the panel title; on narrow viewports the
   group wraps to a new line below the title. The two children
   (toggle + search) can themselves wrap relative to each other
   if the column is especially tight. */
.insights-panel-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.insights-panel-toggle {
  display: flex;
  gap: 4px;
}

.insights-panel-toggle .btn-sm {
  font-size: 11px;
  padding: 4px 12px;
  letter-spacing: 0.2px;
}

/* Force black text on the inactive (outline) toggle button.
   Bootstrap-Vue's btn-outline-dark inherits a near-white color in
   the global theme on this site, so the "All Time" label was
   showing as nearly invisible against white. Pin the colors
   explicitly so it reads cleanly in both states. */
.insights-panel-toggle .btn-outline-dark {
  color: #222 !important;
  background: #fff !important;
  border: 1px solid #555 !important;
  font-weight: 600 !important;
}

.insights-panel-toggle .btn-outline-dark:hover,
.insights-panel-toggle .btn-outline-dark:focus {
  color: #fff !important;
  background: #555 !important;
  border-color: #555 !important;
}

.insights-panel-toggle .btn-dark {
  color: #fff !important;
  font-weight: 600 !important;
}

/* ===== HISTORY SEARCH INPUT =====
   Pill-shaped input with magnifying-glass icon on the left and an
   × clear button on the right (only visible when there's text).
   Width grows to fill remaining space on the controls row, with a
   sensible min so it stays usable when the column is tight. */
.insights-panel-search {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 130px;
  min-width: 130px;
  max-width: 200px;
  padding: 0 8px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 14px;
  height: 26px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-panel-search:focus-within {
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}

.insights-search-icon {
  flex-shrink: 0;
  color: #888;
}

.insights-panel-search:focus-within .insights-search-icon {
  color: #c0392b;
}

.insights-search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #222;
  padding: 0;
}

.insights-search-input::placeholder {
  color: #aaa;
  font-style: italic;
}

.insights-search-clear {
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  cursor: pointer;
  line-height: 1;
}

.insights-search-clear:hover {
  background: #c0392b;
  color: #fff;
}

/* Inline "Clear search" link inside the no-results empty state. */
.insights-empty-clear {
  display: inline-block;
  margin-left: 6px;
  color: #c0392b;
  text-decoration: underline;
  cursor: pointer;
  font-style: normal;
}

.insights-empty-clear:hover {
  color: #a8321f;
  text-decoration: underline;
}

.insights-panel-sub {
  margin: 0 0 14px;
  font-size: 12px;
  color: #777;
  line-height: 1.4;
}

.insights-panel-sub strong {
  color: #444;
}

.insights-empty {
  padding: 30px 12px;
  text-align: center;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 6px;
}

.insights-empty-text {
  font-size: 13px;
  color: #888;
  font-style: italic;
  line-height: 1.5;
}

/* ===== SELECTED TRAY =====
   Sits at the top of the panel (above the part list) when the user
   has anything queued. Soft yellow-ish accent so it reads as
   "this is your queue, not the catalog." Each chip is a pill with
   the part code and an × to remove. Clear-all link sits in the
   header so users can reset without a per-chip dance. */
/* Staging tray — the user's working order. Earns its
   visual weight: a 4px red left accent (matches the
   "Order #N" pattern used on the cart page), a soft
   shadow to lift it off the panel, and a dedicated title
   row above the count line so it never reads as
   secondary content. */
.insights-selected-tray {
  margin-bottom: 16px;
  padding: 14px 14px 12px 14px;
  background: #fff;
  border: 1px solid #e7d6a6;
  border-left: 4px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(192, 57, 43, 0.08);
  position: relative;
}

.insights-selected-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e6c97a;
}

.insights-selected-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  flex-shrink: 0;
}

.insights-selected-title-text {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.insights-selected-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8a6d2a;
}

.insights-selected-clear {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #888;
  text-decoration: underline;
  cursor: pointer;
}

.insights-selected-clear:hover {
  color: #c0392b;
}

/* ===== QUEUE ROWS =====
   Replaces chip-based rendering. Each row is a compact mini-cart
   line: part code → qty input → × remove. Number input width is
   tight (around 60px) so it sits inline with the other elements
   without dominating. */
.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-queue-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5e9c5;
}

.insights-queue-row:last-child {
  border-bottom: none;
}

.insights-queue-row-code {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 700;
  color: #5a4716;
  letter-spacing: 0.2px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insights-queue-row-qty {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.insights-qty-input {
  width: 56px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #222;
  text-align: center;
  border: 1px solid #d4c486;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

/* Hide the spinner controls in webkit so the input looks clean —
   the user can still type any positive integer. */
.insights-qty-input::-webkit-inner-spin-button,
.insights-qty-input::-webkit-outer-spin-button {
  opacity: 1;
}

.insights-qty-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a6d2a;
}

.insights-queue-row-remove {
  flex-shrink: 0;
  background: transparent;
  border: none;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 16px;
  font-weight: 700;
  color: #888;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

/* ===== ORDER GROUPS WITHIN TRAY =====
   Each PO becomes its own little card inside the tray, with an
   "Order #N · PO: 12345 · M parts" header and chips beneath. The
   visual weight of the group header makes the order grouping legible
   without being noisy. */
.insights-order-group {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ecdfb3;
  border-radius: 4px;
}

.insights-order-group:first-of-type {
  margin-top: 4px;
}

.insights-order-group-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px dashed #f0e3b5;
}

.insights-order-group-num {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.insights-order-group-meta {
  font-size: 11px;
  color: #8a6d2a;
}

.insights-order-group-meta strong {
  color: #5a4716;
}

/* ===== CONTINUE BUTTON META =====
   Sub-line under the Continue button shown only when there's more
   than one order — otherwise the breakdown is implicit. */
.insights-continue-meta {
  margin-top: 6px;
  text-align: center;
  font-size: 11px;
  color: #888;
  font-style: italic;
}

/* Sub-meta when the cart already matches the staging tray —
   the button text says "Go to Cart" and this confirms why
   without yelling. Subtle green to convey "synced / no
   action needed". */
.insights-continue-meta-synced {
  color: #2d7a3a;
}

/* Sync-status line shown beneath the staging tray after the
   Continue button was removed. Communicates whether the cart
   reflects the current queue without taking up a full button.
   Synced state uses the brand green (already used elsewhere
   for "all good" signals); pending state uses a muted gray
   so it doesn't grab attention during the brief save window. */
/* ===========================================================
   CREATE ORDER FORM — left side of /orders/create
   ===========================================================
   Two-step layout: Order Details (Company + PO) and Find Your
   Part by Machine (machine select + wear filter). Plus a
   tinted help panel for users whose machine isn't listed yet
   (new equipment, not-yet-configured), which routes them to
   the parts team rather than letting them get stuck. */

.create-order-form {
  /* Card visual: lift the form off the page with a thin red
     top accent, white background, soft shadow, and rounded
     corners. The red top accent is a real border-top (see the
     #orderForm rule below) so it matches the cart panel exactly.
     Background of the section behind the card is left as
     the page default; the card creates contrast on its own
     via shadow rather than a colored backdrop, which keeps
     the surrounding chrome neutral.

     The form element also carries Bootstrap's `.row`
     class, so we override the negative margins it brings
     in — otherwise the row's children would sit 15px
     outside the card's padding and break the visual.

     Note: this must NOT use `overflow: hidden` — that was
     clipping the Machine dropdown menu (Semantic UI .menu is
     position:absolute and got cut off at the card edge). The
     red border-top accent follows the card's border-radius on
     its own, so no clipping boundary is needed. */
  position: relative;
  background: #ffffff;
  border-radius: 8px;
  padding: 28px 28px 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04),
              0 8px 24px rgba(0, 0, 0, 0.08);
  margin-left: 0;
  margin-right: 0;
}

.create-order-form > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

/* Brand-red top accent. Implemented as a real 3px top BORDER (not a
   ::before bar) so it renders IDENTICALLY to the floating cart / Top 5
   panels (.staging-tray-wrapper, .insights-panel both use
   `border-top: 3px solid #c0392b` on a 6px-radius card). The old ::before
   sat 1px below the card's grey top border and rounded at 8px (vs the
   card's 6px), so the red line read as thinner/lower than the cart's.
   Targeting #orderForm (id) + !important beats the panel's faint 1px grey
   top border. A border doesn't clip children (the ::before was originally
   introduced to dodge an overflow:hidden clip, which no longer exists), so
   this is both simpler and a pixel-for-pixel match to the cart. */
#orderForm {
  border-top: 3px solid #c0392b !important;
}

/* Align the form card with the breadcrumb. steele's "standard page
   spacing" rule forces `#createOrder > .container { padding: 30px 24px 15px
   !important }`, which pushed the card's left edge 24px inboard of the
   "Home" breadcrumb (and its right edge 24px short of the search box). The
   plain `padding: 0` here lost to that !important, so we override the
   HORIZONTAL padding with our own !important — this scoped rule's data-v
   attribute gives it higher specificity than steele's, so it wins. Vertical
   padding (top 30 / bottom 15) is intentionally left to steele; the card's
   top then aligns with the floating panel via alignWidgetStack(). */
#createOrder > .container {
  margin-top: 20px;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Bootstrap's .row applies margin-left/right: -15px to
   outdent within a padded container. With the container
   padding zeroed (above), those negative margins would push
   the columns past the page edge. Neutralize them and
   neutralize the columns' compensating padding so children
   sit flush with the breadcrumb. The right column gets a
   small left-padding back to preserve the gutter between
   form and staging tray. */
#createOrder .create-order-row {
  margin-left: 0;
  margin-right: 0;
}

#createOrder .create-order-row > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

#createOrder .create-order-row > .col-lg-5 {
  padding-left: 24px;
}

@media (max-width: 991.98px) {
  /* On stacked layout the staging tray drops below the
     form — restore default padding so it doesn't bleed
     into the form card visually. */
  #createOrder .create-order-row > .col-lg-5 {
    padding-left: 0;
    margin-top: 16px;
  }
}

.create-order-title {
  margin-bottom: 6px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.create-order-subtitle {
  margin-bottom: 24px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.new-equipment-link {
  color: #c0392b;
  font-weight: 500;
  text-decoration: underline;
}

.new-equipment-link:hover {
  color: #a8321f;
}

/* ----- Numbered step container ----- */
.form-step {
  margin-bottom: 20px;
  padding: 18px 20px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

/* ===== COMPLETED STEPS NAV =====
   Compact row of clickable numbered circles for finished/started steps,
   shown above the one expanded step. Works in light + dark mode. */
.completed-steps-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}
.completed-steps-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a93a0;
  margin-right: 2px;
}
.completed-step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease, box-shadow 0.1s ease, background-color 0.1s ease;
}
.completed-step-circle.is-done {
  background: #27ae60; /* green = completed */
}
.completed-step-circle.is-pending {
  background: #adb5bd; /* grey = reachable but not yet completed */
}
.completed-step-circle:hover {
  transform: scale(1.12);
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.3);
}

.form-step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

/* Variant for headers that carry a right-aligned action
   (currently: Step 3 with the Show-wear-parts-only toggle).
   The header-main wrapper holds the step num + title on
   the left; the action sits flush right via
   justify-content: space-between. flex-wrap lets the
   action drop to a new row on very narrow viewports
   instead of overflowing the card. */
.form-step-header-with-action {
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 8px;
}

.form-step-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  /* min-width: 0 + flex-shrink: 1 lets the title truncate
     gracefully if the header gets squeezed instead of
     pushing the action off the right edge. */
  min-width: 0;
  flex: 1 1 auto;
}

/* Step number circle. Default state is muted gray —
   "this step exists, but it isn't done yet." When the
   step's data is captured (company picked, machine picked,
   assembly picked) it flips to green via the `.is-complete`
   modifier, giving the user a glanceable progress
   indicator without adding extra UI. */
.form-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #adb5bd;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}

.form-step-num.is-complete {
  background: #27ae60;
}

.form-step-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #222;
  line-height: 28px;
}

.form-step-body label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  text-transform: none;
  letter-spacing: 0;
}

.form-step-body input[type="text"] {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.form-step-body input[type="text"]:focus {
  border-color: #c0392b;
  outline: none;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

/* Helper text under inputs: small, muted, italic. Keeps
   guidance close to the input it applies to without
   competing with the label. */
.form-helper-text {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.form-helper-text a {
  color: #c0392b;
  text-decoration: underline;
}

/* ----- Wear-parts toggle ----- */
.wear-parts-toggle {
  /* Override Bootstrap-Vue's default vertical alignment so
     the label and helper text stack neatly to the right of
     the checkbox rather than running on a single line. */
}

/* Variant when the toggle lives inside a step header rather
   than its own row. Tightens the label sizing so it sits
   comfortably next to the step title without dominating
   the row, and removes any inherited block margins from
   the previous .col-12 mt-3 wrapper. */
.wear-parts-toggle-header {
  margin: 0;
  flex-shrink: 0;
}

.wear-parts-toggle-header .wear-parts-label {
  font-size: 13px;
}

.wear-parts-toggle-header .wear-parts-helper {
  font-size: 11px;
}

.wear-parts-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #222;
  line-height: 1.3;
}

.wear-parts-helper {
  display: block;
  font-size: 12px;
  color: #888;
  font-style: italic;
  font-weight: 400;
  margin-top: 2px;
}

/* ----- New equipment help panel -----
   Visually distinct from the numbered steps so users
   intuit it as a different type of element ("not part of
   the form, but a way out"). Soft yellow tint conveys
   "info / heads up" without being alarming. */
.new-equipment-panel {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff8e6;
  border: 1px solid #f3deaa;
  border-radius: 6px;
  padding: 16px 18px;
  margin-bottom: 20px;
}

.new-equipment-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: #b8860b;
  margin-top: 2px;
}

.new-equipment-icon svg {
  width: 100%;
  height: 100%;
}

.new-equipment-content {
  flex: 1;
}

.new-equipment-content strong {
  display: block;
  font-size: 14px;
  color: #222;
  margin-bottom: 4px;
}

.new-equipment-content p {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.new-equipment-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.new-equipment-actions .btn-sm {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
}

/* Force black text on both action buttons regardless of
   Bootstrap variant inheritance. The btn-outline-primary
   class otherwise pulls red text from the brand red, which
   gets lost against the yellow tinted panel background. */
.new-equipment-actions .btn-outline-primary,
.new-equipment-actions .btn-outline-secondary {
  color: #222 !important;
  border-color: #888 !important;
  background: #fff !important;
}

.new-equipment-actions .btn-outline-primary:hover,
.new-equipment-actions .btn-outline-secondary:hover {
  color: #fff !important;
  background: #c0392b !important;
  border-color: #c0392b !important;
}

/* ----- Submit row ----- */
.form-submit-row {
  margin-top: 8px;
}

.proceed-btn {
  font-size: 15px;
  font-weight: 600;
  padding: 12px 16px;
  letter-spacing: 0.3px;
  border-radius: 4px;
}

.proceed-btn:disabled {
  background: #d0d0d0 !important;
  border-color: #d0d0d0 !important;
  color: #888 !important;
  cursor: not-allowed;
}

.insights-sync-status {
  margin-top: 12px;
  padding: 8px 12px;
  text-align: center;
  font-size: 12px;
  color: #2d7a3a;
  background: #f1f9f3;
  border: 1px solid #d4e9d8;
  border-radius: 4px;
  font-weight: 500;
}

.insights-sync-dot {
  display: inline-block;
  margin-right: 6px;
  color: #2d7a3a;
  font-size: 10px;
  vertical-align: middle;
}

.insights-sync-status-pending {
  color: #888;
  background: #f7f7f7;
  border-color: #e5e5e5;
  font-style: italic;
}

.insights-part-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.insights-part-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.insights-part-item:last-child {
  border-bottom: none;
}

.insights-part-main {
  flex: 1 1 auto;
  min-width: 0;
}

.insights-part-code {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 2px;
}

.insights-part-desc {
  font-size: 13px;
  color: #333;
  line-height: 1.35;
  margin-bottom: 4px;
}

.insights-part-context {
  color: #888;
  font-size: 12px;
}

.insights-part-meta {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.insights-part-meta strong {
  color: #555;
  font-style: normal;
}

/* Compact add-to-cart action — icon + label, brand red, link-styled
   so it doesn't compete visually with the page's primary CTA button. */
.insights-part-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #c0392b;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 4px;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  cursor: pointer;
}

.insights-part-add:hover {
  background: #c0392b;
  color: #fff;
  text-decoration: none;
}

.insights-part-add.disabled,
.insights-part-add.disabled:hover {
  opacity: 0.5;
  pointer-events: none;
  background: #fff;
  color: #c0392b;
}

/* Selected state — Add button flips to the filled treatment with a
   checkmark icon to signal "this part is in your queue." Click
   removes it from the queue. */
.insights-part-add.is-selected {
  background: #c0392b;
  color: #fff;
  border-color: #c0392b;
}

.insights-part-add.is-selected:hover {
  background: #a8321f;
  color: #fff;
  border-color: #a8321f;
}

.insights-part-add svg {
  flex-shrink: 0;
}

/* ===== CONTINUE BUTTON =====
   Appears at the bottom of the panel when at least one part is
   queued. Full-width primary red mirroring the form's "Proceed
   with Order" button on the left so the two flows feel parallel:
   left = manual machine browse, right = quick-add from history. */
.insights-continue-wrap {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}

.insights-continue-btn {
  display: block;
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* See more button — link-style centered action below the part list.
   Sits flush at the bottom of the panel so the list visually grows
   into the same space rather than displacing other content. */
.insights-see-more-wrap {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px dashed #e5e5e5;
  text-align: center;
}

.insights-see-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #444;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.insights-see-more-btn:hover {
  background: #f3f3f3;
  border-color: #999;
  color: #222;
}

.insights-see-more-count {
  font-weight: 400;
  color: #888;
  text-transform: none;
  letter-spacing: 0;
}

/* Previous orders card */
#prevOrders {
  height: 100%;
  align-content: start;
}

#prevOrders .po-heading {
  font-size: 1.5rem;
  line-height: 1.2;
}

#prevOrders .btn.btn-dark {
  min-width: 160px;
  text-transform: uppercase;
}

/* Emergency CTA spacing */
#createOrder #emergencyCTA,
#createOrder .red-cta,
#createOrder .enterPO {
  margin-top: 8px;
}

/* Small helper text under company selector */
.cart-reset-span {
  cursor: pointer;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 991.98px) {
  #createOrder > .container {
    margin-top: 16px;
  }

  #updateBanner {
    min-height: 72px;
    margin-top: 10px;
  }

  #configuredParts {
    margin-top: 10px;
  }

  #prevOrders .btn.btn-dark {
    width: 100%;
  }

  /* Stack the insights panel below the form on smaller screens
     and let it consume the full width — keeps each item readable. */
  .insights-panel {
    margin-top: 16px;
  }

  .insights-part-add-label {
    display: none;
  }

  .insights-part-add {
    padding: 6px 10px;
  }
}

/* ===== INLINE WEAR PARTS PANEL =====
   Sits in the right column above .insights-panel. Designed
   to feel native to the existing card stack — same border-
   radius, same shadow depth, same heading scale. Brand-red
   top accent distinguishes it from the cream Frequently
   Ordered card below. */
.inline-wear-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 16px 16px 14px;
  margin-bottom: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.inline-wear-header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e3e3e3;
}

.inline-wear-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  margin: 0 0 4px 0;
}

.inline-wear-discount-note {
  font-size: 11px;
  color: #2e7d32;
  margin: 0;
  font-style: italic;
}

.inline-wear-discount-note strong {
  font-weight: 700;
}

/* Assembly picker — Semantic UI dropdown to match the
   Machine dropdown so the "Don't see your assembly?"
   help block can live inside the .menu. The native
   <select> implementation that previously lived here
   couldn't host arbitrary HTML inside <option>, which
   forced the help block to sit below the picker. The
   Semantic UI dropdown requires manual sync points
   (init after options load, set selected on programmatic
   value change, clear on reset) — those are handled in
   loadInlineWearAssemblyOptions and resetInlineWearState.

   The .inline-wear-picker-* native-select rules below are
   retained because the sub-assembly picker is still a
   native <select> (it's a filter, not a primary picker —
   doesn't need search or in-dropdown footer). */
.inline-wear-picker {
  margin-bottom: 12px;
}

.inline-wear-picker-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 0 0 4px 0;
}

.inline-wear-picker-select {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  color: #222;
  background: #fff;
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  appearance: auto;
  cursor: pointer;
}

.inline-wear-picker-select:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.inline-wear-picker-select:disabled {
  background: #f7f7f7;
  cursor: wait;
}

/* Tighten the Semantic UI assembly dropdown's font and
   min-height to fit this panel's compact density —
   Semantic UI's defaults are sized for full-width forms,
   which look oversized next to the sibling sub-assembly
   <select> below. */
.inline-wear-assembly-dropdown.ui.dropdown {
  font-size: 13px !important;
  min-height: 38px;
}

/* Empty / loading states — soft grey block, centered text. */
.inline-wear-empty {
  padding: 16px 12px;
  font-size: 12px;
  color: #F00;
  text-align: center;
  background: #fafafa;
  border-radius: 4px;
}

/* ===== ACCORDION =====
   Each sub-assembly is a card with a clickable header.
   Default-expanded so the parts are immediately visible. */
.inline-wear-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Wayfinding banner — sits between the first (expanded)
   sub-assembly card and the rest (collapsed). Keeps
   the user from missing that there are more categories
   below the one they're seeing. Subtle but visible:
   muted background, small text, downward arrow to
   reinforce the spatial relationship. */
.inline-wear-more-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fff8e1;
  border: 1px dashed #e0c97a;
  border-radius: 4px;
  font-size: 12px;
  color: #6b5614;
  line-height: 1.4;
}

.inline-wear-more-hint strong {
  color: #c0392b;
  font-weight: 700;
}

.inline-wear-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 2px solid #c0392b;
  border-radius: 4px;
  overflow: hidden;
}

.inline-wear-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: #fafafa;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.inline-wear-card-header:hover {
  background: #f3f3f3;
}

.inline-wear-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}

.inline-wear-card-name {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 700;
  color: #2b2b2b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-wear-card-count {
  flex-shrink: 0;
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Parts list inside each card. Each row stacks code+chip,
   description, and price vertically; ADD button is
   right-aligned via flex on the .inline-wear-part container. */
.inline-wear-parts {
  list-style: none;
  margin: 0;
  padding: 0;
}

.inline-wear-part {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid #f5f5f5;
}

.inline-wear-part:first-child {
  border-top: none;
}

.inline-wear-part-main {
  flex: 1 1 auto;
  min-width: 0;
}

.inline-wear-part-code-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.inline-wear-part-code {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.inline-wear-discount-chip {
  display: inline-block;
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
  background: #2e7d32;
  border-radius: 8px;
  line-height: 1.4;
}

.inline-wear-part-desc {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
  margin-bottom: 2px;
  word-break: break-word;
}

.inline-wear-part-price {
  font-size: 11px;
  font-weight: 600;
  color: #2b2b2b;
}

.inline-wear-part-qpm {
  font-weight: 500;
  color: #888;
}

/* ADD button — pill style, brand-red. is-selected toggles
   to a filled "Edit" pill so users see at a glance which
   parts are already in their queue. */
.inline-wear-part-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: #fff;
  color: #c0392b;
  border: 1.5px solid #c0392b;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  cursor: pointer;
}

.inline-wear-part-add:hover {
  background: #c0392b;
  color: #fff;
  text-decoration: none;
}

.inline-wear-part-add.is-selected {
  background: #c0392b;
  color: #fff;
}

.inline-wear-part-add.is-selected:hover {
  background: #a93222;
}

.inline-wear-part-add.disabled {
  opacity: 0.45;
  pointer-events: none;
}

/* ===== HEADER-SEARCH RESULT BANNER =====
   Sits at the very top of the right-column .insights-panel,
   above the staging tray (.insights-selected-tray) and the
   Frequently Ordered section. Renders only when the user
   arrived here via a part-suggestion click in the floating
   header search bar (Head.vue → selectOption → router.push).

   Visual language is intentionally close to
   .insights-selected-tray — red left border on a white card
   with a soft red shadow — but the accent color and icon are
   tuned to read as "search context" rather than "queued
   parts". Same vertical rhythm (margin-bottom: 16px) so the
   stack reads cleanly when both the banner and the staging
   tray are visible.

   Layout: [icon][body grows][× clear button], all in a
   single row. Body is the column that absorbs extra width;
   icon and clear button stay fixed-size on the ends. */
.header-search-result {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 12px 12px 14px;
  background: #fff;
  border: 1px solid #e7d6a6;
  border-left: 4px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(192, 57, 43, 0.08);
  position: relative;
}

.header-search-result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  flex-shrink: 0;
  /* Nudge down to optically center against the title row,
     which sits a hair below the box's top padding. */
  margin-top: 1px;
}

.header-search-result-body {
  flex: 1 1 auto;
  min-width: 0; /* allows long part codes to ellipsize */
}

.header-search-result-title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  line-height: 1.3;
}

.header-search-result-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #c0392b;
}

.header-search-result-code {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  word-break: break-word;
}

.header-search-result-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #6b6b6b;
  line-height: 1.35;
  word-break: break-word;
}

/* Match the × button's visual weight to the staging tray's
   row-remove buttons so the page reads as one coherent set
   of dismissible items. */
.header-search-result-clear {
  border: none;
  background: transparent;
  color: #999;
  font-size: 22px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  flex-shrink: 0;
  /* Pull the × up to optically align with the title row
     (button has its own line-box and the default flex
     baseline puts it slightly low). */
  margin-top: -2px;
  transition: color 120ms ease;
}

.header-search-result-clear:hover,
.header-search-result-clear:focus {
  color: #c0392b;
  outline: none;
}
</style>

<!-- ===== PO EXPLAINER MODAL =====
     b-modal teleports its DOM to document.body, which means scoped
     styles in this component can't reach it. This non-scoped block
     is intentionally global, but namespaced via .po-explainer-modal
     so it only affects this one modal — not every b-modal on the
     site. Keeps the explainer body readable, the input full-width,
     and the action buttons consistent with the rest of the page. -->
<style>
.po-explainer-modal .modal-body {
  padding: 18px 20px;
}

.po-explainer-modal .po-explainer-text {
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.55;
  color: #444;
}

.po-explainer-modal .po-modal-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 0 0 6px;
}

.po-explainer-modal .po-modal-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #222;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.po-explainer-modal .po-modal-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}

.po-explainer-modal .modal-footer .btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 12px;
  padding: 8px 18px;
}

/* ===== PO PICKER MODAL =====
   Shown when the queue already has Orders. Each option is a
   stacked button — Order #N + PO info on top, count beneath.
   "New PO Number" option uses a dashed border to differentiate
   from existing-order picks. */
.po-picker-modal .modal-body {
  padding: 18px 20px;
}

.po-picker-modal .po-picker-text {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #555;
}

/* Edit-mode hint: shown above the explainer text to remind the
   user where the part currently lives. Soft yellow chip to match
   the tray's order-group accent. */
.po-picker-modal .po-picker-current {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #fffaf0;
  border: 1px solid #f3e3b8;
  border-radius: 4px;
  font-size: 12px;
  color: #5a4716;
}

.po-picker-modal .po-picker-current strong {
  color: #c0392b;
}

.po-picker-modal .po-picker-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.po-picker-modal .po-picker-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 12px 16px;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.po-picker-modal .po-picker-option:hover {
  background: #c0392b;
  color: #fff;
}

.po-picker-modal .po-picker-option-num {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.po-picker-modal .po-picker-option:hover .po-picker-option-num {
  color: #fff;
}

.po-picker-modal .po-picker-option-meta {
  font-size: 12px;
  color: #777;
}

.po-picker-modal .po-picker-option:hover .po-picker-option-meta,
.po-picker-modal .po-picker-option:hover .po-picker-option-meta strong {
  color: #fff;
}

.po-picker-modal .po-picker-option-meta strong {
  color: #444;
}

.po-picker-modal .po-picker-option-new {
  border-style: dashed;
  border-color: #888;
}

.po-picker-modal .po-picker-option-new .po-picker-option-num {
  color: #444;
}

.po-picker-modal .po-picker-option-new:hover {
  background: #444;
  border-color: #444;
}

.po-picker-modal .po-picker-option-new:hover .po-picker-option-num {
  color: #fff;
}

/* Remove option — destructive, red theme to match the cart's
   destructive actions elsewhere on the site. Visually separated
   from the safe options above by the dashed border on the New PO
   option that precedes it. */
.po-picker-modal .po-picker-option-remove {
  border-color: #c0392b;
  background: #fff;
  margin-top: 4px;
}

.po-picker-modal .po-picker-option-remove .po-picker-option-num {
  color: #c0392b;
}

.po-picker-modal .po-picker-option-remove:hover {
  background: #c0392b;
  border-color: #c0392b;
}

.po-picker-modal .po-picker-option-remove:hover .po-picker-option-num,
.po-picker-modal .po-picker-option-remove:hover .po-picker-option-meta {
  color: #fff;
}

.po-picker-modal .modal-footer .btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 12px;
  padding: 8px 18px;
}

/* ===== MACHINE IMAGE + ZOOM MODAL =====
   Image is constrained to its column width so it stays compact in
   the Step 2 panel. Modal CSS is ported verbatim from
   ShopWearParts.vue so the zoom UX is consistent. */
.machine-image-wrap {
  display: flex;
  justify-content: center;
  margin-top: 4px;
  /* In-place hover-zoom needs overflow:hidden so the scaled image
     stays clipped to the wrap (otherwise it would bleed out and
     visually "jump" over neighboring layout). position:relative
     is defensive — keeps the wrap as a positioning context if any
     overlays (loaders, badges) are added later. */
  overflow: hidden;
  position: relative;
}

.machine-preview-img {
  width: 100%;
  max-width: 360px;
  height: auto;
  max-height: 240px;
  cursor: zoom-in;
  object-fit: contain;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  background: #fff;
  /* In-place zoom: short transition makes hover-in / hover-out
     feel polished without lagging when the cursor moves quickly
     within the image. transform-origin is set inline by
     inPlaceZoomMove() — defaults to center via the data property. */
  transition: transform 0.1s ease-out;
  will-change: transform;
}

/* Hover state — scale up the image; transform-origin (inline,
   driven by mousemove) decides which area becomes the focal point.
   2x balances "shows enough detail" with "still see enough of the
   image at once". Bump higher if customers find it too modest;
   the scale here is the only number to change. */
.machine-image-wrap:hover .machine-preview-img,
.machine-image-wrap.is-zoom-locked .machine-preview-img {
  transform: scale(4);
}

/* When locked (single-click on image, see onImageClick), the
   wrap switches to sticky positioning so the zoomed view stays
   visible at the top of the viewport as the user scrolls down
   through the parts list. Sticky preserves the wrap's original
   document-flow position too — unlocking simply restores the
   natural layout with no jump. z-index keeps the wrap above
   subsequent form sections that scroll past underneath. */
.machine-image-wrap.is-zoom-locked {
  /* position: fixed (not sticky) so the locked panel can sit
     over the right column's .insights-panel area regardless
     of where the wrap lives in the document. Sticky would
     constrain to the wrap's natural ancestor; fixed gives
     us free placement based on the inline geometry vars below. */
  position: fixed;
  /* All three coordinates are set inline by onImageClick via
     CSS custom properties, captured from .insights-panel's
     bounding rect at lock-activation time:
       --lock-top    = panel's top edge in viewport
       --lock-left   = panel's left edge in viewport
       --lock-width  = panel's width
     This makes the locked panel adopt the right column's exact
     geometry — same position, same width as Frequently Ordered.
     Fallbacks (10px / auto / auto) keep the panel visible
     somewhere reasonable if the measurement fails. */
  top: var(--lock-top, 10px);
  left: var(--lock-left, auto);
  width: var(--lock-width, auto);
  /* Float ABOVE the global floating cart/Top-5 stack (z 8500) and the
     pill rail (z 9000). This block is GLOBAL (non-scoped <style>), so the
     rule also governs the locked image on Search/RFQ — at z-index 1000 it
     sat BEHIND the stack and the image "disappeared" on click. 9500 fixes
     it on every build page and keeps all three in agreement. */
  z-index: 9500;
  /* Heavy visual treatment so the locked panel reads as a
     distinct floating element. 3px brand-red border on the
     wrap (img border would be clipped by overflow:hidden),
     opaque white background to mask any transparent regions
     in the source PNG, drop shadow for elevation. */
  background: #fff;
  /* Match the content cards EXACTLY: a 3px brand-red line on TOP only, an
     8px radius, and a drop shadow for elevation — NO full neutral side/bottom
     border. A full 1px side border traced the rounded corner and let the
     black page show through the corner triangle (worst in dark/mid mode);
     dropping it gives the same clean, soft corner the left content card and
     cart panel have. This block is GLOBAL, so it must stay byte-for-byte in
     sync with the identical rule in Search/Index.vue and RFQ/Create.vue. */
  border-top: 3px solid #c0392b;
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  /* Margin-top stripped — position:fixed removes the wrap
     from document flow, but explicitly zeroing keeps the
     rendered box flush with the inline top coordinate. */
  margin-top: 0;
}

/* Locked-state image styling.
   - Crosshair cursor signals "anchored at this point — click to
     release the lock".
   - max-width: 100% lets the wrap fill the available column
     width when locked (so the visible window into the zoomed
     image is wide).
   - max-height is computed adaptively as
     calc(100vh - var(--lock-top, 100px) - 40px):
       * 100vh = total viewport height
       * minus var(--lock-top) = subtract the top offset where
         the wrap is pinned (set inline by onImageClick from
         the measured anchor element's viewport bottom)
       * minus 40px = breathing room at the very bottom of the
         viewport so the locked image isn't flush against the
         viewport's bottom edge
     Without the calc, a fixed 70vh combined with a non-trivial
     top offset would push the image's bottom below the viewport
     and cause clipping. The calc approach guarantees the image
     always fits inside the visible window regardless of how
     far down the anchor pins it.
   - object-fit: contain is inherited from the base rule. */
.machine-image-wrap.is-zoom-locked .machine-preview-img {
  max-width: 100%;
  max-height: calc(100vh - var(--lock-top, 100px) - 40px);
  /* cursor: grab (open hand) signals "this can be dragged" —
     the universal hand-grab convention. Replaces the previous
     'cursor: move' which rendered as 4-way arrows and tested
     poorly in user feedback. While dragging in either mode
     the cursor flips to 'grabbing' (closed fist) via the
     mode-specific class rules below. */
  cursor: grab;
  /* Block text selection during drag — without this, dragging
     across the image often selects whatever text is behind /
     adjacent and creates a distracting blue highlight that
     follows the cursor. Applied to both wrap and img to cover
     both pointer-event targets. */
  user-select: none;
  -webkit-user-select: none;
}
.machine-image-wrap.is-zoom-locked {
  user-select: none;
  -webkit-user-select: none;
}

/* Both drag modes (panning inside OR moving panel) show the
   closed-fist 'grabbing' cursor. The two modes are
   functionally distinct but visually share one drag-state
   cursor — mirrors the OS-native dragging cue users expect.
   Applied on the wrap and img so the cursor stays consistent
   regardless of which element the cursor is over. */
.machine-image-wrap.is-zoom-locked.is-panning-internal,
.machine-image-wrap.is-zoom-locked.is-panning-internal .machine-preview-img,
.machine-image-wrap.is-zoom-locked.is-dragging,
.machine-image-wrap.is-zoom-locked.is-dragging .machine-preview-img {
  cursor: grabbing;
}

/* ===== LOCKED-PANEL CLOSE BUTTON =====
   Small circular × in the panel's top-right corner. Visible
   only when the panel is locked (template-gated via v-if).
   Sized for easy click target without dominating the panel
   visually. Brand-red border matches the wrap border for
   visual cohesion. */
.locked-close-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #c0392b;
  /* Rest state inverted from the original: red background +
     white × so the close button reads as a clear red dot in
     the panel corner. The previous fill-on-hover treatment
     (white bg, red ×) was too subtle at 28x28 and tended to
     blend into the panel's white interior. */
  background: #c0392b;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  padding: 0;
  /* Above the image (z-index 1 on stacking context — the
     wrap's z-index 1000 already lifts everything inside it
     above the page, so 1 here is just relative to siblings). */
  z-index: 1;
  /* Override the wrap's grab cursor — this is a click target,
     not a drag handle. */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
/* Hover inverts to white-on-red — visual confirmation that
   the cursor is on a clickable target. */
.locked-close-btn:hover {
  background: #fff;
  color: #c0392b;
}

/* Loading-state wrappers for image fetches and the parts
   accordion. Shared visual language: small spinner + label,
   centered, gray text — matches the muted nature of "loading"
   states without competing with primary content. */
.machine-image-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 60px;
  width: 100%;
  max-width: 360px;
  color: #666;
  font-size: 13px;
}

.inline-wear-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 12px;
  color: #666;
  font-size: 14px;
}

.custom-image-modal {
  position: fixed;
  z-index: 99999;
  inset: 0;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.custom-image-modal-inner {
  background: #fff;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.custom-image-close {
  position: absolute;
  top: 10px;
  right: 14px;
  z-index: 2;
  border: none;
  background: transparent;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
}

.diagram-zoom-wrap {
  flex: 1;
  overflow: hidden;
  position: relative;
  cursor: grab;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8f8f8;
}

.diagram-zoom-wrap:active {
  cursor: grabbing;
}

.imagepreview {
  max-width: 95%;
  max-height: 95%;
  user-select: none;
  -webkit-user-drag: none;
  transition: transform 0.05s linear;
}

.custom-image-controls {
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  background: #fff;
}

/* ===== MACHINE DROPDOWN FOOTER ("Don't see your machine?") =====
   Sits inside the Semantic UI dropdown's .menu, after the
   data items. The dropdown library would normally treat
   any clicked element inside .menu as a value-pick;
   `data-no-select` plus `@click.stop` on the inner
   buttons keeps that behavior contained and lets our own
   handlers run. */
.dropdown-footer-divider {
  border-top: 1px solid #e6e6e6;
  margin: 4px 0 0;
}

.dropdown-footer-help {
  /* Lift the visual weight slightly so this block reads as
     "support content" rather than another option. The Semantic
     UI items above are flat-white; this gets a tinted
     background to draw the eye. */
  background: #fffbe8;
  padding: 14px 18px;
  cursor: default;
}

.dropdown-footer-help:hover {
  /* Override Semantic UI's .menu > * hover state so the
     footer doesn't look "selectable." */
  background: #fffbe8 !important;
}

.dropdown-footer-content {
  font-size: 13px;
  color: #444;
}

.dropdown-footer-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 14px;
  color: #222;
}

.dropdown-footer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #c0392b;
  color: #fff;
  border-radius: 50%;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}

.dropdown-footer-blurb {
  margin: 0 0 10px;
  line-height: 1.4;
  color: #555;
}

.dropdown-footer-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dropdown-footer-action {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  background-color: #c0392b !important;
  color: #fff !important;
  border-color: #c0392b !important;
}
.dropdown-footer-action:hover,
.dropdown-footer-action:focus {
  background-color: #a02d20 !important;
  border-color: #a02d20 !important;
  color: #fff !important;
}

/* ===== PARTS INQUIRY MODAL =====
   Internal layout — the modal frame itself is Bootstrap-Vue
   default styling. */
.parts-inquiry-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
}

.parts-inquiry-form .form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.parts-inquiry-form .form-row > [class*="col-"] {
  flex: 1 1 0;
  min-width: 200px;
  padding: 0;
}

.parts-inquiry-form label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.parts-inquiry-form .form-control,
.parts-inquiry-form input[type="file"] {
  width: 100%;
  font-size: 14px;
}

/* ==========================================================
   STAGING TRAY WRAPPER — matches the "Your Cart" presentation
   on Index_-_Account.vue. Wraps the existing
   .insights-selected-tray inside a card with a header
   ("YOUR CART") and gives the bottom-of-tray Continue
   button consistent styling across the three cart-driven
   pages (Search, Create-Order, Create-RFQ).
   ========================================================== */
.staging-tray-wrapper {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}
.staging-tray-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
.staging-tray-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c0392b;
}
.staging-tray-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
}
.staging-tray-body {
  flex: 1;
}
.staging-continue-btn {
  width: 100%;
  margin-top: 10px;
  padding: 9px 12px;
  background: #c0392b;
  color: #fff;
  border: 0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: background 0.15s ease;
}
.staging-continue-btn:hover {
  background: #a3301f;
}
.staging-continue-btn-quote {
  background: #444;
}
.staging-continue-btn-quote:hover {
  background: #222;
}
</style>